/**
 * Triage dashboard API client.
 *
 * Thin `fetch` wrapper + TanStack Query `queryOptions` factories for
 * every JSON endpoint the backend exposes under `/api/*`. Keep in
 * sync with `src/server/routes/dashboard_api.rs` — the shapes below
 * match the Rust types one-for-one:
 *
 * - `Session`            ↔ `AuthMeResponse`
 * - `AnalysisSummary`    ↔ `AnalysisSummary` (same name)
 * - `AnalysesResponse`   ↔ `AnalysesResponse`
 *
 * # Auth
 *
 * Every request sends cookies (`credentials: "same-origin"`). On a
 * 401 the wrapper redirects the browser to `/auth/google/login`
 * rather than throwing a catchable error in the component tree —
 * session expiry is a full-page event, not a recoverable one. We
 * also throw `ApiError` from the promise so TanStack Query sees a
 * rejected state and stops retrying (retry policy in `App.tsx`
 * bails on 401/403).
 *
 * # Why queryOptions factories
 *
 * TanStack Query v5's recommended pattern. Each function returns a
 * fully-formed `queryOptions` object that the component passes to
 * `useQuery(queries.foo())`. Benefits:
 *
 * - Single source of truth for `queryKey` + `queryFn` per endpoint.
 *   Changing a fetch URL is a one-line edit here, not a grep.
 * - Same factory works for route-level prefetching via
 *   `queryClient.ensureQueryData(queries.foo())`.
 * - Typed end-to-end — the generic on `api<T>` ensures the return
 *   type flows into the component without annotations.
 */

import { queryOptions } from "@tanstack/react-query";
import { noteResponse } from "@/lib/updating";

// ─── Error type ─────────────────────────────────────────────────────────────

/**
 * Thrown by the `api<T>` wrapper on any non-2xx response (other than
 * 401, which redirects instead). Carries the HTTP status code and an
 * application-level error code so components can branch on it
 * (though in v1 we just surface the message).
 */
export class ApiError extends Error {
  public status: number;
  public code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

// ─── Fetch wrapper ──────────────────────────────────────────────────────────

/**
 * Fetch an `/api/*` endpoint and return the decoded JSON body.
 *
 * - Sends cookies (`credentials: "same-origin"`) so the session
 *   cookie goes out automatically. No bearer tokens on the SPA side.
 * - On 401, redirects the whole browser to `/auth/google/login`
 *   and throws an `ApiError` so the calling code terminates.
 * - On other non-2xx, parses `{error, code}` from the body (our
 *   backend error envelope — see `dashboard_api::ErrorBody`) and
 *   throws `ApiError`.
 * - On 204 No Content, returns an empty object cast to `T`.
 *
 * The generic `T` is the successful response shape. Caller picks
 * the right type; no runtime validation — we trust the backend.
 */
export async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...options,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
  // Galley #38663: a stopped worker's answer shows the "Triage is updating" banner,
  // a success clears it. What this function returns or throws is unchanged.
  noteResponse(res);

  if (res.status === 401) {
    // Full-page redirect — the login flow ends with a Set-Cookie
    // response and a redirect back to the SPA (either `/` or the
    // preserved deep-link target), which reloads the SPA with a
    // fresh session. Throwing here prevents downstream .then() from
    // running with undefined data, which would otherwise race the
    // redirect and log a misleading error to the console.
    //
    // Deep-link preservation: we capture the current URL (pathname +
    // search + hash) and forward it as `?next=<encoded>` so the
    // backend OAuth flow can round-trip it through the Google
    // handshake and redirect the user back to where they were trying
    // to go. Without this, a user who clicks a Slack link like
    // `https://dashboard.../analyses/<uuid>` while logged out ends up
    // on `/` after login and has to re-navigate manually.
    //
    // The `next` value is validated server-side in
    // `sanitize_next` (src/server/routes/auth_google.rs) — client-
    // side we just forward the raw same-origin path. An attacker who
    // can edit this line can navigate the user anywhere anyway (they
    // control the bundle); the security gate is at the server.
    //
    // `window.location.replace(...)` (not `href =`) so the
    // pre-login URL doesn't accumulate an extra history entry that
    // the back button would have to skip.
    const current =
      window.location.pathname + window.location.search + window.location.hash;
    const nextQuery =
      current && current !== "/" ? `?next=${encodeURIComponent(current)}` : "";
    window.location.replace(`/auth/google/login${nextQuery}`);
    throw new ApiError("Session expired", 401, "AUTH_REQUIRED");
  }

  if (!res.ok) {
    // Backend error envelope: {error: "...", code: "..."}. Fall back
    // to the HTTP status text if the body isn't parseable, and to
    // "HTTP <status>" when that is empty too: HTTP/2 carries no status
    // text, so a load balancer's HTML 502 would otherwise give every
    // red box an empty reason (Galley #17894).
    const body = (await res.json().catch(() => ({
      error: res.statusText,
      code: "UNKNOWN",
    }))) as { error?: string; code?: string };
    throw new ApiError(
      body.error || res.statusText || `HTTP ${res.status}`,
      res.status,
      body.code ?? "UNKNOWN",
    );
  }

  if (res.status === 204) {
    return {} as T;
  }

  return (await res.json()) as T;
}

// ─── Response types ─────────────────────────────────────────────────────────

/**
 * `GET /api/auth/me` response. Returned by
 * `src/server/routes/api/session.rs::auth_me`.
 *
 * v1 has no RBAC tiers — every authenticated user sees the same
 * page. If/when we add roles, this is where the `role` field lands.
 */
export interface Session {
  email: string;
  display_name: string;
  /**
   * True when the signed-in user's email is in
   * `worker_config.data.admins`. Derived server-side on every
   * `/api/auth/me` request (NOT stored in the cookie), so rotating
   * the admin list takes effect on the next page load without a
   * re-login.
   *
   * ⚠️ UX SIGNAL ONLY. The SPA uses this to show/hide admin-only
   * UI elements (nav links, the `/admin/llm` calls-drill-down
   * modal, the `PromptInspectorModal`'s system-block fetch
   * button). The backend re-verifies admin status on every
   * admin-gated endpoint via `require_admin` — a client that
   * spoofs `is_admin: true` still gets 403 from the server.
   */
  is_admin: boolean;
  /**
   * Galley #26408 (the owner's #26840): true when the signed-in user
   * holds the operator role. Optional: absent (an older backend) reads
   * as false. UX SIGNAL ONLY, like `is_admin`: every `/api/operator/*`
   * route re-checks the role server-side and answers 403 otherwise.
   */
  is_operator?: boolean;
  /**
   * Deployment label (`"prod"` / `"staging"`) from the worker's
   * `TRIAGE_ENV` (step 0.1). Display only — never branch behavior on it.
   * Same SPA build serves both instances; the backend supplies the label.
   */
  environment: string;
  /**
   * The Slack account linked to this login via `/triage link`, or `null`
   * when not linked. Looked up server-side on every `/api/auth/me`.
   * Optional so older fixtures (and an older backend) still type-check.
   */
  slack?: SlackIdentity | null;
}

/**
 * A linked Slack account. `user_id` is what Slack mentions use; the two
 * names are display-only snapshots from Slack's profile at link time.
 */
export interface SlackIdentity {
  user_id: string;
  display_name: string | null;
  real_name: string | null;
}

/** The name to show for a linked Slack account: display name, then full name, then the id. */
export function slackIdentityLabel(slack: SlackIdentity): string {
  return slack.display_name ?? slack.real_name ?? slack.user_id;
}

/** `GET /api/slack-link/preview` — what a `/triage link` code would connect (read-only). */
export interface SlackLinkPreview {
  slack_user_id: string;
  slack_display_name: string | null;
  slack_real_name: string | null;
  valid_until: string;
  email: string;
  /** What this login is linked to today, if anything (it will be replaced). */
  current: SlackLink | null;
}

/** An active Slack ↔ dashboard link (`POST /api/slack-link/confirm` response). */
export interface SlackLink {
  email: string;
  slack_user_id: string;
  slack_team_id: string;
  slack_display_name: string | null;
  slack_real_name: string | null;
  linked_at: string;
}

/** Read-only preview of a `/triage link` code. Errors carry `CODE_*` codes. */
export function previewSlackLink(code: string): Promise<SlackLinkPreview> {
  return api<SlackLinkPreview>(
    `/api/slack-link/preview?code=${encodeURIComponent(code)}`,
  );
}

/** Consume a `/triage link` code and link it to the signed-in login. */
export function confirmSlackLink(code: string): Promise<SlackLink> {
  return api<SlackLink>("/api/slack-link/confirm", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

/** End the signed-in login's Slack link (`unlinked: false` = nothing was linked). */
export function unlinkSlack(): Promise<{ unlinked: boolean }> {
  return api<{ unlinked: boolean }>("/api/slack-link/unlink", {
    method: "POST",
  });
}

/** `GET /api/env` — unauthenticated; same label as `Session.environment`. */
export interface PublicEnv {
  environment: string;
}

/**
 * One row in the analyses table. Matches `AnalysisSummary` in
 * `src/server/routes/dashboard_api.rs`. See the Rust struct's
 * doc-comments for per-field semantics — the TypeScript shape is
 * identical.
 *
 * Any field can be `null` — `analysis.json` schemas have drifted
 * over time and we tolerate missing data per-row rather than
 * failing the whole page.
 */
export interface AnalysisSummary {
  analysis_id: string;
  generated_at: string; // ISO-8601
  report_type: string; // Raw user.json.log bucket: "ReportProblem" | "SuggestImprovement" | ...
  os: string; // "web" | "iOS 17.4" | "Android 14" | "unknown"
  username: string | null;
  device: string | null;
  app_version: string | null;
  one_line: string | null;
  client_report_uuid: string | null;
  /**
   * 2026-05-06 (Bug #3 fix): classifier verdict from the intake
   * pipeline, one of `"crash"`, `"user-shake"`, `"oops-anomaly"`,
   * `"oops-routine"`, `"not-a-bug"`. Sourced from
   * `bundle_lifecycle.llm_report_type` via a batched Postgres
   * lookup on the server side — NOT stored in analysis.json.
   *
   * `null` for analyses that pre-date the classifier wiring OR
   * when the server's lifecycle_store was unavailable at request
   * time. SPA falls back to grouping by raw `report_type` when
   * this field is null.
   *
   * Optional field for backward compat — older backends don't
   * populate it; newer SPAs must handle both shapes.
   */
  llm_report_type?: string | null;
}

/**
 * `GET /api/analyses` response envelope. The `count` field is
 * redundant with `items.length` but spares components a `.length`
 * call for the header.
 */
export interface AnalysesResponse {
  items: AnalysisSummary[];
  count: number;
}

/**
 * One (bucket-start, count) pair in a stats series. `ts` is an
 * ISO-8601 UTC timestamp aligned to the granularity boundary.
 *
 * The server returns only non-empty buckets — callers fill in
 * zero-count buckets client-side (see `fillZeroBuckets` in
 * `components/TimeSeriesChart.tsx`) so the chart has a dense x-axis.
 */
export interface StatsSeriesPoint {
  ts: string;
  count: number;
}

/**
 * `GET /api/stats` response. Matches `StatsResponse` in
 * `src/server/routes/dashboard_api.rs`. Empty `analyzed` / `intake`
 * arrays are expected when the DB isn't configured on this host —
 * the page renders an "No data in this window" empty state rather
 * than surfacing that as an error.
 */
export interface StatsResponse {
  /** Bucket granularity actually used (derived from the window):
   *  `5min`/`15min`/`30min`/`hour`/`day`. TRIAG-308. */
  granularity: HistogramGranularity;
  /** Effective window in hours after clamping (TRIAG-308). */
  window_hours: number;
  range_start: string;
  range_end: string;
  /** Issues analyzed — bucketed by `completed_at`. */
  analyzed: StatsSeriesPoint[];
  /** Issues that came in — bucketed by `arrived_at`. */
  intake: StatsSeriesPoint[];
}

// ─── Config types (Wave 9j + 9k) ────────────────────────────────────────────

/**
 * `GET /api/config` response shape. Matches `ConfigResponse` in
 * `src/server/routes/api/config.rs` (Wave 9j).
 *
 * `data` is the full `worker_config.data` JSONB payload with every
 * top-level key matching `*_token` / `*_secret` / `*_key`
 * (case-insensitive) stripped server-side. The SPA never sees
 * secret values; attempting to send any back via `PUT /api/config`
 * is rejected with 400 `SECRETS_REJECTED`.
 *
 * `version` is the optimistic-lock counter. The SPA captures it
 * and echoes it back on the next `PUT` as `expected_version` so
 * the server can detect concurrent writes.
 *
 * `data` is typed as `Record<string, unknown>` rather than a
 * specific struct because the schema evolves — every config-schema
 * addition on the Rust side would otherwise cascade into a type
 * update here. The `/admin` page reads individual fields via
 * `data["admins"]` with runtime type narrowing per field.
 */
export interface ConfigResponse {
  data: Record<string, unknown>;
  version: number;
  updated_at: string;
  /**
   * Additive (Galley #8936): values the worker actually runs with, computed
   * on the server from `data` with the worker's own defaults filled in, so
   * an absent key shows the shipped default. Absent on an older server, or
   * when `data` doesn't deserialize; readers fall back to today's default.
   */
  effective?: { resume_jobs_enabled?: boolean };
}

/**
 * `PUT /api/config` success response. Matches the
 * `SaveConfigResponse::Saved` variant on the Rust side (Wave 9k):
 *
 *   `{"outcome": "saved", "version": 43, "updated_at": "…"}`
 *
 * `version` is the post-bump counter (always `expected_version + 1`
 * on the Rust side — the server invariant `new_version ==
 * expected_version + 1` is pinned by the store trait docs). The
 * SPA stores this as the new `expected_version` for the next
 * save in the same session.
 */
export interface ConfigSaveSuccessResponse {
  outcome: "saved";
  version: number;
  updated_at: string;
}

/**
 * `PUT /api/config` conflict response (HTTP 409). Matches the
 * `SaveConfigResponse::VersionMismatch` variant on the Rust side
 * (Wave 9k):
 *
 *   `{"outcome": "version_mismatch", "current_version": 42}`
 *
 * `current_version` is the version the stored row actually holds
 * — strictly greater than the caller's `expected_version` on a
 * real conflict. The `ConfigForm` component surfaces this via its
 * conflict-resolution modal (Keep mine / Discard mine).
 *
 * NOT an error envelope — this is a legitimate response shape
 * with its own discriminator (`outcome`), NOT the standard
 * `ApiError` body. The `putConfig` helper below translates both
 * success and conflict shapes into a `ConfigSaveResult`
 * discriminated union the `ConfigForm` consumes.
 */
export interface ConfigSaveConflictResponse {
  outcome: "version_mismatch";
  current_version: number;
}

/**
 * Discriminated-union result of a `PUT /api/config` call.
 *
 * Maps the backend's three response shapes (200 Saved, 409
 * Conflict, 4xx/5xx Error) onto the `SaveResult<T>` contract the
 * `ConfigForm` component consumes. The `status` field is the
 * discriminator the ConfigForm switches on.
 *
 * Generic on `T` because the `ConfigForm` wrapper is generic too
 * — the `config` consumer passes `Record<string, unknown>` (the
 * `data` JSONB shape), but a future non-config save-form could
 * reuse the ConfigForm with a different value type and get a
 * correctly-typed result back.
 */
export type ConfigSaveResult =
  | {
      status: "saved";
      new_version: number;
      updated_at: string;
    }
  | {
      status: "version_mismatch";
      current_version: number;
    }
  | {
      status: "error";
      message: string;
      code: string;
    };

// ─── Bundle lifecycle wire shape (Wave 9b+) ─────────────────────────────────

/**
 * One `bundle_lifecycle` row as returned by the dashboard's bundle
 * endpoints. Matches `BundleLifecycleRow` in
 * `src/worker/lifecycle/row.rs` field-for-field. Every field
 * except the four identity fields (`analysis_id`, `s3_bucket`,
 * `s3_key`, `arrived_at`) is nullable because bundles fill their
 * row incrementally as the pipeline runs.
 *
 * Used directly (no wrapper) as:
 * - `GET /api/bundles/:uuid` response (Wave 9b)
 * - entries in `BundlesListResponse.items` (Wave 9d)
 * - the `canonical` + every entry in `matches` on
 *   `DuplicatesResponse` (Wave 9g)
 *
 * Also flattened into `AnalysisDetailResponse` (Wave 9e) via
 * `#[serde(flatten)]` on the Rust side — so every field below
 * appears at the top level of that response too. TypeScript
 * represents the flattened shape as `BundleLifecycleRow & {...}`;
 * see `AnalysisDetailResponse` further down.
 *
 * Field descriptions deliberately terse — the authoritative doc
 * is in `src/worker/lifecycle/row.rs`. Keeping the TS shape
 * compact means fewer lines to drift out of sync.
 */
/** Galley #25422: how much of a cut-off upload was recovered
 *  (PARTIAL-UPLOAD INTERFACE v1, #26853). */
export interface PartialUpload {
  files_kept: number;
  files_dropped: number;
}

export interface BundleLifecycleRow {
  // Identity
  analysis_id: string;
  s3_bucket: string;
  s3_key: string;

  // Lifecycle timestamps (all ISO 8601 UTC)
  arrived_at: string;
  retrieved_from_s3_at: string | null;
  triage_completed_at: string | null;
  classified_at: string | null;
  dedup_decided_at: string | null;
  analysis_started_at: string | null;
  analysis_finished_at: string | null;
  completed_at: string | null;

  // Client identity
  platform: string | null;
  /**
   * TRIAG-276: the distinguished server trace id (32-hex) from the
   * bundle's trigger error, or `null` when there is no server trace
   * (client-side failure / web / Android / pre-migration). Distinct
   * from the ambient `server_trace_id` handles in the backend-handles
   * card; this is the ONE failing request that prompted the report.
   */
  trigger_trace_id: string | null;
  app_version: string | null;
  build_number: string | null;
  dedup_scope_key: string | null;
  os: string | null;
  device: string | null;
  report_type: string | null;
  autogenerated: boolean | null;
  username: string | null;
  client_report_uuid: string | null;
  source: string | null;
  /**
   * TRIAG-59 PHASE 4 (2026-05-21): iOS app surface the report
   * originated from (`"MagicEditor"`, `"Onboarding"`, etc.).
   * Populated from `properties.source` in `user.json.log` at
   * classify time. `null` on web / Android / un-backfilled rows.
   */
  feature_area: string | null;

  /**
   * Galley #25422 (the owner's option A, #25424): set only on a report
   * whose upload was cut off and was analysed from the files that
   * survived. Absent (or null) on every normal report. Render through
   * `PartialUploadChip` only.
   */
  partial_upload?: PartialUpload | null;

  /**
   * TRIAG-346 (migration 066): the user's VERBATIM report text from
   * user.json.log, stored at identify time. Null = not captured
   * (pre-066 rows until the backfill, or no description in the
   * bundle). Render through `UserVoiceCard` / `isRenderableUserVoice`
   * ONLY — the empty + "Oops Error Alert" sentinel suppression lives
   * there.
   */
  user_description: string | null;

  /**
   * TRIAG-234 — intake classifier-v2 signals. `null` on skip paths +
   * historical (pre-deploy) rows.
   * - `confidence`: the AI's certainty in its read (`"low"`/`"medium"`/`"high"`).
   * - `worth_full_analysis`: the AI's "is this worth a deep investigation" verdict.
   * - `dedup_gist`: a short normalized "what's wrong" phrase.
   */
  confidence: string | null;
  worth_full_analysis: boolean | null;
  dedup_gist: string | null;
  /**
   * TRIAG-211 — why this analysis ran: `"auto-gate"` (the gate auto-promoted
   * a worth-it shake), `"manual"`, or `"rush"`. `null` = not auto-gated
   * (every deduped / not-promoted bundle + historical rows). Renders as the
   * ⚡ Auto-started chip.
   */
  analysis_trigger: string | null;

  /**
   * TRIAG-239 — why the auto-analysis gate did NOT auto-run this bundle
   * (`gate_disabled` / `not_eligible_type` / `not_worth` / `below_confidence`
   * / `daily_cap_reached` / `config_error`). `null` = not gated / promoted /
   * historical. Drives the exact "why it's waiting" banner copy.
   */
  auto_gate_skip_reason: string | null;

  // Hash-path dedup
  hash_normalized: string | null;
  hash_sha256: string | null;
  hash_matched_analysis_id: string | null;

  // Vector-path dedup
  vector_store_ref: string | null;
  vector_embed_model: string | null;
  vector_best_similarity: number | null;
  vector_best_threshold: number | null;
  vector_matched_analysis_id: string | null;

  // LLM judge
  llm_judge_called: boolean | null;
  llm_judge_verdict: string | null;
  llm_judge_model: string | null;
  llm_judge_prompt_version: string | null;
  llm_judge_input_tokens: number | null;
  llm_judge_output_tokens: number | null;
  llm_judge_duration_ms: number | null;
  llm_judge_candidates_tried: number | null;

  // Decision summary
  dedup_methodology: string | null;
  was_deduplicated: boolean | null;
  canonical_analysis_id: string | null;

  // Classification output
  classified_outcome: string | null;
  priority: string | null;
  llm_report_type: string | null;
  one_line: string | null;
  min_version_enforced: string | null;

  // Analysis outputs
  analysis_s3_bucket: string | null;
  analysis_s3_key: string | null;
  brief_url: string | null;
  detailed_summary_url: string | null;
  pdf_url: string | null;
  json_url: string | null;
  zip_url: string | null;
  patches_zip_url: string | null;
  bug_fix_url: string | null;
  presigned_urls_expire_at: string | null;
  patches_generated: boolean | null;

  /**
   * Migration 023 (2026-05-06) — basenames of screenshots
   * persisted under `results/<analysis_id>/screenshots/<filename>`.
   *
   * Three-state semantics (matches the SQL column comment):
   *
   *   - `null`           — row not yet processed for screenshots
   *     (predates the feature, or the backfill subcommand
   *     hasn't reached it, or the worker forward-path crashed
   *     before recording).
   *   - `[]`             — processed, bundle had no screenshots.
   *   - `["a.jpg", ...]` — processed, listed basenames available
   *     at the S3 prefix.
   *
   * `<ScreenshotsStrip>` (in `dashboard/src/components/`) renders
   * a thumbnail strip when non-empty and a thin "No screenshots
   * attached" panel for both `null` and `[]`. The white-lie for
   * `null` is acceptable: backfill covers the last month, beyond
   * that operators rarely look (per the 2026-05-06 design call).
   */
  screenshot_files: string[] | null;

  /**
   * Timestamp when `notify_complete` fired successfully for this
   * bundle, stamped by the [`record_slack_notify_posted`] writer
   * added in migration 022 (2026-05-02).
   *
   * Three call sites stamp this:
   *   - Happy-path `run_job_inner` after notify_complete Ok.
   *   - Failure-wrapper `run_job` after notify_complete(fail) Ok.
   *   - Slice-C shutdown-cancel arm in sqs.rs (marks bundles as
   *     "notified via channel summary" so the boot sweeper skips
   *     them).
   *
   * `null` means notify_complete hasn't fired yet OR was lost due
   * to SIGTERM between `record_completed` and `notify_complete`.
   * The boot sweeper in `src/worker/notify_backlog_sweeper.rs`
   * retries rows where this is null + terminal state set. Admin
   * can trigger immediate resend via
   * `POST /api/bundles/:uuid/resend-slack`.
   *
   * The SPA surfaces this via a slate chip on `/bundles/:uuid`
   * showing "Slack: posted {relative_time}" or "Slack: pending
   * resend" (amber). Single-transition: once non-null, never
   * cleared by any writer.
   */
  slack_notify_posted_at: string | null;
  investigations_count: number | null;
  findings_count: number | null;

  // Errors
  last_error: string | null;

  /**
   * Intake-routing decision (migration 025, 2026-05-12).
   *
   * - `"auto"` — bundle was enqueued to a priority queue at
   *   intake (default behavior).
   * - `"manual"` — bundle was held at intake; an operator must
   *   click the Slack `[Run analysis]` button, run
   *   `/triage rush <prefix>` in Slack, or escalate via the
   *   dashboard banner (Phase 2) to start the full pipeline.
   * - `null` — legacy pre-2026-05-12 row. SPA treats as if
   *   `"auto"`.
   *
   * Drives the "Auto-off" chip on `/bundles` rows + the
   * "awaiting operator action" banner on `/bundles/:uuid`.
   * Orthogonal to the route cascade (a `"manual"` bundle
   * whose analysis hasn't started still appears in the
   * `queued` route bucket).
   */
  intake_policy: "auto" | "manual" | null;

  /**
   * Operator dismissal state (migration 026, 2026-05-12).
   *
   * - `null` — not dismissed. Either still awaiting operator
   *   action, escalated, analyzed, or completed.
   * - non-null ISO 8601 — the operator dismissed this gated
   *   bundle without escalating it to analysis. Drives the
   *   `DismissedSection` banner + the `dismissed` strip status
   *   + the `dismissed` filter on `/bundles`.
   *
   * Reversible via `POST /api/bundles/:aid/undismiss`, which
   * clears `dismissed_at`, `dismissed_by`, and `dismissed_reason`
   * atomically.
   */
  dismissed_at: string | null;
  /**
   * Email of the operator who dismissed the bundle. Required
   * when `dismissed_at` is set; cleared by undismiss.
   */
  dismissed_by: string | null;
  /**
   * Operator-supplied dismissal reason. Application-level enum
   * at the API boundary (`"spam"` / `"duplicate"` /
   * `"not_actionable"` / `"out_of_scope"` / `"other"` /
   * `"other: <note>"`); rendered verbatim in the DismissedSection
   * banner. Forensic overrides via psql may produce other
   * values; the dashboard renders unknown values as a raw badge.
   */
  dismissed_reason: string | null;

  // Retention
  lifecycle_expires_at: string | null;

  // Raw payload
  bundle_json: Record<string, unknown> | null;

  // Bookkeeping
  created_at: string | null;
  updated_at: string | null;

  // ── Wire-only, derived per-request (Commit B, 2026-05-01) ──
  /**
   * Per-user "new since your last visit" indicator. Populated
   * ONLY by the `/api/bundles` list-endpoint enrichment pass
   * against the session user's `user_seen_bundles` state; detail
   * endpoints (`GET /api/bundles/:uuid`, `GET /api/analyses/:uuid`)
   * leave the field absent from the JSON response via
   * `#[serde(skip_serializing_if = "Option::is_none")]` on the
   * Rust side.
   *
   * The `NewIndicator` component renders a blue dot when this
   * field is explicitly `true`; `false` and `undefined` both
   * render nothing (calm-onboarding default + detail-row default
   * converge on the same visual).
   *
   * Three predicate clauses on the server (all closed in the
   * Commit B 5-hat session):
   *   - Never viewed: no `user_seen_bundles` row exists for
   *     `(session.email, row.analysis_id)`.
   *   - First-visit-ever path: handler bulk-marks every currently
   *     displayed row as seen → all rows return `false` (calm
   *     onboarding).
   *   - Enrichment path: per-row membership check against the
   *     seen set → `true` iff the row is absent from the set.
   *
   * Absent when `DATABASE_URL` is unset (the dev/test path with
   * no `user_seen_store`) or when the server's enrichment pass
   * failed silently — the SPA treats both cases identically
   * (no indicator).
   */
  is_new_for_user?: boolean;

  /**
   * TRIAG-329 (knowledge-preservation guard): counts of the
   * operator-authored knowledge attached to this bundle — the rows
   * a purge/restart CASCADE would permanently destroy. Populated
   * ONLY by the detail endpoint (`GET /api/bundles/:uuid`); absent
   * on list rows, on older servers, and when the server-side count
   * failed (fail-open). The purge/restart confirmation dialogs
   * render a ⚠ warning when `notes + jira_links + reviews > 0`;
   * `seen_marks` is informational only (seen-marks re-light on the
   * next view and are harmless to lose).
   */
  operator_artifacts?: OperatorArtifactCounts;

  /**
   * Galley #12510: the patch step started recently and has recorded no
   * outcome yet, so its patches may still come (the patch sweep re-makes
   * the ones a deploy cut). The server's rule is the Hand off's
   * `patches_still_being_made`. Populated ONLY by the detail endpoint;
   * absent on list rows and on older servers, which reads as today.
   */
  patches_pending?: boolean;
}

/**
 * TRIAG-329: operator-knowledge counts for one bundle. Mirrors the
 * Rust `OperatorArtifactCounts` (src/worker/lifecycle/store.rs)
 * field-for-field — the serialization shape is pinned by a Rust
 * unit test (`operator_artifact_counts_serializes_snake_case`).
 */
export interface OperatorArtifactCounts {
  /** Live (non-soft-deleted) notes attached to this bundle. */
  notes: number;
  /** Live (non-soft-deleted) Jira ticket links. */
  jira_links: number;
  /** Review-marks (green checks) that cascade with this bundle. */
  reviews: number;
  /** Per-user seen-marks — harmless to lose; never warned about. */
  seen_marks: number;
}

/**
 * TRIAG-329: true when destroying this bundle would lose
 * irreplaceable operator knowledge. Mirrors the Rust
 * `OperatorArtifactCounts::has_knowledge` exactly — seen-marks
 * deliberately excluded.
 */
export function hasOperatorKnowledge(
  counts: OperatorArtifactCounts | undefined,
): boolean {
  if (!counts) return false;
  return counts.notes > 0 || counts.jira_links > 0 || counts.reviews > 0;
}

// ─── Bundle list response (Wave 9d) ─────────────────────────────────────────

/**
 * `GET /api/bundles` response. Cursor-paginated, sorted newest-
 * first. `next_cursor` is opaque (server-encoded base64 of
 * `(arrived_at, analysis_id)`); echo it back as `?cursor=…` on
 * the next page. `null` → this is the last page.
 */
export interface BundlesListResponse {
  items: BundleLifecycleRow[];
  next_cursor: string | null;
  count: number;
}

// ─── Commit pre-flight review rows (TRIAG-312) ──────────────────────────────

/**
 * One `commit_lifecycle` row, as returned by `GET /api/commits` (in `items`)
 * and `GET /api/commits/:sha`. Matches `CommitRow` in
 * `src/worker/commits/lifecycle.rs` field-for-field. `result_json` is the full
 * structured review (the same `final_report` + `investigations` the worker
 * produced) so the detail page renders without a second S3 fetch.
 */
export interface CommitRow {
  sha: string;
  repo: string;
  branch: string;
  subject: string | null;
  author: string | null;
  committed_at: string | null;
  arrived_at: string;
  queued_at: string | null;
  review_started_at: string | null;
  review_finished_at: string | null;
  last_heartbeat_at: string | null;
  status: string; // queued | reviewing | complete | failed | skipped
  retry_count: number;
  severity_max: string | null; // high | medium | low | none
  serious_findings: number | null;
  /**
   * The single SHIP-DECISION label the list + Slack lead with:
   * `needs_fix` | `ship_with_followup` | `clean`. `null` = not yet labeled
   * (a pre-decision row, before backfill) — rendered "relabel pending", never
   * a guess. This is the primary signal; `severity_max` is engineer detail.
   */
  recommendation: string | null;
  backend_main_sha: string | null;
  /** The model the review's attempt started on. */
  model: string | null;
  /**
   * Detail only (`GET /api/commits/:sha`), Galley #38093: every model the
   * finished review's turns were booked under, first-used first. Absent when
   * the ledger has nothing for it. Two entries mean the review fell back.
   */
  models_used?: string[];
  result_json: CommitReviewJson | null;
  review_md_key: string | null;
  transcript_key: string | null;
  error: string | null;
}

/**
 * The PM-first plain-English TL;DR at the top of a commit review. Produced as
 * STRUCTURED output by the `write_commit_summary` tool and stored under
 * `result_json.plain_tldr` — no free-text parsing. Older reviews may omit it;
 * the web then falls back to parsing `final_report.detailed_summary`.
 */
export interface CommitPlainTldr {
  what: string; // what this commit does (~75 words)
  risk: string; // what could go wrong / is wrong (~90 words)
  example: string; // a real example — one concrete case that gets mishandled (~100 words)
  fix: string; // how to fix (~100 words)
}

/**
 * The mandatory double-check verdict (from the `record_verification` tool),
 * stored at `result_json.verification`. A review is only published once this
 * exists — it is the proof the work was re-examined before shipping.
 */
export interface CommitVerification {
  /**
   * Whether the double-check found the report accurate as-written
   * (`confirmed`) or rewrote it to match the verdict (`needs_update` is
   * resolved by a Phase-5 rewrite before publish, so a published review is
   * always `confirmed`). Optional: older reviews predate this field.
   */
  status?: "confirmed" | "needs_update";
  /**
   * The ship-decision label (`needs_fix` | `ship_with_followup` | `clean`) the
   * reviewer assigned in the double-check. Optional: older reviews predate it.
   */
  recommendation?: "needs_fix" | "ship_with_followup" | "clean";
  revisited: string; // which assumptions/findings were re-examined
  corrections: string; // what changed (or that nothing needed changing)
  verdict: string; // one-line ship verdict
  confidence: string; // high/medium/low + why
}

/** The structured review stored inline in `commit_lifecycle.result_json`. */
export interface CommitReviewJson {
  commit?: {
    short_sha?: string;
    subject?: string;
    author?: string;
    date?: string;
    files_changed?: number;
    added_lines?: number;
  };
  backend?: { main_sha?: string; main_date?: string } | null;
  plain_tldr?: CommitPlainTldr | null;
  verification?: CommitVerification | null;
  final_report?: {
    summary_75_word?: string;
    executive_summary?: string;
    detailed_summary?: string;
  } | null;
  investigations?: CommitInvestigation[];
  /**
   * > 0 means the double-check found the first pass would mislead a reader and
   * the report was REWRITTEN to match the verdict; the detail page surfaces a
   * "revised on review" marker on the verdict card.
   */
  rewrite_attempts?: number;
  [k: string]: unknown;
}

export interface CommitInvestigation {
  title?: string;
  severity?: string;
  root_cause?: { description?: string } | null;
  causal_chain?: { event?: string; mechanism?: string }[];
  user_impact?: string;
  recommendations?: { priority?: string; action?: string }[];
  [k: string]: unknown;
}

export interface CommitsListResponse {
  items: CommitRow[];
  next_cursor: string | null;
  count: number;
}

// ─── Route summary (Wave 12 — RouteSummaryStrip on /bundles) ────────────────

/**
 * `GET /api/bundles/route-summary?window_hours=N` response — per-
 * route bundle counts over a rolling time window.
 *
 * Field shape mirrors the Rust `RouteSummary` struct in
 * `src/worker/lifecycle/store.rs` exactly. Every number is a
 * non-negative count; `total` is the sum of the seven per-route
 * fields, precomputed server-side so the SPA doesn't need to
 * re-sum. `window_hours` is the effective window (after defensive
 * clamping) — the SPA renders a `(last N hours)` subtitle from
 * this value so the caption matches exactly what was queried.
 *
 * Route derivation is priority-ordered; see
 * `Bundles.tsx::deriveRoute` for the client-side mirror of the
 * same rules. Per CONTRIBUTING §"Best practices at a glance"
 * UI/UX rule 5, the `RouteSummaryStrip` renders each count as a
 * chip carrying color + label + glyph together — never color
 * alone.
 */
export interface RouteSummaryResponse {
  /** `last_error IS NOT NULL` — pipeline hit a fatal error. */
  failed: number;
  /** Classifier-rejected (version gate, unsupported platform, or classifier error). */
  skipped: number;
  /** `was_deduplicated = TRUE` — dedup layer matched this to an existing canonical. */
  deduped: number;
  /** `completed_at IS NOT NULL` and none of the above — full analysis finished. */
  analyzed: number;
  /**
   * 2026-05-03 Slice A — `analysis_started_at IS NOT NULL AND
   * NOW() - analysis_started_at > INTERVAL '2 hours'` and none of
   * the above. 2h heuristic flavor of "analysis started but never
   * completed and no error written." MUST precede `analyzing` in
   * the priority-ordered cascade because every stalled row
   * satisfies the analyzing predicate too — documented in
   * `pipelineStages.ts::deriveRoute` + the matching backend CASE
   * WHEN in `stores/postgres.rs::route_summary`. Divergence across
   * the three is the exact silent-divergence pathology
   * `CONTRIBUTING.md §"Failure visibility"` prohibits.
   *
   * Distinct from the SPA's `isStalled` predicate (which also
   * catches the registry-empty sub-second-precise case). The
   * aggregate SQL can only see persisted state; the per-bundle
   * `StalledBundleBanner` surfaces the registry signal. After
   * the 2h mark the two converge.
   */
  stalled: number;
  /**
   * `analysis_started_at IS NOT NULL` — LLM pipeline running right
   * now and within the 2h healthy-duration window. Past 2h the
   * row rolls into `stalled` instead.
   */
  analyzing: number;
  /** `dedup_decided_at IS NOT NULL` — first occurrence, waiting for the analysis loop. */
  queued: number;
  /** Pre-dedup limbo — arrived / retrieved / triaged / classified. */
  intake: number;
  /**
   * Galley #11179: arrived over an hour ago and never got further (the
   * `stranded` arm, just before `intake` in the cascade). Additive;
   * optional so a response without it reads as 0.
   */
  stranded?: number;
  /** Sum of every per-route count. */
  total: number;
  /** Effective window (in hours) after defensive clamping. */
  window_hours: number;
}

// ─── Bundle status response (Wave 9c) ───────────────────────────────────────

/**
 * Nested `timestamps` object on [`BundleStatusResponse`]. One
 * entry per lifecycle transition; every entry except
 * `arrived_at` is nullable because rows don't reach terminal
 * states atomically.
 */
export interface BundleStatusTimestamps {
  arrived_at: string;
  retrieved_from_s3_at: string | null;
  triage_completed_at: string | null;
  classified_at: string | null;
  dedup_decided_at: string | null;
  analysis_started_at: string | null;
  analysis_finished_at: string | null;
  completed_at: string | null;
}

/**
 * Compact polling shape for `GET /api/bundles/:uuid/status`
 * (Wave 9c). The `stage` string is one of: `"arrived"` |
 * `"downloaded"` | `"triaged"` | `"classified"` |
 * `"dedup-decided"` | `"analyzing"` | `"post-analysis"` |
 * `"complete"` (computed server-side via
 * `BundleLifecycleRow::pipeline_stage`).
 *
 * The backend sets `Cache-Control: max-age=3` on the response
 * so the browser coalesces 5-second-interval polling across
 * multiple tabs efficiently. The `/bundles/:uuid` page wraps
 * this in a `useQuery` with `refetchInterval: 5000` that stops
 * once `timestamps.completed_at` is non-null.
 */
export interface BundleStatusResponse {
  analysis_id: string;
  stage: string;
  timestamps: BundleStatusTimestamps;
  dedup_methodology: string | null;
  canonical_analysis_id: string | null;
  priority: string | null;
  pdf_url: string | null;
  expires_at: string | null;

  /**
   * 2026-05-02 Slice B — live in-memory registry view.
   *
   * `null` when:
   *   - This worker process isn't holding the analysis (never
   *     picked up, already completed/failed, or — future
   *     multi-worker — held by a different worker process).
   *   - The worker's AppState was constructed without a registry
   *     (test rigs, CLI mode).
   *
   * Non-null → the Kill button should appear (admin-only) and the
   * BundleDetail page renders a green "Worker active — on phase X,
   * elapsed Y" chip. When `kill_requested` is true inside the
   * populated object, the button shifts to amber "Killing…" state
   * and is disabled until the registry entry drops (which happens
   * when the worker writes `last_error` and the spawned task's
   * `InFlightGuard` drops).
   *
   * Also gates the Restart button: a non-null `in_flight` value
   * means Restart is hidden (the server predicate
   * `is_row_restartable` returns false for actively-held rows to
   * prevent accidental destruction of live LLM work).
   */
  in_flight: InFlightInfo | null;

  /**
   * Phase 1 Part B — Priority 4 (2026-05-06): canonical retry/
   * supersede state, folded into one wire-level view so the SPA's
   * `CanonicalHealthBanner` can render a single at-a-glance state
   * without cross-referencing multiple fields.
   *
   * `null` / `undefined` means the canonical is healthy (no
   * retry/supersede state of operator interest) — the banner is
   * suppressed entirely per CONTRIBUTING UI/UX rule 2 ("empty
   * states distinguish filter-narrowed from data-empty; don't
   * render a 'nothing to see here' block when there's nothing
   * to see").
   *
   * Non-null → the SPA renders `CanonicalHealthBanner` in one of
   * four variants (see `state`). Added 2026-05-06 alongside the
   * rest of Priority 4.
   */
  canonical_retry_view?: CanonicalRetryStateView | null;
}

/**
 * Phase 1 Part B — Priority 4 (2026-05-06): wire-level view of a
 * canonical's retry/supersede state for the SPA's
 * `CanonicalHealthBanner` component.
 *
 * One object with a tagged `state` field (kebab-case). Matches the
 * Rust `CanonicalRetryStateView` struct in
 * `src/server/routes/api/bundles.rs` byte-for-byte. Every field is
 * always present on the wire; fields not relevant to the current
 * `state` are rendered as `0` / `null` and the SPA ignores them.
 *
 * The four `state` values map to the CanonicalHealthBanner's four
 * rendered shapes:
 *
 * - `"unhealthy"` — canonical's last analysis failed, retries still
 *   available (`budget_remaining > 0`). Amber banner: "⚠ Analysis
 *   failed. Auto-retry budget: N/default remaining."
 * - `"superseded"` — this canonical was superseded by a newer one.
 *   Slate banner with a link to the successor: "↗ This canonical
 *   was superseded by <short>."
 * - `"promoted"` — this canonical was promoted FROM an older
 *   budget-exhausted canonical. Informational slate banner: "↑
 *   Promoted from <old_short> (gen N)."
 * - `"permanently-failed"` — budget exhausted AND at depth cap.
 *   Red banner: "❌ Permanently failed — depth cap N reached."
 *
 * `"healthy"` is NOT a state — healthy canonicals return
 * `canonical_retry_view: null` and the SPA suppresses the banner.
 */
export type CanonicalRetryStateViewKind =
  | "unhealthy"
  | "superseded"
  | "promoted"
  | "permanently-failed";

export interface CanonicalRetryStateView {
  /** Banner-variant discriminator; always present. */
  state: CanonicalRetryStateViewKind;
  /**
   * Current budget remaining. `0` when exhausted; `>0` when
   * UNHEALTHY with retries left. Only meaningful when
   * `state === "unhealthy"` or `"permanently-failed"`.
   */
  budget_remaining: number;
  /**
   * Policy default budget — rendered as the denominator in
   * `{N}/{default}` strings.
   */
  budget_default: number;
  /**
   * Lineage generation of THIS canonical. `0` for originals.
   */
  generation: number;
  /**
   * Policy depth-cap — rendered as the "depth cap {N} reached"
   * label on the permanently-failed banner.
   */
  depth_cap: number;
  /**
   * When `state === "superseded"`, the newer canonical's
   * analysis_id; SPA renders a short-ID link to `/bundles/:uuid`.
   * `null` on every other state.
   */
  superseded_by: string | null;
  /**
   * When `state === "promoted"`, the older canonical this one
   * replaced. `null` on every other state.
   */
  supersedes: string | null;
  /**
   * Total retries fired for this signature's lineage; monotonic,
   * survives promotions. `0` when no retry has fired yet.
   */
  total_retries: number;
}

// ─── Analysis detail response (Wave 9e) ─────────────────────────────────────

/**
 * `GET /api/analyses/:uuid` response. Includes every
 * `BundleLifecycleRow` field flattened at the top level (via
 * `#[serde(flatten)]` on the Rust side) plus two siblings:
 *
 * - `analysis_json` — the parsed `analysis.json` from S3
 *   (fetched server-side), or `null` when the object doesn't
 *   exist (pre-analysis row, or `NoSuchKey` from S3 — which
 *   degrades to `null` rather than 502 per the handler's
 *   tolerance contract).
 * - `duplicates_matched_count` — how many OTHER bundles share
 *   this bundle's canonical. Never negative.
 *
 * Represented as an intersection-type over `BundleLifecycleRow`
 * so TypeScript knows every row field is present without
 * re-declaring each one. Requires a type-only merge (not
 * `extends`) because `BundleLifecycleRow` is an interface and
 * we can't extend it with additional nullable-field-free
 * properties cleanly.
 */
export type AnalysisDetailResponse = BundleLifecycleRow & {
  analysis_json: Record<string, unknown> | null;
  duplicates_matched_count: number;
  /**
   * TRIAG-341: canonical-mapped lineage hash for the "Issue history
   * & notes" panel. For a deduped row this is the CANONICAL's
   * `hash_sha256` — a vector-matched duplicate's own hash differs
   * from its canonical's and would key the wrong lineage — for a
   * canonical row it equals the row's own hash. `null` (user-filed
   * one-offs / web) suppresses the panel entirely, matching
   * /trends' no-affordance semantics for one-offs. Deliberately
   * required (not optional): the SPA and server deploy atomically
   * on the one staging box, and an own-hash fallback here would
   * silently reintroduce the wrong-lineage bug on old servers.
   */
  lineage_hash_sha256: string | null;
};

// ─── Duplicates response (Wave 9g) ──────────────────────────────────────────

/**
 * One bucket in the per-day occurrence histogram on
 * [`DuplicatesResponse`]. `day` is a UTC calendar date in
 * `YYYY-MM-DD` format. Server omits days with zero matches.
 */
export interface HistogramBucket {
  day: string;
  count: number;
}

/**
 * `GET /api/duplicates/:canonical_uuid` response. The canonical
 * row + every row deduped against it (capped at 200 per the
 * backend's `MAX_DUPLICATES`) + a per-day histogram computed
 * from `matches.arrived_at`.
 *
 * `truncated` is syntactic sugar for `total_matches >
 * matches.length` so the SPA doesn't have to compute the
 * comparison itself on every render.
 */
export interface DuplicatesResponse {
  canonical: BundleLifecycleRow;
  matches: BundleLifecycleRow[];
  histogram: HistogramBucket[];
  total_matches: number;
  truncated: boolean;
  /**
   * Phase 2 P2.2 — Priority 9 (2026-05-06): `true` when the
   * response was expanded to the full signature lineage via
   * `?full=true` AND the canonical carried a non-NULL
   * `(hash_sha256, dedup_scope_key)` signature. `false` on the
   * default single-canonical path OR when the canonical has
   * NULL signature fields (silent degrade — the response still
   * carries the single-canonical match set).
   *
   * `#[serde(default)]` on the Rust side → missing on the wire
   * means `false`. SPA reads this to toggle the "Show full
   * signature family" button label + the banner explaining the
   * expanded view.
   */
  full_lineage?: boolean;
  /**
   * Phase 2 P2.2 — Priority 9 (2026-05-06): count of canonical
   * rows (`was_deduplicated = false`) sharing the signature
   * `(hash_sha256, dedup_scope_key)` with the requested
   * canonical. `1` = steady state; `>1` indicates supersede-
   * chain lineage or pre-Phase-0 window-boundary forks. `0` on
   * degraded cases (canonical has NULL signature — pre-dedup
   * skips). The SPA surfaces this count in a chip + renders
   * the "Show full signature family →" button when `>1`.
   */
  canonical_count_in_signature?: number;
  /**
   * Phase 2 P2.2 — Priority 9 (2026-05-06): the signature's
   * `hash_sha256`. `null` when the canonical's row has NULL
   * `hash_sha256`. Exposed so the SPA can construct the
   * `?full=true` URL + surface signature identity in the
   * expanded-view banner.
   */
  signature_hash_sha256?: string | null;
  /**
   * Phase 2 P2.2 — Priority 9 (2026-05-06): the signature's
   * `dedup_scope_key`. Same `null` semantics as
   * `signature_hash_sha256`.
   */
  signature_dedup_scope_key?: string | null;
  /**
   * TRIAG-421: analysis ids (matches + canonical) with an active
   * analyse request — rendered as "Queued" in the Analysis column.
   * TRIAG-433: also covers the rows of `analysed_duplicates`.
   */
  active_rush_ids?: string[];
  /**
   * TRIAG-433: this canonical's duplicates that someone sent for their
   * own analysis (any state), newest first, at most 50 — including ones
   * older than the 200 newest in `matches`. Path canonical only, also in
   * the full-lineage view. Absent on older servers.
   */
  analysed_duplicates?: BundleLifecycleRow[];
  /**
   * Galley #39373: how many of the family's reports have an analysis of their own, all
   * time, the same number Trends shows (`lib/analysed.ts`). Absent on older servers.
   */
  analyzed_count?: number;
}

// ─── Trend report response (Wave 9i) ────────────────────────────────────────

/**
 * Priority 2 (2026-05-01) — sortable columns on the `/trends`
 * Families table. Mirrors the Rust `TrendSortKey` enum in
 * `src/worker/lifecycle/store.rs`. Wire format is snake_case.
 *
 * Five keys:
 *
 * - `total_count` — aggregate occurrence count (pre-Priority-2
 *   default).
 * - `last_seen` — MAX(arrived_at) across the family. DESC
 *   surfaces "firing right now" — the primary 2026-05-01
 *   operator ask.
 * - `first_seen` — canonical's arrived_at. DESC surfaces
 *   newly-emerged signatures.
 * - `canonical_count` — distinct canonicals sharing the signature
 *   (supersede chains + pre-Phase-0 forks).
 * - `scope` — lexicographic (platform, app_version,
 *   build_number). ASC groups by platform first.
 */
export type TrendSortKey =
  | "total_count"
  | "last_seen"
  | "first_seen"
  | "canonical_count"
  | "scope";

/**
 * Priority 2 (2026-05-01) — sort direction. Lowercase wire
 * format to match the Rust `TrendSortDir` serde form.
 */
export type TrendSortDir = "asc" | "desc";

/** Priority 2 (2026-05-01) — default sort key (pre-change behavior). */
export const DEFAULT_TREND_SORT_KEY: TrendSortKey = "total_count";
/** Priority 2 (2026-05-01) — default sort direction. */
export const DEFAULT_TREND_SORT_DIR: TrendSortDir = "desc";

/**
 * Natural first-click direction per sort key. DESC for numeric +
 * time columns (operator wants "highest / newest first" on the
 * first click); ASC for the composite Scope column (alphabetical
 * reads as a natural grouping). Matches the same pattern the
 * `/bundles` page's `nextSortState` uses.
 */
export function naturalTrendSortDir(key: TrendSortKey): TrendSortDir {
  switch (key) {
    case "scope":
      return "asc";
    case "total_count":
    case "last_seen":
    case "first_seen":
    case "canonical_count":
      return "desc";
  }
}

/**
 * Per-methodology counts for one `TrendFamily`. `hash_hits` +
 * `vector_hits` + `first_occurrence` sum to `total_count` on the
 * parent family.
 */
export interface MethodologyBreakdown {
  hash_hits: number;
  vector_hits: number;
  first_occurrence: number;
}

/**
 * Client identity tuple — the `(platform, app_version,
 * build_number)` columns of the canonical row — used as a key
 * in the families table to let operators scope a family to its
 * shipped build. Every field is nullable because the canonical
 * row's columns themselves are nullable.
 */
export interface TrendScope {
  platform: string | null;
  app_version: string | null;
  build_number: string | null;
}

/**
 * One recurring-family row on the `/trends` page. `total_count`
 * is the count of rows in `bundle_lifecycle` whose
 * `canonical_analysis_id` points at this canonical (inclusive of
 * the canonical itself — a family with `total_count = 1` is a
 * singleton with no duplicates yet).
 *
 * `canonical_short_uuid` is computed server-side (first 8 chars
 * of `canonical_analysis_id`) so the SPA doesn't need to
 * reimplement the project's `short_id` rule in TypeScript.
 */
/**
 * Priority 1 (2026-05-06) — discriminator between
 * signature-grouped recurring-bug families and one-off
 * user-filed reports. Operator directive: `/trends` is the
 * primary interaction surface and must NOT silently exclude
 * user-filed reports (mobile shakes, Usersnap web feedback,
 * empty-error bundles, rush-bypass) even though those rows
 * have NULL `hash_sha256` and/or NULL `dedup_scope_key`.
 *
 * Wire format is kebab-case; mirrors the Rust `FamilyKind`
 * enum in `src/worker/lifecycle/store.rs`.
 *
 * - `"signature"` — multiple rows sharing the same
 *   `(hash_sha256, dedup_scope_key)` tuple, consolidated via
 *   the Phase 2 P2.1 CTE. `total_count >= 1`.
 * - `"one-off"` — a single user-filed report with NULL
 *   signature identity. `total_count === 1` always.
 */
export type FamilyKind = "signature" | "one-off";

export interface TrendFamily {
  /**
   * Priority 1 (2026-05-06): discriminator between
   * signature-grouped recurring-bug families and one-off
   * user-filed reports. Missing on older worker builds that
   * pre-date Priority 1; the SPA treats missing as `"signature"`
   * for backward compat.
   */
  kind?: FamilyKind;
  canonical_analysis_id: string;
  canonical_short_uuid: string;
  scope: TrendScope;
  preview: string | null;
  total_count: number;
  first_seen: string;
  last_seen: string;
  methodology_breakdown: MethodologyBreakdown;
  /**
   * Phase 2 P2.1 (2026-05-06): number of distinct canonical rows
   * sharing this family's `(hash_sha256, dedup_scope_key)`
   * signature. `1` is the steady state; values `> 1` indicate
   * EITHER pre-Phase-0 window-boundary forks OR Phase-1 supersede
   * chains. The SPA renders a `↺ N canonicals` chip when > 1
   * and surfaces the full lineage via `/duplicates/:uuid?full=true`
   * (Phase 2 P2.2, follow-up commit).
   *
   * Optional field for backward compat — older worker builds
   * that pre-date P2.1 don't set this; SPA treats missing as
   * equivalent to `1`.
   */
  canonical_count_in_signature?: number;
  /**
   * TRIAG-59 v2 (2026-05-19): number of CURRENTLY-ACTIVE
   * canonical rows for this signature — i.e. rows where
   * `was_deduplicated = false AND canonical_superseded_by IS NULL`.
   * Distinct from `canonical_count_in_signature` which counts
   * ALL canonicals in the lineage (including superseded ones).
   *
   * `active_variant_count >= 2` is the rendering condition for
   * the Grouped-view chevron + drill-down: each active canonical
   * is a v2 sub-pattern variant. `#[serde(default)]` so older
   * cached SPA bundles see 0 instead of a parse error.
   */
  active_variant_count?: number;
  /**
   * TRIAG-59 v2 (2026-05-19): number of canonicals in this
   * signature that the current session user has reviewed.
   * Populated ONLY by the `/api/trends` handler's per-user
   * enrichment pass; absent on cached / unenriched responses.
   *
   * Drives the parent-row chip's partial-state rendering:
   * - `0` → "Mark all reviewed" affordance
   * - `1..active_variant_count` → "✓ N/M reviewed" partial
   * - `==active_variant_count` → "✓ Reviewed" complete
   *
   * Optional because the field is dropped via
   * `skip_serializing_if` when not enriched (e.g. the user_seen
   * store is None in dev/test rigs). The SPA treats missing as
   * "fall back to legacy per-canonical chip behavior."
   */
  reviewed_canonical_count?: number;
  /**
   * Galley #39373: how many of the family's reports have an analysis of their own, all
   * time (`lib/analysed.ts`). Absent on older servers: then nothing new shows.
   */
  analyzed_count?: number;
  /**
   * TRIAG-59 v2 (2026-05-20): total non-deleted bundle_notes count
   * across every bundle in this signature (i.e. across every
   * sub-signature). Populated by the `/api/trends` handler's
   * `count_for_signature_bulk` enrichment.
   *
   * Drives the Grouped-view multi-sub-signature parent row's
   * read-only "N notes" indicator. Operators don't interact with
   * notes at the signature level (notes attach to bundles inside
   * sub-signatures); the count is purely informational and the
   * make/edit affordance lives on each drill-down row's per-
   * canonical activity chip.
   *
   * Optional because the field is dropped via
   * `skip_serializing_if` when not enriched.
   */
  total_note_count?: number;
  /**
   * Phase 2 P2.1 (2026-05-06): the signature's `hash_sha256`
   * (64 lowercase hex chars). Exposed so the SPA can build
   * signature-scoped links (family expansion, "Consolidate this
   * signature" CLI hint). Optional for backward compat.
   */
  hash_sha256?: string | null;
  /**
   * Phase 2 P2.1 (2026-05-06): the signature's `dedup_scope_key`
   * (platform + app_version + build_number hash). Optional for
   * backward compat.
   */
  dedup_scope_key?: string | null;

  /**
   * 2026-05-01 operator directive — per-bucket activity
   * histogram covering the queried window at the granularity
   * the handler derived from `window_hours`:
   *
   *   - `window_hours <= 24` → hourly buckets (≤ 24 possible)
   *   - `window_hours >  24` → daily buckets (N possible)
   *
   * Drives the `/trends` chart so operators see the real
   * distribution of a family's occurrences across the window —
   * replaces the pre-change "spike on first_seen" approximation
   * that rendered a single tall bar at an arbitrary hour.
   *
   * Only buckets with `count > 0` are returned from the
   * backend; the SPA's `buildChartSeries` fills in zeros for
   * absent buckets by constructing a dense bucket list client-
   * side from the window bounds + granularity.
   *
   * Optional for backward compat — older worker builds that
   * pre-date this field return `undefined`; SPA falls back to
   * the first_seen-spike rendering in that case so no client
   * hard-fails on a stale JSON fixture.
   */
  histogram?: TrendHistogramBucket[];

  /**
   * Commit B (2026-05-01) — per-user "new" indicator. Populated
   * ONLY by the `/api/trends` list-endpoint enrichment pass
   * against the session user's `user_seen_bundles` state.
   *
   * Signature-scoped predicate: `true` iff the user has never
   * engaged with any bundle in this signature's lineage OR
   * `family.last_seen` is strictly after the user's most recent
   * engagement timestamp across the lineage.
   *
   * One-off predicate (NULL hash/scope): `true` iff the
   * canonical's `analysis_id` is not in `user_seen_bundles`.
   *
   * Broken canonicals (sentinel `"signature:..."` IDs): always
   * `false` — non-drillable rows don't carry a "new" signal.
   *
   * First-visit-ever path: handler bulk-marks every current
   * family's canonical as seen → all rows return `false`
   * (calm-onboarding).
   *
   * Absent when `DATABASE_URL` is unset (dev/test) or the
   * enrichment pass silently failed — the SPA renders no
   * indicator in both cases.
   */
  is_new_for_user?: boolean;

  /**
   * TRIAG-59 PHASE 4 (2026-05-21): the iOS feature_area the
   * canonical's `properties.source` resolves to (e.g.
   * `"MagicEditor"`, `"Onboarding"`, `"BotDesigner"`).
   * `undefined` when the canonical's row has NULL `feature_area`
   * (web / Android / un-backfilled bundles). Drives the per-row
   * Feature chip in the Preview cell.
   */
  feature_area?: string;

  /**
   * TRIAG-59 PHASE 4 (2026-05-21): count of distinct non-empty
   * `username` values across every bundle in the signature's
   * window aggregate. Drives the optional "N users" sub-line
   * under the Count cell when the value is suspiciously low
   * relative to total_count (synthetic / test-traffic signal).
   */
  distinct_users?: number;

  /**
   * TRIAG-59 PHASE 4 (2026-05-21): total_count over the
   * disjoint immediately-prior window of the same length.
   * Populated only when the SPA passed `?include_prior=true`
   * (the "vs prior window" toggle). The SPA computes a delta
   * against `total_count` and renders an ↑/↓ chip with
   * Poisson-noise-aware coloring.
   */
  prior_window_total_count?: number;
}

/**
 * 2026-05-01 operator directive — one bucket in a
 * `TrendFamily.histogram` array. Mirrors the Rust
 * `TrendHistogramBucket` struct in
 * `src/worker/lifecycle/store.rs`.
 *
 * `bucket_start` is an ISO-8601 UTC timestamp at the bucket
 * boundary (`date_trunc('hour', ...)` or
 * `date_trunc('day', ...)` depending on the query's
 * granularity). `count` is the number of occurrences whose
 * `arrived_at` fell within that bucket.
 */
export interface TrendHistogramBucket {
  bucket_start: string;
  count: number;
}

/**
 * 2026-05-01 — granularity discriminator for the trends chart.
 *
 * TRIAG-36 (2026-05-17) expanded from `"hour" | "day"` to
 * include three minute-bucket variants so sub-day windows
 * (1h / 3h / 6h) produce ~12 bars instead of the prior
 * placeholder + ChartCard mix:
 *
 *   - `"5min"`  → 1h window  (12 bars)
 *   - `"15min"` → 3h window  (12 bars)
 *   - `"30min"` → 6h window  (12 bars)
 *   - `"hour"`  → 12h, 24h  (12 / 24 bars)
 *   - `"day"`   → > 24h     (N bars)
 *
 * The backend's `HistogramGranularity` enum serializes to these
 * exact strings via `#[serde(rename = "5min" / "15min" / "30min")]`
 * + the type-level `rename_all = "lowercase"` for hour/day.
 */
export type HistogramGranularity = "5min" | "15min" | "30min" | "hour" | "day";

/**
 * Users page (TRIAG-427): chart bucket sizes = the wire `HistogramGranularity`
 * plus `"week"` (Monday 00:00 UTC, matching Postgres `date_trunc('week')`).
 * Deliberately NOT added to `HistogramGranularity`: that union mirrors the
 * Rust enum (pinned by histogram-granularity.test.ts) and feeds the
 * exhaustive switches in Trends.tsx.
 */
export type ChartGranularity = HistogramGranularity | "week";

/**
 * Pick the granularity that pairs with a given window size.
 * Must stay in lockstep with
 * `HistogramGranularity::for_window_hours` in
 * `src/worker/lifecycle/store.rs` — the SPA's chart bucket list
 * is sized using this function + the backend's histogram is
 * computed using the same rule, so they agree on bucket
 * boundaries by construction.
 *
 * 2026-05-17 (TRIAG-36): added the three minute variants.
 * Returns the type-level result; production callers should
 * prefer the wire-emitted `TrendReport.granularity` field for
 * the single-source-of-truth path.
 */
export function trendHistogramGranularity(
  windowHours: number,
): HistogramGranularity {
  // Mirrors src/worker/lifecycle/store.rs::HistogramGranularity::for_window_hours.
  // Backend tests pin the boundaries exactly; the SPA pins them
  // here for the fallback path when a (pre-TRIAG-36) backend
  // response doesn't carry the `granularity` field.
  if (windowHours <= 1) return "5min";
  if (windowHours <= 3) return "15min";
  if (windowHours <= 6) return "30min";
  if (windowHours <= 24) return "hour";
  return "day";
}

/**
 * Step size in milliseconds for each granularity. Used by the
 * dense bucket-list builder in `Trends.tsx::buildChartSeries`.
 */
export function histogramStepMs(g: ChartGranularity): number {
  switch (g) {
    case "5min":
      return 5 * 60 * 1000;
    case "15min":
      return 15 * 60 * 1000;
    case "30min":
      return 30 * 60 * 1000;
    case "hour":
      return 60 * 60 * 1000;
    case "day":
      return 24 * 60 * 60 * 1000;
    case "week":
      return 7 * 24 * 60 * 60 * 1000;
  }
}

/**
 * Truncate a Date to the bucket boundary for the given
 * granularity. Mirrors the Rust
 * `HistogramGranularity::truncate` impl: hour/day zero out the
 * lower fields; the minute variants round DOWN to the nearest
 * multiple of `step_seconds` from the `2001-01-01 UTC` origin
 * (matching the Postgres `date_bin` SQL).
 */
export function histogramTruncate(ts: Date, g: ChartGranularity): Date {
  switch (g) {
    case "5min":
    case "15min":
    case "30min": {
      const stepMs = histogramStepMs(g);
      const ms = ts.getTime();
      // Floor-from-epoch matches date_bin's origin-based
      // alignment (the origin `2001-01-01 UTC` is on a clean
      // 5/15/30-min boundary from epoch 0).
      const bucketMs = Math.floor(ms / stepMs) * stepMs;
      return new Date(bucketMs);
    }
    case "hour": {
      const d = new Date(ts);
      d.setUTCMinutes(0, 0, 0);
      return d;
    }
    case "day": {
      const d = new Date(ts);
      d.setUTCHours(0, 0, 0, 0);
      return d;
    }
    case "week": {
      // Monday 00:00 UTC (ISO week, = Postgres date_trunc('week')). Not
      // floor-from-epoch: 1970-01-01 was a Thursday.
      // getUTCDay(): Sun=0 … Sat=6 → days since Monday = (day + 6) % 7.
      const d = new Date(ts);
      d.setUTCHours(0, 0, 0, 0);
      d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
      return d;
    }
  }
}

/**
 * `GET /api/trends` response. The three window-level count
 * fields (`total_bundles_in_window`, `total_deduped`,
 * `dedup_rate`) are NOT filtered by the query params — they
 * answer "how's the window going overall." Only `families` is
 * filter-aware.
 *
 * `dedup_rate` is a float in `[0.0, 1.0]`, always present (0.0
 * for an empty window — not `NaN`, which would serialize as
 * `null` and break the SPA's number-formatter).
 */
export interface TrendReport {
  /**
   * The window size in hours that was actually queried. Echoed
   * back on every response so the SPA can render the "Showing
   * last N hours/days" label without trusting its own
   * query-param state.
   *
   * TRIAG-32 (2026-05-15): renamed days → hours so the
   * /trends segmented control can offer 1h / 3h / 6h / 12h
   * options. 24h = 1 day; 168h = 7 days; 8760h = 365 days.
   */
  window_hours: number;
  total_bundles_in_window: number;
  total_deduped: number;
  dedup_rate: number;
  families: TrendFamily[];

  /**
   * Histogram bucket granularity used for every family's
   * `histogram[]` array (TRIAG-36, 2026-05-17). Derived
   * server-side from `window_hours` via
   * `HistogramGranularity::for_window_hours`; echoed back on
   * the wire so the SPA chart renderer can drive its
   * dense-bucket-list step + x-axis tick density off the
   * authoritative source rather than re-implementing the
   * dispatch.
   *
   * **Backward compat**: optional in TS for SPA clients
   * mid-deploy that may receive a pre-TRIAG-36 response. The
   * chart renderer falls back to `trendHistogramGranularity(
   * window_hours)` when this field is absent.
   */
  granularity?: HistogramGranularity;

  /**
   * Galley #30414 (PKG 8): present only when the request asked
   * `?hide_moderation=true`. How many reports, in how many
   * families, this response left out because their defining error
   * is a moderation refusal, and per rule (`rules`, labelled by the
   * backend from the same list the SQL uses). `truncated` = the count
   * hit the families limit, so it's a floor. Absent from older
   * backends, and then the page says nothing about it.
   * MODERATION CONTRACT v2 (#30470).
   */
  moderation_hidden?: {
    reports: number;
    families: number;
    rules?: { id: string; label: string; reports: number }[];
    truncated?: boolean;
  };
}

// ─── Analysis conversation response (Feature B — 2026-05-01) ────────────────

/**
 * One content block inside a `ConversationMessage.content[]`.
 * Heterogeneous union covering the four observed block types in
 * staging's `conversation.json` schema v1.0 (probed 2026-05-01
 * against analysis `173ddb53-6235-43bc-a691-c7996f3c1f1b`, 125
 * tool calls, 180 messages, ~935 KB total content).
 *
 * Fields beyond the discriminant are best-effort — the server
 * streams the S3 object through without schema enforcement so a
 * future pipeline-side schema bump surfaces as missing-field
 * rendering rather than a hard error on this side.
 */
export type ConversationBlock =
  | { type: "text"; text?: string | null }
  | { type: "thinking"; thinking?: string | null }
  | {
      // 2026-05-01 late-evening follow-up — `redacted_thinking`
      // is Anthropic's wire shape for safety-filtered reasoning
      // blocks. The server emits these when internal reasoning
      // would have violated a safety policy; the `data` field is
      // an encrypted payload (base64-ish) that preserves
      // conversation-level signature integrity for multi-turn
      // round-tripping but hides the human-readable content.
      //
      // Expected occurrence on our domain (technical log /
      // code analysis): low but non-zero per the 2026-05-01
      // expert consultation. The SPA MUST render this variant
      // distinctly so operators see the structural presence
      // of the redacted block rather than a mysterious gap in
      // the stream. See `Conversation.tsx::RedactedThinkingBlock`
      // for the renderer.
      //
      // Our Rust backend already preserves these blocks
      // byte-for-byte in `conversation.jsonl` (see
      // `src/anthropic/types.rs::ContentBlock::RedactedThinking`
      // + `conversation.rs` module docstring). The SPA is the
      // final surface that needed to handle this variant.
      type: "redacted_thinking";
      data?: string | null;
    }
  | {
      type: "tool_use";
      id?: string | null;
      name?: string | null;
      input?: unknown;
    }
  | {
      type: "tool_result";
      tool_use_id?: string | null;
      content?: unknown;
      is_error?: boolean | null;
    };

/**
 * One message turn in `ConversationResponse.messages[]`. Role is
 * always present; `content` is a heterogeneous array of
 * `ConversationBlock`s (or, historically, a plain string — the
 * SPA renders strings as a single implicit `text` block).
 */
export interface ConversationMessage {
  role: "user" | "assistant";
  content: ConversationBlock[] | string;
}

/**
 * One system block from `ConversationResponse.system_blocks[]`.
 * Rendered once at the top of the conversation view, collapsed
 * by default. Claude's system-prompt blocks carry `text` (the
 * raw prompt) + optional `cache_control` metadata.
 */
export interface ConversationSystemBlock {
  type?: string;
  text?: string;
  cache_control?: unknown;
}

/**
 * Top-level shape of the `conversation.json` artifact, streamed
 * by `GET /api/analyses/:uuid/conversation`. Every field except
 * `messages` is optional for forward-compat — `analysis_id`,
 * `backend`, `model`, `schema_version`, `system_blocks` may
 * vanish in future schema bumps; the SPA renders whatever it
 * gets.
 */
export interface AnalysisConversation {
  analysis_id?: string;
  backend?: string;
  model?: string;
  schema_version?: string;
  system_blocks?: ConversationSystemBlock[];
  messages: ConversationMessage[];
  /**
   * Present only when a phase's request was rejected as over the model's
   * context window and triage folded the history and sent it again
   * (Galley #33484, the owner's #34168). `messages` above are what the model
   * read last; each entry keeps what the rejected request carried.
   */
  compactions?: ConversationCompaction[];
}

/** One context-overflow fold, as `conversation.json` records it. */
export interface ConversationCompaction {
  phase: string;
  /** RFC 3339 UTC, when the fold ran. */
  at: string;
  /** Bedrock's rejection, at most 500 characters. */
  rejected: string;
  messages_before: number;
  messages_after: number;
  /** The folded span `[span_start, span_end)`, zero-based, in `history_sent`. */
  span_start: number;
  span_end: number;
  blocks_dropped: number;
  thinking_stripped: number;
  history_sent?: ConversationMessage[];
  /**
   * `false` when the analysis was stopped and resumed after this fold: the
   * record came back from the round state, and the history the rejected
   * request carried stayed with the stopped process, so `history_sent` is
   * absent (Galley #34936, evalyn's d8031ba9). Absent on a fold that never
   * restarted.
   */
  history_kept?: boolean;
}

/**
 * Auth-tracked artifact URL helpers (2026-05-01 operator
 * directive: every artifact download must route through the
 * service endpoint, NOT via presigned S3 URLs, so sessions are
 * gated + `page_views` records are emitted).
 *
 * These return the dashboard-side URL that the SPA uses in
 * `<a href>` / `window.location` / `fetch(...)` call sites —
 * they're cookie-authenticated + served by the Rust handlers in
 * `src/server/routes/api/analysis_artifacts.rs`. They do NOT
 * fetch the content themselves.
 *
 * Historical context: pre-Priority-2, `row.pdf_url` etc. were
 * 7-day presigned S3 URLs. Those bypassed auth + tracking. The
 * SPA now uses these helpers instead so every download is
 * session-gated and recorded in `page_views` with the correct
 * `action_kind`. The worker's `mint_fresh_urls_for_row` still
 * mints presigned URLs for a possible future rollback window,
 * but the SPA consumes the auth-tracked path exclusively.
 */
export function artifactPdfUrl(analysisId: string): string {
  return `/api/analyses/${analysisId}/pdf`;
}
export function artifactTriageBriefUrl(analysisId: string): string {
  return `/api/analyses/${analysisId}/triage-brief`;
}
export function artifactAnalysisJsonUrl(analysisId: string): string {
  return `/api/analyses/${analysisId}/analysis-json`;
}

// ─── Commit pre-flight review (TRIAG-312) ───────────────────────────────────

/** Auth-tracked commit-review artifact endpoints (Slack-link targets). */
export function commitReviewMdUrl(sha: string): string {
  return `/api/commits/${sha}/review`;
}
export function commitTranscriptUrl(sha: string): string {
  return `/api/commits/${sha}/transcript`;
}
export function artifactPatchesZipUrl(analysisId: string): string {
  return `/api/analyses/${analysisId}/patches-zip`;
}
export function artifactBugFixUrl(analysisId: string): string {
  return `/api/analyses/${analysisId}/bug-fix`;
}
export function artifactConversationUrl(analysisId: string): string {
  return `/api/analyses/${analysisId}/conversation`;
}
/**
 * 2026-05-01 Priority A follow-up — source-bundle.zip (the
 * original log bundle uploaded by the app). Auth-tracked
 * streaming path; attachment disposition so the browser prompts
 * a download of the binary ZIP rather than trying to render it.
 */
export function artifactSourceBundleUrl(analysisId: string): string {
  return `/api/analyses/${analysisId}/source-bundle`;
}
/**
 * 2026-05-01 Priority A follow-up — detailed_summary.txt (the
 * LLM's intermediate summary). Inline disposition so the browser
 * renders it as plain text in a new tab.
 */
export function artifactDetailedSummaryUrl(analysisId: string): string {
  return `/api/analyses/${analysisId}/detailed-summary`;
}
/**
 * 2026-05-01 Priority A follow-up — verbose.log (the worker's
 * Tier-4 streaming verbose log). Inline disposition.
 */
export function artifactVerboseLogUrl(analysisId: string): string {
  return `/api/analyses/${analysisId}/verbose-log`;
}

/**
 * Migration 023 (2026-05-06) — per-bundle screenshot. Inline
 * disposition + 24h immutable browser cache (the server returns
 * `Cache-Control: private, max-age=86400, immutable`).
 *
 * The `analysisFinishedAt` parameter is appended as a `?v=`
 * query string to act as a cache-buster keyed on the analysis's
 * completion timestamp. Without it, a `/triage rush` re-run that
 * re-uploads new screenshot bytes under the same S3 key would
 * serve stale cached bytes for up to 24h. The query string
 * doesn't affect server-side routing (Axum's `Path` extractor
 * ignores it) but IS part of the browser's cache key.
 *
 * Pass the row's `analysis_finished_at` as `analysisFinishedAt`.
 * Caller-supplied `null` (rare — row that hasn't completed yet)
 * skips the cache-buster; the server's 24h immutable cache will
 * still apply, accepting a small staleness window for the
 * pre-completion case (in practice we don't render screenshots
 * for not-yet-completed bundles anyway, so this is mostly
 * defensive).
 */
export function artifactScreenshotUrl(
  analysisId: string,
  filename: string,
  analysisFinishedAt: string | null,
): string {
  const base = `/api/analyses/${encodeURIComponent(analysisId)}/screenshots/${encodeURIComponent(filename)}`;
  if (analysisFinishedAt === null) {
    return base;
  }
  // ISO timestamp — use `Date.parse` to extract a stable epoch
  // milliseconds value as the cache-buster. Stable across renders
  // for the same `analysis_finished_at`; changes when a rush
  // rerun bumps the timestamp.
  const v = Date.parse(analysisFinishedAt);
  if (Number.isNaN(v)) {
    return base;
  }
  return `${base}?v=${v}`;
}

// ─── Filter options response (Wave 9h) ──────────────────────────────────────────────

/**
 * `GET /api/filter-options` response. Column name → sorted
 * distinct values, used to populate FilterBar's dynamic
 * multi-select dropdowns (app_version, build_number, usernames).
 *
 * The backend caps each column at 500 distinct values (pin in
 * `DistinctColumn::LIMIT_PER_COLUMN`); the SPA treats a 500-
 * entry response as "potentially truncated" without trying to
 * detect it explicitly (the UX is the same — operators narrow
 * via text search when a dropdown gets too long).
 */
export interface FilterOptionsResponse {
  /**
   * Backward-compat shape (Wave 9h): column → bare value
   * array, alphabetically sorted. Populated when the request
   * is sent WITHOUT `?with_counts=true`. The SPA's query
   * wrapper normalizes both shapes into
   * `Record<string, FilterOption[]>` so the UI only sees
   * one shape.
   */
  values?: Record<string, string[]>;

  /**
   * Count-aware shape (2026-05-05): column → sorted
   * `{value, count}` tuples. Populated when the request is
   * sent WITH `?with_counts=true`. The SPA's query wrapper
   * normalizes both shapes into
   * `Record<string, FilterOption[]>` so the UI only sees
   * one shape. See `src/worker/lifecycle/store.rs` for the
   * server-side semantics.
   */
  values_with_counts?: Record<string, Array<{ value: string; count: number }>>;
}

/**
 * TRIAG-307: `GET /api/feature-area-breakdown` response. Powers the
 * /bundles "by feature area" strip — per-area counts PLUS the NULL
 * ("no detected area") bucket + an honest in-scope total that
 * /filter-options can't provide.
 */
export interface FeatureAreaBreakdownResponse {
  areas: Array<{ value: string; count: number }>;
  /** In-scope rows with no detected feature area (the NULL bucket). */
  unknown: number;
  /** Total in-scope rows (detected + unknown). */
  total: number;
  generated_at: string;
}

// ─── Activity time-series (TRIAG-308) ────────────────────────────────────────

/** One bucket of an Activity count series. */
export interface ActivitySeriesPoint {
  bucket_start: string;
  count: number;
}

/** One sub-series of `GET /api/activity/series` (one `group_by` value,
 *  or the single series when ungrouped). */
export interface ActivitySeriesItem {
  key: string;
  label: string;
  points: ActivitySeriesPoint[];
}

/** `GET /api/activity/series` response. Matches `ActivitySeriesResponse`
 *  in `src/server/routes/api/activity_series.rs`. */
export interface ActivitySeriesResponse {
  window_hours: number;
  granularity: HistogramGranularity;
  group_by: string | null;
  series: ActivitySeriesItem[];
  generated_at: string;
}

/** One bucket of the Activity latency series. */
export interface ActivityLatencyPoint {
  bucket_start: string;
  p50_seconds: number;
  p95_seconds: number;
}

/** `GET /api/activity/latency` response. */
export interface ActivityLatencyResponse {
  window_hours: number;
  granularity: HistogramGranularity;
  points: ActivityLatencyPoint[];
  generated_at: string;
}

/** One priority's wait before analysis starts (Galley #24242). Seconds. */
export interface QueueWaitPriority {
  /** `null` when the report's priority was never set. */
  priority: string | null;
  /** Reports whose analysis started in the window. */
  started: number;
  p50_seconds: number;
  p90_seconds: number;
  max_seconds: number;
}

/** One priority's reports waiting for an analysis slot now. */
export interface QueueWaitingNow {
  priority: string | null;
  waiting: number;
  oldest_arrived_at: string;
}

/** `GET /api/activity/queue-wait` response (Galley #24242). */
export interface ActivityQueueWaitResponse {
  window_hours: number;
  priorities: QueueWaitPriority[];
  waiting_now: QueueWaitingNow[];
  generated_at: string;
}

// ─── Commit B (2026-05-01) — user_seen wire types ─────────────────────────

/**
 * `GET /api/user/seen/counts` response body.
 *
 * Drives the TopNav "N new" badges. Polled every ~30s by the
 * shared `useSeenCounts` hook while the dashboard is mounted.
 *
 * Both counts are real aggregates as of Step 2 (2026-05-01
 * follow-up):
 *
 * - `bundles_new` — per-bundle predicate (Commit B, unchanged
 *   since first ship). Counts bundles with `arrived_at >=
 *   user.bundles_first_visit_at` that have no
 *   `user_seen_bundles` row for this user.
 *
 * - `trends_new` — signature-scoped predicate (Step 2). Counts
 *   signatures that either (a) emerged after the user's
 *   `user_surface_first_visit.trends` marker and have no
 *   engagement in the signature's lineage, or (b) have had
 *   activity land after the user's last signature-level
 *   engagement past the relight-grace window (default 120 s).
 *   The predicate knobs are env-var-tunable on the worker side
 *   (`TRENDS_RELIGHT_GRACE_SECONDS`,
 *   `TRENDS_BASELINE_AT_FIRST_VISIT`); the SPA just reads the
 *   resulting count.
 *
 * Pre-Step-2, `trends_new` was hardcoded 0 on the server; SPA
 * consumers that only render the badge when `> 0` saw no badge
 * on the Trends link. Post-Step-2 the badge lights whenever
 * the predicate returns `> 0`, with no SPA deploy required —
 * the defensive `?? 0` shape the badge code uses still works.
 */
export interface SeenCountsResponse {
  bundles_new: number;
  trends_new: number;
  computed_at: string;
}

/**
 * `POST /api/user/seen/mark-all-read` request body.
 *
 * The SPA's "Mark N as read" button pipes the currently loaded
 * list items' analysis IDs + the originating surface here.
 */
export interface MarkAllReadRequest {
  surface: "bundles" | "trends";
  analysis_ids: string[];
}

/**
 * `POST /api/user/seen/mark-all-read` response body.
 *
 * `marked` is the count of IDs in the request — NOT the count
 * of newly-inserted rows (UPSERT semantics mean a repeat mark
 * just advances `seen_at`). Matches what the toast renders.
 */
export interface MarkAllReadResponse {
  marked: number;
  surface: string;
}

/**
 * `POST /api/user/seen/reset` response body. Echoed back into
 * the post-confirm toast: "Reset: cleared N bundles + M markers."
 */
export interface ResetSeenResponse {
  seen_rows_deleted: number;
  first_visit_rows_deleted: number;
}

/**
 * Normalized filter-option shape the UI consumes. Produced
 * from either wire shape by the `queries.filterOptions`
 * wrapper. Matches `dashboard/src/lib/filter-sort.ts::FilterOption`
 * byte-for-byte.
 */
export interface NormalizedFilterOption {
  value: string;
  count?: number;
}

/**
 * Response shape the SPA consumer sees — a single
 * `Record<string, NormalizedFilterOption[]>` regardless of
 * which backend wire shape came back. The `queries.filterOptions`
 * wrapper performs the normalization so callers don't branch
 * on `values` vs `values_with_counts`.
 */
export interface NormalizedFilterOptionsResponse {
  values: Record<string, NormalizedFilterOption[]>;
}

// ─── Backend correlation handles (Slice 1A part 2, migration 024) ──────────
//
// Wire shapes mirror `src/types/handles.rs` and
// `src/server/routes/api/handles.rs` field-for-field. The
// `BundleHandlesResponse` wraps a `HandleGraph` with extraction
// metadata; the `HandleGraph` itself is the same shape stored in
// `bundle_lifecycle.handles_extracted` JSONB.

/**
 * One backend correlation handle extracted from bundle log
 * content at intake time. Matches `BackendHandle` in
 * `src/types/handles.rs`.
 */
export interface BackendHandle {
  /** Canonical kind name (e.g. `room_id`, `server_trace_id`).
   *  See `KNOWN_KINDS` in the Rust crate; unknown kinds render
   *  with a generic icon. */
  kind: string;
  /** The extracted value, lowercased for hex IDs. */
  value: string;
  /** Log file the handle was first seen in. */
  source_file?: string | null;
  /** 1-based line number of first occurrence. */
  source_line?: number | null;
  /** Times this exact (kind, value) pair appeared in the bundle. */
  occurrence_count: number;
  /** True when within ±10 lines of an error marker. */
  near_error: boolean;
  /** Optional human-readable hint about how this handle relates
   *  to the bug (e.g. "in failed PUT /rooms/{room}/users/.../host"). */
  correlation_context?: string | null;
}

/**
 * Anchor handles — the most-relevant subset for the bug at hand.
 * Used by the dashboard hero strip to render 3 always-visible
 * chips. Mirrors `HandleAnchors` in `src/types/handles.rs`.
 */
export interface HandleAnchors {
  reporter_user_id?: string | null;
  reporter_username?: string | null;
  room_at_shake?: string | null;
  primary_bixby_host?: string | null;
}

/**
 * Display-friendly file error from the extractor's diagnostic
 * accumulator. Surfaced in the failure-banner UI.
 */
export interface HandleFileError {
  file_name: string;
  error_message: string;
}

/**
 * Coarse outcome state of one extraction pass. The dashboard
 * `BackendHandlesCard` dispatches on this enum for banner color
 * (green / amber / red / muted-grey) and copy.
 */
export type HandleExtractionState =
  | "complete"
  | "partial"
  | "zero_results"
  | "skipped";

/**
 * Outcome summary surfaced in the dashboard banner. Engineers
 * see this without checking CloudWatch.
 */
export interface HandleExtractionStatus {
  state: HandleExtractionState;
  files_scanned: number;
  /** UN-truncated total error count. */
  file_read_error_count: number;
  /** Sample list capped at 10 in the persisted struct. */
  file_read_errors: HandleFileError[];
  file_parse_error_count: number;
  file_parse_errors: HandleFileError[];
  missing_reporter_anchor: boolean;
  no_reporter_in_bundle: boolean;
  elapsed_ms: number;
}

/**
 * The full handle graph for one bundle. Matches `HandleGraph` in
 * `src/types/handles.rs`.
 */
export interface HandleGraph {
  handles: BackendHandle[];
  /** Index from kind → positions in `handles`. Server pre-computes. */
  by_kind: Record<string, number[]>;
  anchors: HandleAnchors;
  extraction_status?: HandleExtractionStatus | null;
}

/**
 * `GET /api/bundles/:analysis_id/handles` response.
 * Returns 200 with `extracted=false` + empty graph for bundles
 * processed before migration 024.
 */
export interface BundleHandlesResponse {
  analysis_id: string;
  extracted: boolean;
  extracted_at: string | null;
  graph: HandleGraph;
}

/**
 * One id other reports share (Galley #34712): `other_reports` other reports
 * carry the same kind and value, from `other_people` people, and
 * `other_reports_recent` of them arrived in the last `recent_hours` hours.
 * `capped` means more than `cap_per_id` do, and the counts are a floor.
 */
export interface SharedHandle {
  kind: string;
  value: string;
  other_reports: number;
  other_people: number;
  other_reports_recent: number;
  capped: boolean;
}

/** `GET /api/bundles/:id/handles/shared`. Only ids at least one other report shares. */
export interface SharedHandlesResponse {
  analysis_id: string;
  ids_total: number;
  ids_counted: number;
  per_kind_limit: number;
  cap_per_id: number;
  recent_hours: number;
  items: SharedHandle[];
}

// ─── Backend dossier (2026-09-24) ────────────────────────────────────────────────
//
// Mirrors `src/worker/backend_dossier/types.rs::Dossier` (serde
// snake_case). Served verbatim from S3 by
// `GET /api/bundles/:id/backend-dossier`.

export type BackendDossierStatus = "complete" | "partial" | "skipped" | "unavailable";

export interface BackendDossierQueryStat {
  name: string;
  window_secs: number;
  bytes: number;
  ms: number;
  rows: number;
  status: string;
}

export interface BackendDossier {
  schema_version: number;
  analysis_id: string;
  status: BackendDossierStatus;
  reason?: string | null;
  built_at: string;
  elapsed_ms: number;
  report_moment_utc?: string | null;
  device_offset_secs?: number | null;
  device_offset_source: string;
  reporter_source: string;
  queries?: BackendDossierQueryStat[];
  bytes_scanned: number;
  prompt_text: string;
  full_sections?: [string, string][];
  reused?: boolean;
  builder_version?: string;
}

/**
 * Compact bundle metadata sidekick on each handle search match.
 * Matches `BundleSummary` in `src/server/routes/api/handles.rs`.
 */
export interface HandleSearchBundleSummary {
  completed_at: string | null;
  arrived_at: string | null;
  username: string | null;
  platform: string | null;
  app_version: string | null;
  llm_report_type: string | null;
  was_deduplicated: boolean | null;
}

/**
 * One match in `GET /api/handles/search` results.
 */
export interface HandleSearchMatch {
  bundle_uuid: string;
  kind: string;
  source_file: string | null;
  near_error: boolean;
  occurrence_count: number;
  bundle: HandleSearchBundleSummary;
}

/**
 * `GET /api/handles/search` response.
 */
export interface HandleSearchResponse {
  query_value: string;
  query_kind: string | null;
  matches: HandleSearchMatch[];
  truncated: boolean;
}

/**
 * Construct the search endpoint URL with query params. Useful
 * for a future TopNav `<Link>` that round-trips through the
 * URL bar so the search results page is bookmarkable.
 */
export function handleSearchUrl(
  value: string,
  kind?: string | null,
  limit?: number,
): string {
  const params = new URLSearchParams();
  params.set("value", value);
  if (kind) params.set("kind", kind);
  if (limit) params.set("limit", String(limit));
  return `/api/handles/search?${params.toString()}`;
}

// ─── Backend trace lookup (TRIAG-276 Deploy 2) ───────────────────────────────

/**
 * One backend `/production/ecs` record parsed for the dashboard. Every
 * field except `raw_message` is best-effort (the backend JSON schema isn't
 * pinned) — render the structured fields when present, fall back to
 * `raw_message` otherwise.
 */
export interface ParsedBackendRecord {
  timestamp: string;
  service: string;
  /** Log level (INFO entrypoint vs WARN/ERROR reason), if present. */
  level: string | null;
  code: string | null;
  err: string | null;
  user_id: string | null;
  source_permalink: string | null;
  raw_message: string;
}

/**
 * Structured outcome of a server-side backend-trace lookup (tagged enum
 * mirroring the Rust `BackendTraceOutcome`). A `miss` is citable evidence
 * (client-observed, server-unlogged) — NOT proof the backend was healthy.
 */
export type BackendTraceOutcome =
  | { kind: "hit"; records: ParsedBackendRecord[] }
  | { kind: "miss" }
  | { kind: "skipped"; reason: string };

/** `POST /api/bundles/:id/backend-trace-lookup` response. */
export interface BackendTraceLookupResponse {
  analysis_id: string;
  /** The 32-hex trace queried; `null` when the bundle has no trigger trace. */
  trace_id: string | null;
  /** True when served from the persisted cache (no fresh scan ran). */
  cached: boolean;
  looked_up_at: string | null;
  bytes_scanned: number;
  /** `null` when the bundle has no trigger trace. */
  outcome: BackendTraceOutcome | null;
}

/**
 * Resolve a bundle's trigger trace against the backend's production logs
 * (Option B — server-side inline lookup). Cached server-side; pass
 * `refresh=true` to bypass the cache (handles CloudWatch ingestion lag on
 * a freshly-arrived bundle whose first lookup missed).
 */
export async function lookupBackendTrace(
  uuid: string,
  refresh = false,
): Promise<BackendTraceLookupResponse> {
  const qs = refresh ? "?refresh=true" : "";
  return api<BackendTraceLookupResponse>(
    `/api/bundles/${uuid}/backend-trace-lookup${qs}`,
    { method: "POST" },
  );
}

// ─── Query factories ─────────────────────────────────────────────────────────

/**
 * TanStack Query `queryOptions` per endpoint. Usage:
 *
 * ```tsx
 * import { useQuery } from "@tanstack/react-query";
 * import { queries } from "@/lib/api";
 *
 * const { data, isLoading, error } = useQuery(queries.session());
 * ```
 *
 * Each factory is a function (not a constant) so callers can pass
 * parameters — e.g. `queries.analyses({ limit: 100, reportType: "crash" })`.
 */
export const queries = {
  /**
   * Who am I? Fetched once on app load by the `useSession` hook to
   * decide whether to show the login page or the authenticated
   * layout. Cached for 60 seconds so navigation doesn't refetch.
   */
  session: () =>
    queryOptions({
      queryKey: ["auth", "session"] as const,
      queryFn: () => api<Session>("/api/auth/me"),
      staleTime: 60_000,
      // 401 triggers the `window.location.href` redirect in `api()`
      // — no point retrying, the component tree is about to unmount.
      retry: false,
    }),

  /**
   * Recent completed analyses. Optional filter by `reportType`
   * (exact-match server-side) and `limit` (1..=200, default 50).
   *
   * The query key includes the filter params so switching filters
   * fetches a fresh result rather than reusing the cached all-types
   * list. `staleTime: 30_000` means toggling the filter within 30 s
   * hits the cache; after that, the next render re-fetches. Good
   * enough for a page that's live-ish but not real-time.
   */
  analyses: (
    params: {
      limit?: number;
      reportType?: string;
      /**
       * 2026-05-06 (Bug #3 fix): filter by the classifier's
       * verdict instead of the raw `report_type` bucket. One of
       * `"crash"`, `"user-shake"`, `"oops-anomaly"`,
       * `"oops-routine"`, `"not-a-bug"`. Preferred over
       * `reportType` because the classifier's 5-class output is
       * the operator's mental model (as rendered via the Rust
       * `ReportType::operator_label()` helper); the raw
       * `report_type` column on staging only has 3 distinct
       * values and can't distinguish oops from user-shake from
       * crash. Maps to the `llm_report_type` URL query param on
       * `GET /api/analyses`.
       */
      llmReportType?: string;
    } = {},
  ) =>
    queryOptions({
      queryKey: ["analyses", params] as const,
      queryFn: () => {
        const query = new URLSearchParams();
        if (params.limit !== undefined) {
          query.set("limit", String(params.limit));
        }
        if (params.reportType) {
          query.set("report_type", params.reportType);
        }
        if (params.llmReportType) {
          // 2026-05-06 (Bug #3): the SPA's filter dropdown now
          // selects classifier-verdict values (the 5 canonical
          // categories). Send the new-style filter param
          // instead of the legacy `report_type` when the
          // caller's selection is a classifier value. See
          // `Analyses.tsx`'s dropdown `onChange` handler for
          // the classifier-vs-legacy dispatch.
          query.set("llm_report_type", params.llmReportType);
        }
        const qs = query.toString();
        const path = qs ? `/api/analyses?${qs}` : "/api/analyses";
        return api<AnalysesResponse>(path);
      },
      staleTime: 30_000,
    }),

  /**
   * Time-bucketed counts of issues analyzed and issues received,
   * over the last 24 hours (granularity=hour) or 30 days
   * (granularity=day).
   *
   * The server returns only non-empty buckets; the Status page
   * fills in zero buckets client-side so the chart x-axis covers
   * the full window with a bar per bucket.
   *
   * Query key embeds the granularity so toggling between Hour and
   * Day hits the cache independently — switching back within 30 s
   * doesn't refetch.
   */
  stats: (windowHours: number) =>
    queryOptions({
      queryKey: ["stats", windowHours] as const,
      queryFn: () =>
        api<StatsResponse>(`/api/stats?window_hours=${windowHours}`),
      staleTime: 30_000,
    }),

  /**
   * Current `worker_config` row (Wave 9j `GET /api/config`).
   * Admin-gated — non-admin sessions get 403 here, and the fetch
   * wrapper surfaces that as a throw rather than a redirect
   * (unlike 401, which triggers a full-page login redirect).
   *
   * The `/admin` page uses `useQuery(queries.config())` plus a
   * `useMutation` wrapping `putConfig(...)` below. TanStack Query
   * caches for 60 s — long enough to avoid a second fetch during
   * a quick form edit, short enough that returning to the page
   * after a minute sees fresh state.
   *
   * Cached value is invalidated by the `/admin` page's mutation
   * `onSuccess` handler (`queryClient.invalidateQueries({queryKey:
   * ["config"]})`) so the post-save refetch serves the newly
   * bumped version + timestamp without another round-trip delay.
   */
  config: () =>
    queryOptions({
      queryKey: ["config"] as const,
      queryFn: () => api<ConfigResponse>("/api/config"),
      staleTime: 60_000,
    }),

  /**
   * Paginated bundle_lifecycle rows (Wave 9d). `searchString`
   * is an opaque query-string (typically produced by
   * `filterStateToSearchParams(...).toString()` from
   * `lib/filter-types.ts`); the factory appends `cursor` +
   * `limit` on top if present.
   *
   * Query key uses the raw strings so equivalent filter states
   * produce the same cache key. Changing any filter fetches
   * fresh data for that combination.
   */
  bundles: (
    params: {
      searchString?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ) =>
    queryOptions({
      queryKey: [
        "bundles",
        params.searchString ?? "",
        params.cursor ?? "",
        params.limit ?? 50,
      ] as const,
      queryFn: () => {
        const qs = new URLSearchParams(params.searchString ?? "");
        if (params.cursor) qs.set("cursor", params.cursor);
        if (params.limit !== undefined) {
          qs.set("limit", String(params.limit));
        }
        const suffix = qs.toString();
        return api<BundlesListResponse>(
          suffix ? `/api/bundles?${suffix}` : "/api/bundles",
        );
      },
      staleTime: 30_000,
      // Commit B follow-up fix (2026-05-01) — always refetch on
      // mount so back-navigation from a detail page picks up the
      // updated is_new_for_user flags the backend just wrote via
      // the fire-and-forget mark_bundle_seen_for_session side
      // effect. Without this, TanStack's default staleTime=30_000
      // window suppresses the refetch on back-nav within 30s of
      // the original list load + the operator sees stale blue
      // dots on bundles they just viewed. `"always"` trumps
      // staleTime for the mount-time fetch specifically;
      // background refetchInterval still respects staleTime so
      // we don't hammer Aurora on the common scan-and-drill flow.
      refetchOnMount: "always",
    }),

  /**
   * Per-route bundle counts over a rolling time window (Wave 12).
   * Powers the `RouteSummaryStrip` component above the filter bar
   * on `/bundles` — one row of chips showing failed / skipped /
   * deduped / analyzed / analyzing / queued / intake counts.
   *
   * 60 s `staleTime` coalesces across tabs + across the two
   * mount points this query can have (FilterBar summary strip +
   * any future admin-overview page) without hammering Aurora.
   * The `?window_hours=` default matches the backend default of
   * 1 hour when omitted.
   *
   * Query key includes `windowHours` so switching the window
   * (e.g. a future toggle between 1h / 24h) fetches a distinct
   * cache entry instead of overwriting the previous one.
   */
  bundlesRouteSummary: (
    params: {
      windowHours?: number;
      /**
       * Opaque filter query-string (Option B, 2026-05-04) — typically
       * produced by `filterStateToSearchParams(filter).toString()`
       * from `lib/filter-types.ts`, the same helper the paginated
       * `bundles` query above uses.
       *
       * When present, the server narrows the per-route counts to
       * rows matching the filter predicates (platform /
       * app_version / date range / username / search / etc.). The
       * `routes` parameter inside the searchString is explicitly
       * IGNORED by the server — the chip's own selection state is
       * not a filter on its own count (see the server-side
       * `RouteSummaryQuery` struct doc for the "chip inventory vs
       * checkbox selection" rationale).
       *
       * Missing / undefined → whole-DB counts (pre-Option-B
       * behavior preserved for unfiltered callers).
       *
       * Included in the query key so TanStack Query caches per
       * filter combination — toggling filters refetches fresh
       * counts, returning to an identical filter re-uses the
       * cache within the 60s `staleTime` window.
       */
      searchString?: string;
    } = {},
  ) =>
    queryOptions({
      queryKey: [
        "bundles",
        "route-summary",
        params.windowHours ?? 1,
        params.searchString ?? "",
      ] as const,
      queryFn: () => {
        // Start from the caller-provided filter string (if any) so
        // every filter param (platforms, app_versions, date range,
        // etc.) rides through to the server identically to how
        // `bundles` above forwards them. The `window_hours` param
        // overrides / adds to whatever was in the filter string —
        // although `filterStateToSearchParams` never writes
        // `window_hours`, we `.set` (not `.append`) defensively to
        // avoid duplicate keys if that ever changes.
        const qs = new URLSearchParams(params.searchString ?? "");
        if (params.windowHours !== undefined) {
          qs.set("window_hours", String(params.windowHours));
        }
        // The `routes` param inside the search string is passed
        // through but the server ignores it — the SPA deliberately
        // does NOT strip it here because (a) a future server change
        // that makes `routes` meaningful for route-summary would
        // then silently work, and (b) stripping would duplicate
        // parse logic that `filterStateToSearchParams` already owns.
        const suffix = qs.toString();
        return api<RouteSummaryResponse>(
          suffix
            ? `/api/bundles/route-summary?${suffix}`
            : "/api/bundles/route-summary",
        );
      },
      staleTime: 60_000,
    }),

  /**
   * Single bundle_lifecycle row (Wave 9b). Full row shape;
   * used by any page that needs fields not in the polling-
   * shape `BundleStatusResponse`.
   */
  bundle: (uuid: string) =>
    queryOptions({
      queryKey: ["bundle", uuid] as const,
      queryFn: () => api<BundleLifecycleRow>(`/api/bundles/${uuid}`),
      staleTime: 30_000,
    }),

  /**
   * Commit pre-flight review list (TRIAG-312). Cursor-paginated,
   * newest-first, optionally filtered by `status` / `repo`. Backs the
   * `/commits` page. Polled lightly while reviews are in flight.
   */
  commits: (
    params: {
      status?: string;
      repo?: string;
      recommendation?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ) =>
    queryOptions({
      queryKey: [
        "commits",
        params.status ?? "",
        params.repo ?? "",
        params.recommendation ?? "",
        params.cursor ?? "",
        params.limit ?? 0,
      ] as const,
      queryFn: () => {
        const qs = new URLSearchParams();
        if (params.status) qs.set("status", params.status);
        if (params.repo) qs.set("repo", params.repo);
        if (params.recommendation)
          qs.set("recommendation", params.recommendation);
        if (params.cursor) qs.set("cursor", params.cursor);
        if (params.limit !== undefined) qs.set("limit", String(params.limit));
        const suffix = qs.toString();
        return api<CommitsListResponse>(
          suffix ? `/api/commits?${suffix}` : "/api/commits",
        );
      },
      staleTime: 15_000,
    }),

  /** One commit review row (incl. the inline `result_json`). */
  commit: (sha: string) =>
    queryOptions({
      queryKey: ["commit", sha] as const,
      queryFn: () => api<CommitRow>(`/api/commits/${sha}`),
      staleTime: 15_000,
    }),

  /**
   * Compact polling shape for `/bundles/:uuid` (Wave 9c). The
   * `/bundles/:uuid` page uses `refetchInterval: 5000` to poll
   * while the pipeline is in progress, stopping the interval
   * once `timestamps.completed_at` becomes non-null.
   *
   * `staleTime: 0` means every mount or filter change
   * triggers a fresh fetch; the `refetchInterval` callsite is
   * what actually governs cadence.
   */
  bundleStatus: (uuid: string) =>
    queryOptions({
      queryKey: ["bundle-status", uuid] as const,
      queryFn: () => api<BundleStatusResponse>(`/api/bundles/${uuid}/status`),
      staleTime: 0,
    }),

  /**
   * Backend correlation handles for one bundle (Slice 1A part 2).
   * Returns 200 with `extracted=false` + empty graph for bundles
   * processed before migration 024 — the dashboard renders these
   * as `Skipped` state in `BackendHandlesCard`.
   *
   * 60s stale time: the handle graph is written once at intake
   * and never updated, so refetching is wasteful. The query is
   * invalidated explicitly by retry/restart-analysis flows that
   * re-run extraction.
   */
  bundleHandles: (uuid: string) =>
    queryOptions({
      queryKey: ["bundle-handles", uuid] as const,
      queryFn: () => api<BundleHandlesResponse>(`/api/bundles/${uuid}/handles`),
      staleTime: 60_000,
    }),

  /** How many other reports share each of a report's ids (Galley #34712). */
  bundleHandlesShared: (uuid: string) =>
    queryOptions({
      queryKey: ["bundle-handles-shared", uuid] as const,
      queryFn: () =>
        api<SharedHandlesResponse>(`/api/bundles/${uuid}/handles/shared`),
      staleTime: 60_000,
    }),

  /** The reports that carry one id, newest first (`/api/handles/search`). */
  handleReports: (value: string, kind: string, limit: number) =>
    queryOptions({
      queryKey: ["handle-reports", kind, value, limit] as const,
      queryFn: () => api<HandleSearchResponse>(handleSearchUrl(value, kind, limit)),
      staleTime: 60_000,
    }),

  /**
   * The backend dossier the investigator received (2026-09-24). 404 when
   * the bundle has none (analysed before the feature or while it was
   * switched off). `retry: false` so a 404 renders immediately instead of
   * after the default retry back-off.
   */
  bundleBackendDossier: (uuid: string) =>
    queryOptions({
      queryKey: ["bundle-backend-dossier", uuid] as const,
      queryFn: () => api<BackendDossier>(`/api/bundles/${uuid}/backend-dossier`),
      staleTime: 60_000,
      retry: false,
    }),

  /**
   * Analysis detail — flattened BundleLifecycleRow + parsed
   * `analysis.json` body + `duplicates_matched_count`
   * (Wave 9e). Used by the `/analyses/:uuid` page.
   *
   * 60s stale time because once an analysis is complete its
   * state rarely changes; the `regenerateUrls` mutation in
   * this file invalidates the cache explicitly to force a
   * refetch after a presigned-URL bundle refresh.
   */
  analysis: (uuid: string) =>
    queryOptions({
      queryKey: ["analysis", uuid] as const,
      queryFn: () => api<AnalysisDetailResponse>(`/api/analyses/${uuid}`),
      staleTime: 60_000,
    }),

  /**
   * TRIAG-421: the bundle's latest rush / analyse-duplicate request
   * (`GET /api/bundles/:uuid/rush-status`, `null` when there is none).
   * Polls every 15 s while the request is still active. Shared by the
   * bundle page and the report page (same key), so both pages move
   * together.
   */
  rushStatus: (uuid: string) =>
    queryOptions({
      queryKey: ["rush-status", uuid] as const,
      queryFn: () => fetchRushStatus(uuid),
      staleTime: 10_000,
      refetchInterval: (query) =>
        isActiveRushRequest(query.state.data) ? RUSH_STATUS_POLL_MS : false,
    }),

  /**
   * Canonical + matches + per-day histogram for one dedup
   * family (Wave 9g). Used by `/duplicates/:canonical_uuid`.
   *
   * Currently takes only the canonical UUID — the backend
   * also accepts `?date_from` / `?date_to` / `?methodology`
   * query params per Wave 9g but no page wires them yet. The
   * factory will grow a `params` arg when a consumer wants
   * them.
   *
   * 2026-05-06 Priority 9 (Phase 2 P2.2): pass `full: true` to
   * expand the `matches` vec to the full signature lineage —
   * every row whose `canonical_analysis_id` points at ANY
   * canonical sharing the requested canonical's `(hash_sha256,
   * dedup_scope_key)` signature. When the canonical has NULL
   * signature fields, the backend silently degrades to the
   * single-canonical path (response's `full_lineage` flag
   * surfaces which path ran).
   *
   * Query key includes `full` so cached single-canonical and
   * full-lineage responses don't collide on the same canonical.
   */
  duplicates: (canonicalUuid: string, options: { full?: boolean } = {}) =>
    queryOptions({
      queryKey: ["duplicates", canonicalUuid, options.full ?? false] as const,
      queryFn: () => {
        const qs = options.full === true ? "?full=true" : "";
        return api<DuplicatesResponse>(`/api/duplicates/${canonicalUuid}${qs}`);
      },
      staleTime: 30_000,
    }),

  /**
   * Top recurring dedup families in a time window (Wave 9i).
   * Used by `/trends`.
   *
   * `window` accepts `"Nd"` / `"24h"` / bare `"N"` (days).
   * Multi-select params are joined with commas server-side via
   * the backend's `split_csv` helper, so we do the same here.
   */
  trends: (
    params: {
      window?: string;
      limit?: number;
      platform?: string[];
      app_version?: string[];
      build_number?: string[];
      methodology?: string[];
      /**
       * TRIAG-26 (2026-05-15) — classifier-verdict filter
       * (`crash` / `user-shake` / `oops-anomaly` /
       * `oops-routine` / `not-a-bug`). Forwarded to the backend
       * as `?llm_report_types=` (plural; the backend accepts
       * both singular and plural via serde alias). Wires the
       * /trends "Report type" dropdown end-to-end — sibling fix
       * to TRIAG-25 (the /bundles version of the same bug).
       *
       * Empty array or undefined = no constraint.
       */
      report_types?: string[];
      /**
       * TRIAG-59 PHASE 4 (2026-05-21) — iOS feature_area filter
       * (`MagicEditor` / `Onboarding` / `BotDesigner` /
       * `MediaPreview` / `BotProfile`). Forwarded to the backend
       * as `?feature_areas=` (plural). Empty array or undefined
       * = no constraint.
       */
      feature_area?: string[];
      /**
       * TRIAG-140 (2026-05-28) — iOS picker category filter
       * (10-category Firebase-Remote-Config-mutable iOS picker
       * enum). Forwarded as `?ios_picker_categories=`.
       */
      ios_picker_category?: string[];
      /**
       * TRIAG-140 (2026-05-28) — Android picker category filter
       * (shorter Android picker enum). Forwarded as
       * `?android_picker_categories=`.
       */
      android_picker_category?: string[];
      /**
       * TRIAG-138 (2026-05-28) — error_class universal filter
       * (closed enum; v1 has only `network`). Forwarded as
       * `?error_classes=`. Validated against the closed enum
       * server-side; unknown values surface as 400
       * INVALID_FILTER.
       */
      error_class?: string[];
      /**
       * TRIAG-59 PHASE 4 (2026-05-21) — "vs prior window" toggle.
       * When `true`, the response includes per-family
       * `prior_window_total_count` populated from a second SQL
       * pass over the disjoint immediately-prior window of the
       * same length. Default `false` skips the second pass.
       */
      include_prior?: boolean;
      /**
       * Galley #30414 (PKG 8) — the "Hide moderation" toggle.
       * When `true`, the backend leaves out the families whose
       * defining error is a moderation refusal and reports how
       * many it left out (`moderation_hidden`). Omitted when off,
       * so the request is exactly today's.
       */
      hide_moderation?: boolean;
      /**
       * Priority 2 (2026-05-01) — optional sort column.
       * Omitted when the caller wants the default
       * (`total_count`) to preserve URL cleanliness on
       * default-sort pages. Wire form is snake_case per
       * `TrendSortKey`.
       */
      sort_by?: TrendSortKey;
      /**
       * Priority 2 (2026-05-01) — optional sort direction.
       * Omitted when the caller wants the default (`desc`).
       */
      sort_dir?: TrendSortDir;
    } = {},
  ) =>
    queryOptions({
      queryKey: ["trends", params] as const,
      queryFn: () => {
        const qs = new URLSearchParams();
        if (params.window) qs.set("window", params.window);
        if (params.limit !== undefined) {
          qs.set("limit", String(params.limit));
        }
        if (params.platform && params.platform.length > 0) {
          qs.set("platform", params.platform.join(","));
        }
        if (params.app_version && params.app_version.length > 0) {
          qs.set("app_version", params.app_version.join(","));
        }
        if (params.build_number && params.build_number.length > 0) {
          qs.set("build_number", params.build_number.join(","));
        }
        if (params.methodology && params.methodology.length > 0) {
          qs.set("methodology", params.methodology.join(","));
        }
        // TRIAG-26 (2026-05-15): forward the classifier-verdict
        // filter as `?llm_report_types=` so the backend routes to
        // `TrendFilters.llm_report_types` and applies the canonical-
        // scope COALESCE filter on `llm_report_type`. Same wire key
        // as /bundles (the post-TRIAG-25 canonical form).
        if (params.report_types && params.report_types.length > 0) {
          qs.set("llm_report_types", params.report_types.join(","));
        }
        // TRIAG-59 PHASE 4 (2026-05-21): forward feature_area
        // selection as the plural URL key the backend's serde
        // alias accepts.
        if (params.feature_area && params.feature_area.length > 0) {
          qs.set("feature_areas", params.feature_area.join(","));
        }
        // TRIAG-140 (2026-05-28): forward iOS / Android picker
        // selections as the plural URL keys the backend accepts.
        if (
          params.ios_picker_category &&
          params.ios_picker_category.length > 0
        ) {
          qs.set("ios_picker_categories", params.ios_picker_category.join(","));
        }
        if (
          params.android_picker_category &&
          params.android_picker_category.length > 0
        ) {
          qs.set(
            "android_picker_categories",
            params.android_picker_category.join(","),
          );
        }
        // TRIAG-138 (2026-05-28): forward error_class selection.
        if (params.error_class && params.error_class.length > 0) {
          qs.set("error_classes", params.error_class.join(","));
        }
        if (params.include_prior) {
          qs.set("include_prior", "true");
        }
        if (params.hide_moderation) {
          qs.set("hide_moderation", "true");
        }
        // Priority 2 (2026-05-01): include sort params only when
        // NOT default — matches the URL-cleanliness contract the
        // SPA uses for `?window=30d` (strip when equal to default)
        // and matches the backend's handler default behavior
        // (absent param → TrendSortConfig::default()).
        if (params.sort_by && params.sort_by !== DEFAULT_TREND_SORT_KEY) {
          qs.set("sort_by", params.sort_by);
        }
        if (params.sort_dir && params.sort_dir !== DEFAULT_TREND_SORT_DIR) {
          qs.set("sort_dir", params.sort_dir);
        }
        const suffix = qs.toString();
        return api<TrendReport>(
          suffix ? `/api/trends?${suffix}` : "/api/trends",
        );
      },
      staleTime: 60_000,
      // Commit B follow-up fix (2026-05-01) — always refetch on
      // mount so back-navigation from a /duplicates or /analyses
      // detail page picks up the updated signature-scoped
      // is_new_for_user flags the backend just wrote via the
      // fire-and-forget mark_bundle_seen_for_session side effect.
      // Same rationale as the `bundles` query above: TanStack's
      // default staleTime=60_000 window suppresses the mount-time
      // refetch on back-nav within 60s and the operator sees
      // stale blue dots on families they just engaged with.
      // Background refetch cadence + polling (none today on
      // /trends) still respects staleTime — only the explicit
      // mount-time fetch is forced.
      refetchOnMount: "always",
    }),

  /**
   * Feature B (2026-05-01) — the full LLM conversation artifact
   * (schema v1.0) for a completed analysis. Used by the
   * `/analyses/:uuid/conversation` page to render the interleaved
   * chronological stream of user / assistant / tool_use /
   * tool_result / thinking blocks.
   *
   * The backend endpoint `GET /api/analyses/:uuid/conversation`
   * reads `conversation.json` from the output S3 bucket and
   * streams it inline. 404 maps to a `fetch` rejection that the
   * consuming component renders as the "artifact not available
   * for this analysis" empty state per CONTRIBUTING rule 2
   * (distinguish narrowed-filter from data-empty — here:
   * analysis-in-progress vs predates-conversation-write-infra vs
   * S3-fetch-failed).
   *
   * Staleness is `Infinity` because once an analysis completes,
   * the conversation is immutable — no refetch needed in a user's
   * session. A page navigation reloads from cache; cache clears
   * on TanStack defaults when the query is garbage-collected.
   */
  analysisConversation: (uuid: string) =>
    queryOptions({
      queryKey: ["analysis-conversation", uuid] as const,
      queryFn: () =>
        api<AnalysisConversation>(`/api/analyses/${uuid}/conversation`),
      staleTime: Number.POSITIVE_INFINITY,
    }),

  /**
   * Distinct values for filter-bar dropdowns (Wave 9h). Used
   * by every filterable list page to populate
   * `FilterOptions` for the `FilterBar` component's dynamic
   * multi-select fields (app_version, build_number, username).
   *
   * `columns` restricts the response to specific columns —
   * pages typically pass only the columns they render (e.g.
   * `/bundles` asks for `["app_versions", "build_numbers",
   * "usernames"]`) to minimize both backend work and response
   * size. Omit to fetch every column.
   *
   * # Plural ↔ singular adapter (Bug 8 fix, 2026-05-01)
   *
   * The SPA convention is PLURAL (`app_versions`,
   * `build_numbers`, `usernames`) — matches the `FilterState`
   * selection fields, the `FilterOptions` interface, and the
   * `buildFilterOptions` plural-keyed consumers. The Rust
   * backend's `DistinctColumn::from_str` / `as_column()`
   * convention is SINGULAR (`app_version`, `build_number`,
   * `username`) — that's what the SQL column names are called
   * and what `DistinctColumn::Display` emits.
   *
   * Prior to this fix the SPA passed plural names in the
   * `?columns=` query string which the backend rejected with
   * 400 INVALID_COLUMN (29 rejections/hour in CloudWatch).
   * Even if the request had succeeded, the response body keys
   * are emitted singular (`values.app_version`) and the SPA's
   * `buildFilterOptions` reads plural (`v.app_versions`) — so
   * the dropdowns would have stayed empty regardless.
   *
   * This adapter is the single point that bridges the two
   * conventions: callers keep passing plural names (no churn
   * on the 4 call sites in Bundles.tsx + Trends.tsx +
   * FilterBar.tsx + filter-types.ts); the request URL carries
   * singular; the response keys get remapped back to plural
   * before returning. Every SPA consumer below this function
   * sees the plural shape it already expects, zero changes.
   *
   * The mapping is intentionally hardcoded (not derived from
   * the keys by lopping the trailing `s`) because English
   * pluralization isn't reversible in general — `classified_outcomes`
   * → `classified_outcome` is fine, but a future column like
   * `series` or `metadata` would break a naive singularize.
   * A hardcoded two-way table is explicit and scales cleanly
   * when new columns land (extend the table in lockstep with
   * the `DistinctColumn` enum in `src/worker/lifecycle/store.rs`).
   *
   * 2026-05-05 — the wrapper accepts two optional advanced
   * parameters:
   *
   * - `filterState`: the current page's filter state
   *   (plural shape, same as serialized to the URL via
   *   `filterStateToSearchParams`). When provided, the
   *   backend narrows each column's distinct-values query
   *   by all the OTHER filter fields (exclude-self
   *   semantics — a column's own filter selection never
   *   narrows its own dropdown, so operators can always ADD
   *   to their selection).
   * - `windowHours`: optional time window in hours. Callers
   *   with a natural window (`/trends?window=Nh|Nd`) pass
   *   this to align the dropdown with their UX context.
   *   `/bundles` (no window toggle) omits it for an
   *   unbounded dropdown.
   *
   * When either advanced parameter is provided, the
   * wrapper sends `?with_counts=true` + the filter params +
   * optional `window_hours`. The response is normalized from
   * either wire shape into `Record<string, FilterOption[]>`
   * so the UI doesn't branch on the discriminator.
   *
   * The query key includes a stable hash of filterState +
   * windowHours so TanStack's cache partitions per filter
   * combination — different filter states don't serve each
   * other's cached responses.
   *
   * TRIAG-32 (2026-05-15): renamed days → hours so the
   * /trends segmented control can pass hour-granular values
   * (1h / 3h / 6h / 12h plus the existing 24h+ options).
   */
  filterOptions: (
    columns?: string[],
    options?: {
      filterState?: Record<string, unknown>;
      windowHours?: number;
    },
  ) =>
    queryOptions({
      queryKey: [
        "filter-options",
        columns ?? "all",
        // Serialize the advanced options into the query key
        // so different filter states don't share a cache
        // entry. `JSON.stringify` with a stable key order is
        // sufficient for the object shapes we pass (flat
        // records of strings + arrays). TanStack uses
        // structural equality on the key, so identical inputs
        // hit the cache.
        options?.filterState
          ? JSON.stringify(
              Object.keys(options.filterState)
                .sort()
                .reduce<Record<string, unknown>>((acc, k) => {
                  acc[k] = (options.filterState as Record<string, unknown>)[k];
                  return acc;
                }, {}),
            )
          : "no-filter",
        options?.windowHours ?? "no-window",
      ] as const,
      queryFn: async (): Promise<NormalizedFilterOptionsResponse> => {
        // Plural (SPA) ↔ singular (backend) column-name mapping.
        // Keys mirror the `FilterOptions` interface in
        // `lib/filter-types.ts`; values mirror the Display
        // output of `DistinctColumn::as_column()` on the Rust
        // side. A column added on one side without the
        // matching entry here will silently drop off the
        // filter-bar dropdown — catch it by grepping for both
        // names whenever `FilterOptions` or `DistinctColumn`
        // gets a new member.
        const PLURAL_TO_SINGULAR: Record<string, string> = {
          platforms: "platform",
          app_versions: "app_version",
          build_numbers: "build_number",
          usernames: "username",
          sources: "source",
          report_types: "report_type",
          llm_report_types: "llm_report_type",
          priorities: "priority",
          dedup_methodologies: "dedup_methodology",
          classified_outcomes: "classified_outcome",
          feature_areas: "feature_area",
          // TRIAG-140 (2026-05-28): two new picker chips, both
          // backed by `report_problem_issue` server-side but
          // platform-scoped via distinct DistinctColumn variants
          // so they're queryable as separate column names from
          // the SPA's perspective.
          ios_picker_categories: "ios_picker_category",
          android_picker_categories: "android_picker_category",
        };

        // Reverse map so we can translate the backend's
        // singular response keys back into the SPA's plural
        // filter-option keys.
        const SINGULAR_TO_PLURAL: Record<string, string> = Object.fromEntries(
          Object.entries(PLURAL_TO_SINGULAR).map(([p, s]) => [s, p]),
        );

        // Translate request: plural → singular. Unknown entries
        // (caller passed a column name not in the table) fall
        // through unchanged so a typo surfaces as a 400 from
        // the backend rather than silently being dropped here.
        const pathColumns =
          columns && columns.length > 0
            ? columns.map((c) => PLURAL_TO_SINGULAR[c] ?? c)
            : undefined;
        const path =
          pathColumns && pathColumns.length > 0
            ? `/api/filter-options?columns=${pathColumns.join(",")}`
            : "/api/filter-options";

        // 2026-05-05: build the query string. Includes
        // `with_counts=true` + the filter state + optional
        // window_hours when the caller passes the advanced
        // options. Absent advanced options → the wrapper
        // still requests the new shape (`with_counts=true`)
        // with no filters so the SPA always sees the
        // normalized `NormalizedFilterOption[]` shape.
        //
        // Note: `columns` is already baked into `path` above
        // via the `?columns=` query string; we append the
        // rest of the params to `path` with `&` / `?` as
        // appropriate below.
        //
        // TRIAG-32 (2026-05-15): unit shifted days → hours.
        // The backend's `FilterOptionsQuery::window_hours`
        // field carries `#[serde(alias = "window_days")]` so
        // a stale SPA cache hit using the old param name
        // continues working, but new requests use the
        // canonical hour-granular name.
        const qs = new URLSearchParams();
        qs.set("with_counts", "true");
        if (options?.windowHours !== undefined) {
          qs.set("window_hours", String(options.windowHours));
        }
        // Filter state: the SPA's URL uses plural names
        // (`platforms=ios,android`). The server accepts both
        // singular and plural via Cat 21 serde aliases. Pass
        // through verbatim as plural — the backend handles
        // the mapping.
        if (options?.filterState) {
          for (const [key, value] of Object.entries(options.filterState)) {
            if (value === null || value === undefined) continue;
            if (Array.isArray(value)) {
              if (value.length === 0) continue;
              qs.set(key, value.map((v) => String(v)).join(","));
            } else if (typeof value === "string") {
              if (value.length === 0) continue;
              qs.set(key, value);
            } else {
              qs.set(key, String(value));
            }
          }
        }

        const pathWithQs = `${path}${path.includes("?") ? "&" : "?"}${qs.toString()}`;

        const resp = await api<FilterOptionsResponse>(pathWithQs);

        // Normalize the response to `NormalizedFilterOption[]`
        // regardless of which wire shape came back:
        //   - `values`: bare-string arrays → wrap as `{value}`
        //   - `values_with_counts`: tuples → pass through as
        //     `{value, count}`
        //
        // The handler populates ONE of the two maps per
        // response (discriminated via `#[serde(skip_serializing_if = "HashMap::is_empty")]`
        // on the Rust side) so we can safely read either.
        // Keys in the raw response are SINGULAR (backend's
        // `DistinctColumn::as_column()` output); we translate
        // to PLURAL (SPA's `FilterOptions` interface) below.
        const sourceMap: Record<string, NormalizedFilterOption[]> = {};
        if (resp.values_with_counts) {
          for (const [singular, entries] of Object.entries(
            resp.values_with_counts,
          )) {
            sourceMap[singular] = entries.map((e) => ({
              value: e.value,
              count: e.count,
            }));
          }
        } else if (resp.values) {
          for (const [singular, bareValues] of Object.entries(resp.values)) {
            sourceMap[singular] = bareValues.map((v) => ({ value: v }));
          }
        }

        // Translate singular → plural. Unknown keys pass
        // through unchanged so a new backend column surfaces
        // in the response rather than silently vanishing.
        const remapped: Record<string, NormalizedFilterOption[]> = {};
        for (const [k, v] of Object.entries(sourceMap)) {
          const pluralKey = SINGULAR_TO_PLURAL[k] ?? k;
          remapped[pluralKey] = v;
        }
        return {
          values: remapped,
        } satisfies NormalizedFilterOptionsResponse;
      },
      staleTime: 60_000,
    }),

  /**
   * TRIAG-307: feature_area distribution for the /bundles breakdown
   * strip — per-area counts PLUS the NULL ("no detected area") bucket
   * + honest total that filterOptions can't provide. `filterState` is
   * the same plural-keyed record `filterOptions` takes (the caller
   * maps report_types→llm_report_types); the backend applies
   * exclude-self on feature_area so the breakdown shows the full
   * distribution regardless of the active feature_area selection.
   */
  featureAreaBreakdown: (
    filterState?: Record<string, unknown>,
    windowHours?: number,
  ) =>
    queryOptions({
      queryKey: [
        "feature-area-breakdown",
        filterState
          ? JSON.stringify(
              Object.keys(filterState)
                .sort()
                .reduce<Record<string, unknown>>((acc, k) => {
                  acc[k] = (filterState as Record<string, unknown>)[k];
                  return acc;
                }, {}),
            )
          : "no-filter",
        windowHours ?? "no-window",
      ] as const,
      queryFn: async (): Promise<FeatureAreaBreakdownResponse> => {
        const qs = new URLSearchParams();
        if (windowHours !== undefined) {
          qs.set("window_hours", String(windowHours));
        }
        if (filterState) {
          for (const [key, value] of Object.entries(filterState)) {
            if (value === null || value === undefined) continue;
            if (Array.isArray(value)) {
              if (value.length === 0) continue;
              qs.set(key, value.map((v) => String(v)).join(","));
            } else if (typeof value === "string") {
              if (value.length === 0) continue;
              qs.set(key, value);
            } else {
              qs.set(key, String(value));
            }
          }
        }
        const q = qs.toString();
        return api<FeatureAreaBreakdownResponse>(
          `/api/feature-area-breakdown${q ? `?${q}` : ""}`,
        );
      },
      staleTime: 60_000,
    }),

  /**
   * TRIAG-308: `GET /api/activity/series` — a windowed, time-bucketed
   * count series for the Activity "over time" cards. `groupBy` splits
   * into sub-series (feature_area / feature_area_detected / platform /
   * llm_report_type / classified_outcome); omit for a single series.
   * `timeField` picks the bucketing timestamp (default arrived_at).
   * `failedOnly` restricts to rows with a non-NULL last_error.
   */
  activitySeries: (params: {
    windowHours: number;
    groupBy?: string;
    timeField?: "arrived_at" | "completed_at";
    failedOnly?: boolean;
  }) =>
    queryOptions({
      queryKey: [
        "activity-series",
        params.windowHours,
        params.groupBy ?? "none",
        params.timeField ?? "arrived_at",
        params.failedOnly ?? false,
      ] as const,
      queryFn: () => {
        const qs = new URLSearchParams();
        qs.set("window_hours", String(params.windowHours));
        if (params.groupBy) qs.set("group_by", params.groupBy);
        if (params.timeField) qs.set("time_field", params.timeField);
        if (params.failedOnly) qs.set("failed_only", "true");
        return api<ActivitySeriesResponse>(
          `/api/activity/series?${qs.toString()}`,
        );
      },
      staleTime: 30_000,
    }),

  /**
   * TRIAG-308: `GET /api/activity/latency` — p50/p95 of analysis
   * latency (completed_at − arrived_at, seconds) bucketed over the
   * window by completion time.
   */
  activityLatency: (windowHours: number) =>
    queryOptions({
      queryKey: ["activity-latency", windowHours] as const,
      queryFn: () =>
        api<ActivityLatencyResponse>(
          `/api/activity/latency?window_hours=${windowHours}`,
        ),
      staleTime: 30_000,
    }),

  /**
   * Galley #24242: `GET /api/activity/queue-wait` — the wait before
   * analysis starts, per priority, over the window, plus what waits now.
   */
  activityQueueWait: (windowHours: number) =>
    queryOptions({
      queryKey: ["activity-queue-wait", windowHours] as const,
      queryFn: () =>
        api<ActivityQueueWaitResponse>(
          `/api/activity/queue-wait?window_hours=${windowHours}`,
        ),
      staleTime: 30_000,
    }),

  // ── 2026-04-29 admin LLM observability endpoints ───────────────
  //
  // Four queries powering the /admin/llm dashboard page. Shapes
  // match src/server/routes/api/admin_llm.rs:
  //
  //   GET /api/admin/llm/token-rate       → TokenRateResponse
  //   GET /api/admin/llm/concurrency      → ConcurrencyResponse
  //   GET /api/admin/llm/concurrency/current → ConcurrencyCurrentResponse
  //   GET /api/admin/llm/totals           → TotalsResponse
  //
  // Every endpoint is admin-gated server-side via require_admin;
  // a non-admin session sees 403 ADMIN_REQUIRED. The /admin/llm
  // page catches that error and shows a "not authorized" panel
  // rather than treating it as a transport failure.

  /**
   * Time-bucketed token throughput over a window.
   *
   * `window` and `bucket` use the server-side grammar (`1h`,
   * `30m`, `7d`, etc.). Defaults match the server's defaults
   * (1h window, 1m bucket).
   *
   * Polled every 15s while the page is visible so new data
   * points slide in. StaleTime=10s matches the server's
   * `Cache-Control: max-age=10` on the response so overlapping
   * tabs share cached responses.
   */
  llmTokenRate: (window = "1h", bucket = "1m") =>
    queryOptions({
      queryKey: ["admin-llm-token-rate", window, bucket] as const,
      queryFn: () =>
        api<TokenRateResponse>(
          `/api/admin/llm/token-rate?window=${encodeURIComponent(
            window,
          )}&bucket=${encodeURIComponent(bucket)}`,
        ),
      staleTime: 10_000,
    }),

  /**
   * Time-bucketed historical concurrency derived from completed
   * LLM calls (`llm_token_usage.recorded_at` + `duration_ms`
   * overlap predicate). Also carries the live `current`
   * snapshot from the in-process atomic counter.
   */
  llmConcurrency: (window = "1h", bucket = "30s") =>
    queryOptions({
      queryKey: ["admin-llm-concurrency", window, bucket] as const,
      queryFn: () =>
        api<ConcurrencyResponse>(
          `/api/admin/llm/concurrency?window=${encodeURIComponent(
            window,
          )}&bucket=${encodeURIComponent(bucket)}`,
        ),
      staleTime: 10_000,
    }),

  /**
   * Live in-flight anthropic-call count. Zero-DB endpoint —
   * reads the process-wide AtomicU32 counter directly. Safe
   * to poll at 1-5s cadence for the live KPI card.
   */
  llmConcurrencyCurrent: () =>
    queryOptions({
      queryKey: ["admin-llm-concurrency-current"] as const,
      queryFn: () =>
        api<ConcurrencyCurrentResponse>("/api/admin/llm/concurrency/current"),
      staleTime: 2_000,
    }),

  /**
   * Whole-window aggregate totals. Drives the "big numbers"
   * card on /admin/llm (tokens, cost estimate, avg latency,
   * success/failure split).
   */
  llmTotals: (window = "1h") =>
    queryOptions({
      queryKey: ["admin-llm-totals", window] as const,
      queryFn: () =>
        api<TotalsResponse>(
          `/api/admin/llm/totals?window=${encodeURIComponent(window)}`,
        ),
      staleTime: 10_000,
    }),

  // ── 2026-04-29 TOP-OF-QUEUE #2 — admin Usersnap health ───────
  //
  // One query powering the /admin/usersnap dashboard page. Shape
  // matches `src/server/routes/api/admin_usersnap.rs::UsersnapHealthResponse`.
  //
  //   GET /api/admin/usersnap/health → UsersnapHealthResponse
  //
  // Admin-gated server-side via `require_admin`; a non-admin
  // session sees 403 `ADMIN_REQUIRED` which the page catches
  // and renders as a ForbiddenPanel instead of treating it as
  // a transport failure.
  //
  // Poll cadence 30s — the underlying data (configured project
  // list, boot-time credential presence, bundle aggregates)
  // changes on slash-command / admin-UI edits + per-bundle
  // arrivals; 30s is responsive enough for config edits to
  // surface without hammering the two-table aggregate query.
  adminUsersnapHealth: () =>
    queryOptions({
      queryKey: ["admin", "usersnap", "health"] as const,
      queryFn: () => api<UsersnapHealthResponse>("/api/admin/usersnap/health"),
      staleTime: 30_000,
    }),

  // ─── Priority 3 Phase 2 (2026-05-06) — admin page-views ─────────────────
  //
  // Four queries powering the /admin/page-views dashboard page.
  // Shapes match `src/server/routes/api/admin_page_views.rs` +
  // `src/worker/page_views/mod.rs` one-for-one; every endpoint is
  // admin-gated server-side via `require_admin` so non-admin
  // sessions see a 403 `ADMIN_REQUIRED` that the page catches and
  // renders as a `ForbiddenPanel` instead of failing silently.
  //
  //   GET /api/admin/page-views/summary?window=…   → PageViewSummary
  //   GET /api/admin/page-views/by-user?email=…    → PageViewRecord[]
  //   GET /api/admin/page-views/by-route?route=…   → PageViewRecord[]
  //   GET /api/admin/page-views/timeseries?window=…&granularity=… → TimeseriesBucket[]
  //
  // Poll cadence 30s — the underlying data moves on every
  // authenticated request (middleware records one row per GET),
  // so 30s balances freshness against query pressure on the
  // Postgres aggregate. Each query carries all its distinguishing
  // arguments in the queryKey so a filter change produces a
  // distinct cache entry instead of stomping on the prior result.
  adminPageViewsSummary: (
    window: AdminPageViewsWindow,
    includePolling: boolean,
  ) =>
    queryOptions({
      queryKey: [
        "admin",
        "page-views",
        "summary",
        window,
        includePolling,
      ] as const,
      queryFn: () =>
        api<PageViewSummary>(
          `/api/admin/page-views/summary?window=${encodeURIComponent(window)}${
            includePolling ? "&include_polling=true" : ""
          }`,
        ),
      staleTime: 30_000,
    }),
  adminPageViewsByUser: (
    email: string,
    window: AdminPageViewsWindow,
    limit: number,
    includePolling: boolean,
  ) =>
    queryOptions({
      queryKey: [
        "admin",
        "page-views",
        "by-user",
        email,
        window,
        limit,
        includePolling,
      ] as const,
      queryFn: () =>
        api<PageViewRecord[]>(
          `/api/admin/page-views/by-user?email=${encodeURIComponent(
            email,
          )}&window=${encodeURIComponent(window)}&limit=${limit}${
            includePolling ? "&include_polling=true" : ""
          }`,
        ),
      staleTime: 30_000,
      // Empty email = the page's "no user selected" default. Don't
      // fire the query in that state — the backend would reject
      // with 400 INVALID_EMAIL and the retry loop would hammer it.
      enabled: email.length > 0,
    }),
  adminPageViewsByRoute: (
    route: string,
    window: AdminPageViewsWindow,
    limit: number,
    includePolling: boolean,
  ) =>
    queryOptions({
      queryKey: [
        "admin",
        "page-views",
        "by-route",
        route,
        window,
        limit,
        includePolling,
      ] as const,
      queryFn: () =>
        api<PageViewRecord[]>(
          `/api/admin/page-views/by-route?route=${encodeURIComponent(
            route,
          )}&window=${encodeURIComponent(window)}&limit=${limit}${
            includePolling ? "&include_polling=true" : ""
          }`,
        ),
      staleTime: 30_000,
      enabled: route.length > 0,
    }),
  adminPageViewsTimeseries: (
    window: AdminPageViewsWindow,
    granularity: AdminPageViewsGranularity,
    includePolling: boolean,
  ) =>
    queryOptions({
      queryKey: [
        "admin",
        "page-views",
        "timeseries",
        window,
        granularity,
        includePolling,
      ] as const,
      queryFn: () =>
        api<TimeseriesBucket[]>(
          `/api/admin/page-views/timeseries?window=${encodeURIComponent(
            window,
          )}&granularity=${encodeURIComponent(granularity)}${
            includePolling ? "&include_polling=true" : ""
          }`,
        ),
      staleTime: 30_000,
    }),

  // ─── Commit B (2026-05-01) — user_seen queries ───────────────
  /**
   * Per-user "new" counts for the TopNav badge. Polled every
   * ~30s by the shared `useSeenCounts` hook while the dashboard
   * is mounted. Short 15s staleTime so a quick mark-all-read
   * + refetch cycle shows the post-action count promptly.
   *
   * `refetchOnWindowFocus: true` so operators who switch back
   * to the dashboard tab from Slack see fresh counts immediately
   * instead of stale ones from 25s ago.
   */
  seenCounts: () =>
    queryOptions({
      queryKey: ["user", "seen", "counts"] as const,
      queryFn: () => api<SeenCountsResponse>("/api/user/seen/counts"),
      staleTime: 15_000,
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
      // Commit B follow-up fix (2026-05-01) — always refetch on
      // mount so the TopNav badge picks up the updated count the
      // moment the operator navigates back from a detail page
      // (the detail-page GET fired a fire-and-forget mark-seen
      // write that shrinks the new-count by 1, but TanStack's
      // default staleTime=15_000 window would suppress the
      // refresh on back-nav within 15s). Matches the same
      // refetchOnMount: "always" policy the bundles + trends
      // list queries use after the same mark-seen-then-back-nav
      // cycle.
      refetchOnMount: "always",
      // Silently degrade to zero-badge on any error (503 when
      // DATABASE_URL unset in dev rigs, or 502 during a brief
      // Aurora outage). The badge just doesn't render rather
      // than surfacing red errors to the operator — per
      // CONTRIBUTING §"Fail-closed vs fail-open" category 2.
      retry: 0,
    }),
};

// ─── Commit B (2026-05-01) — user_seen mutation helpers ───────────────────

/**
 * POST `/api/user/seen/mark-all-read` — bulk-mark the given
 * analysis IDs as seen for the calling user on the given
 * surface. Called from the `MarkAllReadButton` after the
 * confirm-modal primary click.
 *
 * Throws `ApiError` on non-2xx — the caller (TanStack Query
 * `useMutation`) renders a toast on error.
 */
export async function markAllRead(
  req: MarkAllReadRequest,
): Promise<MarkAllReadResponse> {
  return api<MarkAllReadResponse>("/api/user/seen/mark-all-read", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

/**
 * POST `/api/user/seen/reset` — wipe all seen state for the
 * calling user. Called from `ResetSeenStateButton` after the
 * destructive-confirm modal's primary click.
 *
 * Empty body by design — the endpoint only knows about the
 * session user, there is no cross-user operation.
 */
export async function resetSeenState(): Promise<ResetSeenResponse> {
  return api<ResetSeenResponse>("/api/user/seen/reset", {
    method: "POST",
  });
}

// ─── Priority 3 Phase 2 — admin page-views wire types ─────────────────────
//
// Match `src/worker/page_views/mod.rs` one-for-one:
//
//   Rust `PageViewWindow`        ↔ `AdminPageViewsWindow`
//   Rust `PageViewGranularity`   ↔ `AdminPageViewsGranularity`
//   Rust `TopUserRow`            ↔ `TopUserRow`
//   Rust `TopRouteRow`           ↔ `TopRouteRow`
//   Rust `TimeseriesBucket`      ↔ `TimeseriesBucket`
//   Rust `PageViewSummary`       ↔ `PageViewSummary`
//   Rust `PageViewRecord`        ↔ `PageViewRecord`

/**
 * Rolling-window selector for the admin page-views aggregates.
 * Serialized as lowercase `"24h"` / `"7d"` / `"30d"` on the wire
 * matching `PageViewWindow::parse` in Rust.
 */
export type AdminPageViewsWindow = "24h" | "7d" | "30d";

/**
 * Bucket granularity for the timeseries endpoint. Serialized as
 * lowercase `"hour"` / `"day"` matching `PageViewGranularity::parse`
 * in Rust.
 */
export type AdminPageViewsGranularity = "hour" | "day";

/** Row in the "top users by activity" table — see `TopUserRow` in Rust. */
export interface TopUserRow {
  email: string;
  view_count: number;
}

/** Row in the "top pages by activity" table — see `TopRouteRow` in Rust. */
export interface TopRouteRow {
  route: string;
  view_count: number;
}

/** Row in the per-page-pattern leaderboard (TRIAG-181, 2026-05-23).
 *  Mirror of Rust `TopRoutePatternRow`. The `route_pattern` field
 *  carries the UUID-collapsed view (`/bundles/:uuid` rather than
 *  `/bundles/<uuid>`); the leaderboard collapses every per-bundle
 *  route into one row with summed view_count.
 */
export interface TopRoutePatternRow {
  route_pattern: string;
  view_count: number;
}

/**
 * One bucket of the `timeseries` endpoint's stacked-bar / line chart.
 *
 * `bucket_start` is ISO 8601 UTC. `count` is the total view count
 * in this bucket regardless of route or user — the timeseries
 * endpoint deliberately does NOT slice by route/user today (see
 * `src/worker/page_views/mod.rs` for the rationale).
 */
export interface TimeseriesBucket {
  bucket_start: string;
  count: number;
}

/** Response shape for `GET /api/admin/page-views/summary`. */
export interface PageViewSummary {
  window: AdminPageViewsWindow;
  total_views: number;
  unique_users: number;
  top_users: TopUserRow[];
  top_routes: TopRouteRow[];
  /** TRIAG-181 (2026-05-23): per-page-pattern leaderboard.
   *  Filters NULL `route_pattern` server-side so pre-TRIAG-195
   *  rows don't pollute the aggregate. Up to 10 rows. */
  top_route_patterns: TopRoutePatternRow[];
}

/**
 * One row of the `by-user` + `by-route` drill-down stream.
 * Matches `PageViewRecord` in Rust; `action_kind` is a loose string
 * (not a union) because the Rust side intentionally tolerates
 * unknown forward-compat variants via `ActionKind::Other`.
 */
export interface PageViewRecord {
  id: number;
  viewed_at: string;
  session_email: string;
  route: string;
  action_kind: string;
  analysis_id: string | null;
  referer: string | null;
  user_agent: string | null;
}

// ─── Admin LLM observability types ──────────────────────────────────────────
//
// Match `src/server/routes/api/admin_llm.rs` + the Rust trait
// read-side types in `src/worker/token_usage_store.rs`. Keep these
// in sync one-for-one; a backend schema drift that isn't mirrored
// here shows up as a silent undefined in chart rendering rather
// than a compile error.

/** One row of the token-rate time series — see `TokenRateBucket` in Rust. */
export interface TokenRateBucket {
  bucket_start: string; // ISO 8601 UTC
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  call_count: number;
}

/** One row of the concurrency time series — see `ConcurrencyBucket` in Rust. */
export interface ConcurrencyBucket {
  bucket_start: string; // ISO 8601 UTC
  concurrent_peak: number;
  concurrent_avg: number;
  contributing_calls: number;
}

/** Live in-flight counter snapshot — see `ConcurrencySnapshot` in Rust. */
export interface ConcurrencySnapshot {
  current: number;
  peak: number;
  started_total: number;
}

/** Totals card shape — see `TotalsResponse` in Rust. */
export interface TotalsResponse {
  window_start: string;
  window_end: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  call_count: number;
  successful_calls: number;
  failed_calls: number;
  total_duration_ms: number;
  avg_duration_ms: number;
  /**
   * Galley #14926: model requests in the window (a turn counts every request of its tool
   * loop; older rows count one). `call_count` stays the ledger-row count. Optional so a page
   * against a server without it keeps today's "Ledger rows" view.
   */
  request_count?: number;
  /** `total_duration_ms / request_count`, tool time between requests included. */
  avg_request_duration_ms?: number;
  /** When per-request counting began; sent only when the window starts before it. */
  request_counting_since?: string;
}

/** Token-rate endpoint response. */
export interface TokenRateResponse {
  window_start: string;
  window_end: string;
  bucket_seconds: number;
  buckets: TokenRateBucket[];
  totals: TotalsResponse;
}

/** Concurrency endpoint response. */
export interface ConcurrencyResponse {
  window_start: string;
  window_end: string;
  bucket_seconds: number;
  buckets: ConcurrencyBucket[];
  current: ConcurrencySnapshot | null;
}

/** Live-counter-only endpoint response. */
export interface ConcurrencyCurrentResponse {
  snapshot: ConcurrencySnapshot | null;
}

// ─── PUT /api/config mutation helper ────────────────────────────────────────

/**
 * Save a new `worker_config` payload with optimistic-lock
 * semantics. Wraps the Wave 9k `PUT /api/config` endpoint.
 *
 * Unlike `GET` queries, mutations don't fit neatly into
 * TanStack Query's `queryOptions` pattern — they're
 * imperatively invoked via `useMutation`. This helper is a bare
 * async function the `/admin` page wraps in a `useMutation({
 * mutationFn: (args) => putConfig(args.data, args.expectedVersion) })`.
 *
 * # Return-type design
 *
 * Returns `Promise<ConfigSaveResult>` — a discriminated union
 * matching the `SaveResult<T>` contract the `ConfigForm`
 * component consumes. 200 success becomes `{status: "saved",
 * ...}`, 409 conflict becomes `{status: "version_mismatch",
 * ...}`, everything else becomes `{status: "error", ...}`.
 *
 * Does NOT throw on 409 — the conflict is a legitimate
 * mutation outcome with structured data in the response body,
 * not an error. Throwing would force the `/admin` page to
 * special-case `ApiError` with `.status === 409` at every
 * call site; returning the discriminator keeps the downstream
 * code branchable on one field.
 *
 * Throws ONLY on 401 (which the `api()` wrapper handles via
 * full-page redirect anyway) and on genuine JavaScript errors
 * (network failure, unparseable body on a 2xx response).
 *
 * # Why not `useMutation` internally
 *
 * This file is framework-agnostic (no TanStack Query Provider
 * required to call `putConfig` — tests and future CLI-style
 * consumers can use it directly). The `/admin` page glues the
 * mutation into a `QueryClient`-aware hook via `useMutation`
 * at its own layer.
 */
export async function putConfig(
  data: Record<string, unknown>,
  expectedVersion: number,
): Promise<ConfigSaveResult> {
  // Use raw `fetch` here (not the `api()` wrapper) because:
  //   (a) 409 is a legitimate response whose body the `api()`
  //       wrapper would throw as an `ApiError` on us,
  //   (b) we need to discriminate on `res.status` + response
  //       body `outcome` field together, which the wrapper
  //       doesn't expose.
  //
  // 401 still triggers the full-page redirect — we replicate
  // that branch locally so the behavior matches the rest of the
  // app.
  const res = await fetch("/api/config", {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data, expected_version: expectedVersion }),
  });

  if (res.status === 401) {
    // Full-page redirect — match `api()`'s behavior so an
    // expired session during a save acts identically to an
    // expired session during a read.
    window.location.href = "/auth/google/login";
    return {
      status: "error",
      message: "Session expired",
      code: "AUTH_REQUIRED",
    };
  }

  // 200 and 409 both have structured success-shape bodies with
  // an `outcome` discriminator. Parse-then-switch covers both.
  if (res.status === 200 || res.status === 409) {
    const body = (await res.json().catch(() => null)) as
      | ConfigSaveSuccessResponse
      | ConfigSaveConflictResponse
      | null;
    if (!body) {
      return {
        status: "error",
        message: "Invalid response body from PUT /api/config",
        code: "BODY_PARSE",
      };
    }
    if (body.outcome === "saved") {
      return {
        status: "saved",
        new_version: body.version,
        updated_at: body.updated_at,
      };
    }
    if (body.outcome === "version_mismatch") {
      return {
        status: "version_mismatch",
        current_version: body.current_version,
      };
    }
    // Unknown outcome value — shouldn't happen given the
    // server's discriminator contract, but a schema drift
    // between server + client wouldn't crash the UI.
    return {
      status: "error",
      message: "Unknown response outcome from PUT /api/config",
      code: "UNKNOWN_OUTCOME",
    };
  }

  // Everything else — 400 / 403 / 502 / 503 — is an `ApiError`
  // envelope. Parse defensively; fall back to status text if
  // the body isn't parseable.
  const errBody = (await res.json().catch(() => ({
    error: res.statusText,
    code: "UNKNOWN",
  }))) as { error?: string; code?: string };
  return {
    status: "error",
    // The same fallback as `api()`'s (Galley #17894).
    message: errBody.error || res.statusText || `HTTP ${res.status}`,
    code: errBody.code ?? "UNKNOWN",
  };
}

// ─── POST /api/analyses/:uuid/regenerate-urls mutation (Wave 9f) ────────────

/**
 * Mint a fresh 7-day presigned URL bundle for every artifact
 * attached to the analysis and persist them to the
 * `bundle_lifecycle` row. Wraps the Wave 9f POST endpoint.
 *
 * Returns the updated `AnalysisDetailResponse` shape (same as
 * `queries.analysis(uuid)`) so the caller can directly replace
 * the cached value via `queryClient.setQueryData` — no
 * round-trip refetch needed after a successful regeneration.
 *
 * Unlike `putConfig`, this mutation has no 409 conflict branch —
 * URL regeneration is idempotent and doesn't race on a version
 * counter. Errors flow through the standard `api()` wrapper's
 * `ApiError`-throwing behavior (the `/analyses/:uuid` page
 * catches via `useMutation`'s error state).
 */
export async function regenerateUrls(
  uuid: string,
): Promise<AnalysisDetailResponse> {
  return api<AnalysisDetailResponse>(`/api/analyses/${uuid}/regenerate-urls`, {
    method: "POST",
  });
}

// ─── POST /api/bundles/:uuid/purge mutation (Cat 2, 2026-05-01) ─────────────

/**
 * Outcome of a `purgeBundle` call that the UI needs to
 * discriminate on. The backend returns 204 No Content for both
 * Deleted + AlreadyGone (HTTP DELETE is idempotent per RFC 9110
 * §9.3.5), so the SPA sees a single success shape. The 409
 * `NOT_STUCK` case is surfaced as a thrown `ApiError` with
 * `code === "NOT_STUCK"` so the caller can distinguish "row is
 * live, don't purge" from a genuine backend error.
 */
export type PurgeOutcome =
  | { kind: "success" }
  | { kind: "not_stuck"; message: string };

/**
 * Admin-gated "Reset and retry" affordance for stuck bundles.
 * Wraps the Cat 2 `POST /api/bundles/:uuid/purge` endpoint.
 *
 * Wrote against the 2026-04-28 `f63b8221` incident where a
 * worker SIGTERM mid-analysis left a bundle_lifecycle row with
 * partial state + SQS redelivery hit the idempotency gate on
 * `record_retrieved`. The button on BundleDetail is only
 * visible when `pipelineStages::isStuck(row)` returns true AND
 * the session is admin; the server-side guard runs the same
 * predicate (`is_row_stuck` in `src/server/routes/api/bundles.rs`)
 * so a non-stuck row returns 409 even if the SPA's guard was
 * bypassed.
 *
 * # Return-type design
 *
 * - `{ kind: "success" }` → row was deleted OR was already gone
 *   (idempotent). Caller invalidates the bundle-status cache +
 *   shows a success toast.
 * - `{ kind: "not_stuck", message }` → server-side guard rejected
 *   because the row isn't stuck. Caller shows the `message` in a
 *   modal error state without retrying. This case is a legitimate
 *   race (operator clicked while an analysis was in-flight +
 *   about to complete) — not a bug, just a "wait and retry"
 *   signal.
 *
 * Any other non-2xx throws via the `api()` wrapper. The caller
 * wraps this mutation in `useMutation` and handles the thrown
 * case in `onError`.
 */
export async function purgeBundle(uuid: string): Promise<PurgeOutcome> {
  const res = await fetch(`/api/bundles/${uuid}/purge`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
  });

  if (res.status === 204) {
    return { kind: "success" };
  }
  if (res.status === 409) {
    // Parse the body for the NOT_STUCK message. Fail-open to a
    // generic message if the body isn't parseable — the 409
    // status alone is the load-bearing signal.
    const body = await res.json().catch(() => null);
    const message =
      (body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : null) ??
      "Bundle does not appear stuck — refusing to purge a potentially live analysis";
    return { kind: "not_stuck", message };
  }
  if (res.status === 401) {
    // Match the `api()` wrapper's 401-redirect-to-login behavior.
    window.location.href = "/login";
    throw new ApiError("not authenticated", 401, "AUTH_REQUIRED");
  }
  const body = await res.json().catch(() => null);
  const code =
    (body && typeof body === "object" && "code" in body
      ? String((body as { code: unknown }).code)
      : null) ?? "UNKNOWN";
  const message =
    (body && typeof body === "object" && "error" in body
      ? String((body as { error: unknown }).error)
      : null) ?? `Purge failed with status ${res.status}`;
  throw new ApiError(message, res.status, code);
}

// ─── Slice B: Kill Analysis (2026-05-02) ────────────────────────────────────

/**
 * Subset of the in-flight registry's per-entry snapshot, as
 * surfaced on the bundle-status response + the kill response.
 * Mirrors the Rust `InFlightInfo` wire type in
 * `src/server/routes/api/bundles.rs`.
 *
 * Fields:
 *   - `worker_id` — `{hostname}:{pid}:{start_unix_ts}` identity
 *     of the worker process holding the analysis. Surfaced on
 *     BundleDetail for operator visibility (e.g. "held by
 *     ip-172-31-6-118:1718524").
 *   - `picked_up_at` — wall-clock UTC ISO 8601 string when the
 *     worker task registered itself.
 *   - `age_secs` — monotonic seconds since picked_up_at. The
 *     SPA prefers this over `Date.now() - picked_up_at` because
 *     the server's monotonic clock won't drift relative to the
 *     registry entry's `Instant`.
 *   - `phase_label` — current phase label (e.g. `phase2b-synthesize`)
 *     or `null` between phases. Updated in real-time via the
 *     pipeline's `set_phase!` macro; dashboard freshness is
 *     bounded by the 5s poll cadence, not by the analysis_phases
 *     DB write lag.
 *   - `kill_requested` — true when the kill endpoint has been
 *     invoked but the worker task hasn't drained + exited yet.
 *     The SPA renders this as "Killing…" until the row's
 *     last_error lands in the DB.
 */
export type InFlightInfo = {
  worker_id: string;
  picked_up_at: string;
  age_secs: number;
  phase_label: string | null;
  kill_requested: boolean;
};

/**
 * Outcome of a `killAnalysis` call, discriminated so the UI can
 * surface "Kill signal sent" vs "Kill already in flight" vs
 * server-side rejection shapes (404 NOT_HELD / 503 REGISTRY_NOT_CONFIGURED).
 *
 * 202 Accepted → `{ kind: "requested" }` — fresh kill signal
 * dispatched. The registry entry's watch channel now carries
 * `true`; the worker's tokio::select! kill arm will fire at
 * the next phase boundary (typically sub-second, up to ~2 min
 * if mid-LLM-stream).
 *
 * 200 OK → `{ kind: "already_requested" }` — this analysis
 * had a prior kill click; the signal is already in flight and
 * the worker is draining.
 *
 * Any 4xx/5xx non-200/202 throws `ApiError` via the caller.
 */
export type KillAnalysisOutcome =
  | { kind: "requested"; response: KillAnalysisResponseBody }
  | { kind: "already_requested"; response: KillAnalysisResponseBody };

/**
 * Body returned on both 202 Accepted and 200 OK paths. Identical
 * shape in both cases so the SPA can render a consistent
 * "kill in progress — held by X, on phase Y" message. The only
 * difference between the two status codes is `fresh_signal`
 * (true on 202, false on 200).
 */
export type KillAnalysisResponseBody = {
  analysis_id: string;
  kill_requested_at: string;
  fresh_signal: boolean;
  worker_id: string;
  phase_label: string | null;
  expected_latency_note: string;
};

/**
 * Admin-gated "Kill analysis" affordance. Wraps the Slice B
 * `POST /api/bundles/:uuid/kill` endpoint.
 *
 * # Semantics
 *
 * Signals the worker task holding the analysis to abort via the
 * in-memory registry's `watch<bool>` kill channel. The worker's
 * tokio::select! kill arm wins the race, drops the in-flight
 * `run_analysis` future (cooperative cancellation), and writes
 * `last_error = "Killed by operator request (no retry): …"` to
 * the bundle_lifecycle row. SQS redelivery of the message is
 * naturally suppressed by the Q7 terminal-state skip at the top
 * of `run_job_inner`.
 *
 * Latency: sub-second in practice (Bedrock streams + Postgres
 * writes all yield at many await points per second). Worst case
 * ~2 min if the worker was deep in a mid-Bedrock-stream recv()
 * awaiting the 150s per-chunk watchdog.
 *
 * # Button visibility contract
 *
 * The Kill button should only appear when the bundle-status
 * response includes a non-null `in_flight` field. If the SPA's
 * Kill button fires against an analysis whose registry entry
 * already dropped (worker exited naturally between poll + click),
 * the server returns 404 NOT_HELD — surfaced via the thrown
 * ApiError with `code === "NOT_HELD"`. The UI should re-fetch
 * status on that error, which will show the fresh terminal state.
 */
export async function killAnalysis(uuid: string): Promise<KillAnalysisOutcome> {
  const res = await fetch(`/api/bundles/${uuid}/kill`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
  });

  if (res.status === 202 || res.status === 200) {
    const body = (await res.json()) as KillAnalysisResponseBody;
    return {
      kind: res.status === 202 ? "requested" : "already_requested",
      response: body,
    };
  }
  if (res.status === 401) {
    window.location.href = "/login";
    throw new ApiError("not authenticated", 401, "AUTH_REQUIRED");
  }
  const body = await res.json().catch(() => null);
  const code =
    (body && typeof body === "object" && "code" in body
      ? String((body as { code: unknown }).code)
      : null) ?? "UNKNOWN";
  const message =
    (body && typeof body === "object" && "error" in body
      ? String((body as { error: unknown }).error)
      : null) ?? `Kill failed with status ${res.status}`;
  throw new ApiError(message, res.status, code);
}

// ─── Slice 3: Restart Analysis (2026-05-02) ─────────────────────────────────

/**
 * Outcome of a `restartAnalysis` call that the UI needs to
 * discriminate on. Parallel to [`PurgeOutcome`] — the handler
 * returns 204 No Content on the happy path (row deleted +
 * re-enqueued to intake queue); the 409 `NOT_RESTARTABLE` case
 * is surfaced as a structured message so the modal can explain
 * WHY the restart was rejected (typically: row is actively
 * analyzing within the 2h healthy window).
 *
 * # Additional 409 variant — `MISSING_S3_REFERENCE`
 *
 * The restart handler rejects rows with empty `s3_bucket` /
 * `s3_key` (ghost row shape). Caller sees this as a 409 with
 * `code === "MISSING_S3_REFERENCE"`; surfaced to the operator
 * as "cannot re-enqueue without source coordinates — investigate
 * via psql" rather than a mute silent failure.
 *
 * # Additional 409 variant — `BUNDLE_IN_FLIGHT` (TRIAG-192, 2026-05-22)
 *
 * The restart handler now also refuses to purge a row whose
 * analysis pipeline is currently in flight (`analysis_started_at
 * IS NOT NULL AND analysis_finished_at IS NULL AND
 * completed_at IS NULL AND last_error IS NULL`). Purging would
 * CASCADE-delete the in-flight pipeline's `tool_calls` +
 * `analysis_phases` children and generate dozens of FK-violation
 * errors for the next ~5 minutes. The dashboard surfaces this as
 * "this bundle is currently analyzing — wait or kill first."
 */
export type RestartAnalysisOutcome =
  | { kind: "success" }
  | { kind: "not_restartable"; message: string }
  | { kind: "missing_s3_reference"; message: string }
  | { kind: "in_flight"; message: string };

/**
 * Admin-gated "Restart Analysis" affordance. Wraps the Slice 3
 * `POST /api/bundles/:uuid/restart-analysis` endpoint.
 *
 * # Semantics
 *
 * The server-side handler does three things, in order:
 *
 *   1. Validates the row is restartable (completed, failed, or
 *      stuck — but NOT actively-analyzing within the 2h healthy
 *      window).
 *   2. DELETEs the `bundle_lifecycle` row + CASCADE children
 *      (`analysis_phases`, `tool_calls`).
 *   3. Re-enqueues an EventBridge-shaped SQS message to the
 *      INTAKE queue (NOT directly to a priority queue — the
 *      intake classifier owns routing, and a restart re-runs
 *      classification + dedup + priority gate against current
 *      state).
 *
 * The caller wraps this mutation in `useMutation` and handles
 * cache invalidation (bundle-status + bundles list +
 * route-summary) on success. The 404 `BUNDLE_NOT_FOUND` case is
 * thrown as `ApiError` — if the row is already gone, restart
 * can't construct the re-enqueue message (we need the S3
 * coordinates), so surfacing the 404 is honest.
 *
 * # Button visibility contract
 *
 * The SPA's `isRestartable(row)` predicate MUST agree with the
 * server's `is_row_restartable(&row)` in Rust. A non-restartable
 * row that slips past the client predicate still fails at the
 * 409 server guard — the error-state UI surfaces the server's
 * message — but a correctly-wired button shouldn't be rendered
 * at all for those rows.
 */
export async function restartAnalysis(
  uuid: string,
): Promise<RestartAnalysisOutcome> {
  const res = await fetch(`/api/bundles/${uuid}/restart-analysis`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
  });

  if (res.status === 204) {
    return { kind: "success" };
  }
  if (res.status === 409) {
    // Parse the body to distinguish NOT_RESTARTABLE from
    // MISSING_S3_REFERENCE. Fail-open to a generic message if
    // the body is unparseable — the 409 alone signals "your
    // click was rejected, not retried."
    const body = await res.json().catch(() => null);
    const code =
      body && typeof body === "object" && "code" in body
        ? String((body as { code: unknown }).code)
        : null;
    const message =
      (body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : null) ?? "Restart rejected by server";
    if (code === "MISSING_S3_REFERENCE") {
      return { kind: "missing_s3_reference", message };
    }
    if (code === "BUNDLE_IN_FLIGHT") {
      // TRIAG-192 (2026-05-22): the server detected the
      // bundle's analysis pipeline is currently in flight
      // and refused to purge to avoid the FK-violation
      // pathology. The SPA surfaces this with a distinct
      // message + a hint to use [Kill] first.
      return { kind: "in_flight", message };
    }
    return { kind: "not_restartable", message };
  }
  if (res.status === 401) {
    window.location.href = "/login";
    throw new ApiError("not authenticated", 401, "AUTH_REQUIRED");
  }
  const body = await res.json().catch(() => null);
  const code =
    (body && typeof body === "object" && "code" in body
      ? String((body as { code: unknown }).code)
      : null) ?? "UNKNOWN";
  const message =
    (body && typeof body === "object" && "error" in body
      ? String((body as { error: unknown }).error)
      : null) ?? `Restart failed with status ${res.status}`;
  throw new ApiError(message, res.status, code);
}

// ═══════════════════════════════════════════════════════════════════════════
// Resend to Slack (migration 022, 2026-05-02)
// ═══════════════════════════════════════════════════════════════════════════
//
// Admin-only. Operator clicks "Resend to Slack" on BundleDetail
// when they see the amber "Slack: pending resend" chip. The
// handler fires notify_complete + stamps slack_notify_posted_at.
// Shares the per-bundle code path with the boot sweeper in
// src/worker/notify_backlog_sweeper.rs::sweep_one_bundle.

/**
 * Wire shape of the `ResendSlackResponse` Rust struct returned by
 * `POST /api/bundles/:aid/resend-slack`. Mirrors
 * `src/server/routes/api/bundles.rs::ResendSlackResponse`.
 */
export interface ResendSlackResponseBody {
  analysis_id: string;
  outcome: "posted" | "already_posted";
  resent_at: string;
}

/**
 * Operator-facing discriminated union returned by the
 * [`resendSlack`] client helper. Maps the server's 5-branch HTTP
 * taxonomy to a narrow, renderable outcome type:
 *
 * - `posted` — Slack message was sent AND slack_notify_posted_at
 *   was stamped. Toast: "Slack notification sent."
 * - `already_posted` — row was already marked (CAS lost, or
 *   short-circuited via the pre-check). Toast: "Already posted."
 * - `error` — any 4xx/5xx response; `code` carries the
 *   server-supplied error code for operator-readable messaging.
 */
export type ResendSlackOutcome =
  | { kind: "posted"; resentAt: string }
  | { kind: "already_posted"; resentAt: string }
  | { kind: "error"; code: string; message: string; status: number };

/**
 * Fire the admin resend-slack endpoint for a specific bundle.
 *
 * Does NOT throw — every failure path maps to a `kind: "error"`
 * result so the caller can render a toast without try/catch
 * boilerplate. Network-level failures (fetch throws) are the only
 * exception and propagate as thrown `TypeError` / `DOMException`
 * per the native `fetch` contract.
 */
export async function resendSlack(uuid: string): Promise<ResendSlackOutcome> {
  const res = await fetch(`/api/bundles/${uuid}/resend-slack`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
  });

  if (res.status === 200) {
    const body = (await res.json()) as ResendSlackResponseBody;
    if (body.outcome === "posted") {
      return { kind: "posted", resentAt: body.resent_at };
    }
    return { kind: "already_posted", resentAt: body.resent_at };
  }

  // Error path — map server-supplied error code + message.
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // Body wasn't JSON (e.g., network-level 502 HTML page from
    // upstream proxy). Fall through to the generic message below.
  }
  const code =
    (body && typeof body === "object" && "code" in body
      ? String((body as { code: unknown }).code)
      : null) ?? "UNKNOWN";
  const message =
    (body && typeof body === "object" && "error" in body
      ? String((body as { error: unknown }).error)
      : null) ?? `Resend failed with status ${res.status}`;
  return { kind: "error", code, message, status: res.status };
}

// ─── Rush + analyse-duplicate API client (TRIAG-422 / TRIAG-421) ─────────────
//
// Wire types + helpers for the dashboard Run-analysis button (gated
// bundles) and the family-page Analyze button (duplicates). Mirrors
// `RushBundleOutcome` + `RushBundleResponse` in
// `triage/src/server/routes/api/bundles.rs` (serialize-shape tests pin
// the field names server-side).

/** Server-side `RushBundleResponse` wire shape (internally tagged). */
interface RushBundleResponseBody {
  analysis_id: string;
  dispatched_at: string;
  outcome:
    | "queued"
    | "already_complete"
    | "already_in_progress"
    | "is_duplicate"
    | "not_a_duplicate"
    | "previously_failed"
    | "not_ingested"
    | "source_missing"
    | "at_capacity";
  /** `queued` only. Always `0`. */
  position?: number;
  /** `queued` only: `"worker_paused"` or `"busy"`; absent = starts now. */
  note?: "worker_paused" | "busy";
  /** `queued` only: the platform is unknown → no source code. */
  no_source_code?: boolean;
  /** `is_duplicate` only. */
  canonical_analysis_id?: string;
  /** `previously_failed` only. */
  reason?: string;
  /** `at_capacity` only. */
  limit?: number;
}

/**
 * Renderable outcome of a rush / analyse-duplicate request. Success
 * variants mirror the server enum; `error` catches every 4xx/5xx with
 * the server-supplied `code`.
 */
export type RushBundleOutcome =
  | {
      kind: "queued";
      position: number;
      dispatchedAt: string;
      note: "worker_paused" | "busy" | null;
      noSourceCode: boolean;
    }
  | { kind: "already_complete"; dispatchedAt: string }
  | { kind: "already_in_progress"; dispatchedAt: string }
  | { kind: "is_duplicate"; dispatchedAt: string; canonicalAnalysisId: string | null }
  | { kind: "not_a_duplicate"; dispatchedAt: string }
  | { kind: "previously_failed"; dispatchedAt: string; reason: string }
  | { kind: "not_ingested"; dispatchedAt: string }
  | { kind: "source_missing"; dispatchedAt: string }
  | { kind: "at_capacity"; dispatchedAt: string; limit: number }
  | { kind: "error"; code: string; message: string; status: number };

/** Map a 200 body to the renderable outcome. Exported for tests. */
export function rushOutcomeFromBody(body: RushBundleResponseBody): RushBundleOutcome {
  const dispatchedAt = body.dispatched_at;
  switch (body.outcome) {
    case "queued":
      return {
        kind: "queued",
        position: body.position ?? 0,
        dispatchedAt,
        note: body.note ?? null,
        noSourceCode: body.no_source_code === true,
      };
    case "already_complete":
      return { kind: "already_complete", dispatchedAt };
    case "already_in_progress":
      return { kind: "already_in_progress", dispatchedAt };
    case "is_duplicate":
      return {
        kind: "is_duplicate",
        dispatchedAt,
        canonicalAnalysisId: body.canonical_analysis_id ?? null,
      };
    case "not_a_duplicate":
      return { kind: "not_a_duplicate", dispatchedAt };
    case "previously_failed":
      return { kind: "previously_failed", dispatchedAt, reason: body.reason ?? "" };
    case "not_ingested":
      return { kind: "not_ingested", dispatchedAt };
    case "source_missing":
      return { kind: "source_missing", dispatchedAt };
    case "at_capacity":
      return { kind: "at_capacity", dispatchedAt, limit: body.limit ?? 0 };
    default:
      // A newer server answer this SPA build doesn't know: say so
      // instead of guessing (the old fallback claimed "in progress").
      return {
        kind: "error",
        code: "UNKNOWN_OUTCOME",
        message: `Unrecognised answer from the server: ${String(body.outcome)}`,
        status: 200,
      };
  }
}

async function postRushLike(url: string, fallback: string): Promise<RushBundleOutcome> {
  const res = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
  });
  if (res.status === 200) {
    return rushOutcomeFromBody((await res.json()) as RushBundleResponseBody);
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // Body wasn't JSON (e.g. a proxy's HTML error page).
  }
  const code =
    (body && typeof body === "object" && "code" in body
      ? String((body as { code: unknown }).code)
      : null) ?? "UNKNOWN";
  const message =
    (body && typeof body === "object" && "error" in body
      ? String((body as { error: unknown }).error)
      : null) ?? `${fallback} (status ${res.status})`;
  return { kind: "error", code, message, status: res.status };
}

/**
 * `POST /api/bundles/:uuid/rush` — the Run-analysis button. Never
 * throws for HTTP errors (they map to `kind: "error"`); network
 * failures propagate as the native `fetch` rejection.
 */
export async function rushBundle(uuid: string): Promise<RushBundleOutcome> {
  return postRushLike(`/api/bundles/${uuid}/rush`, "Rush failed");
}

/** Where an analyse-duplicate click came from (audit). */
export type AnalyzeDuplicateOrigin = "family-page" | "evidence-panel" | "bundle-page";

/**
 * `POST /api/bundles/:uuid/analyze-duplicate` (TRIAG-421) — run a full,
 * independent analysis of a deduplicated bundle, or retry a failed one.
 * Same error contract as {@link rushBundle}.
 */
export async function analyzeDuplicate(
  uuid: string,
  origin: AnalyzeDuplicateOrigin,
): Promise<RushBundleOutcome> {
  return postRushLike(
    `/api/bundles/${uuid}/analyze-duplicate?origin=${encodeURIComponent(origin)}`,
    "Analysis request failed",
  );
}

/** One durable rush request (`rush_requests` row). */
export interface RushRequestInfo {
  id: number;
  analysis_id: string;
  requested_at: string;
  requested_by: string;
  origin: string;
  mode: "normal" | "analyze-duplicate";
  status: "pending" | "dispatched" | "started" | "done" | "failed" | "skipped" | "cancelled";
  status_reason: string | null;
  attempts: number;
  last_dispatched_at: string | null;
  updated_at: string;
}

/** How often a page re-reads an active rush request (and the row it drives). */
export const RUSH_STATUS_POLL_MS = 15_000;

/** The request is still pending, dispatched or started. */
export function isActiveRushRequest(req: RushRequestInfo | null | undefined): boolean {
  return req?.status === "pending" || req?.status === "dispatched" || req?.status === "started";
}

/** `GET /api/bundles/:uuid/rush-status` — the bundle's latest request. */
export async function fetchRushStatus(uuid: string): Promise<RushRequestInfo | null> {
  const body = await api<{ analysis_id: string; request: RushRequestInfo | null }>(
    `/api/bundles/${uuid}/rush-status`,
  );
  return body.request;
}

// ══════════════════════════════════════════════════════════════════════
// Dismiss / Undismiss API client (migration 026, 2026-05-12)
// ══════════════════════════════════════════════════════════════════════
//
// Wrappers for `POST /api/bundles/:aid/dismiss`,
// `POST /api/bundles/:aid/undismiss`, and
// `GET /api/bundles/awaiting-count`. Closes the operator triage
// loop: gated bundles now have both "escalate" (rushBundle) and
// "reject" (dismissBundle) paths, plus a reversible undismiss
// that returns dismissed bundles to the awaiting-action queue.

/**
 * Operator-supplied dismissal reason. Wire shape matches the
 * Rust `DismissReason` enum's `serde(rename_all = "snake_case")`
 * representation exactly. `"other"` carries an optional free-
 * text note in the `note` field of `DismissBundleRequest`; the
 * other four reasons ignore the note.
 */
export type DismissReason =
  | "spam"
  | "duplicate"
  | "not_actionable"
  | "out_of_scope"
  | "other";

/**
 * Discriminated union returned by [`dismissBundle`]. Mirrors
 * the server's `DismissBundleOutcome` enum exactly. The error
 * variant catches every 4xx/5xx with the server-supplied error
 * code so the toast can render specifically (e.g. "the analysis
 * already started, dismiss is no longer available").
 */
export type DismissBundleOutcome =
  | { kind: "dismissed"; actedAt: string }
  | { kind: "already_dismissed"; actedAt: string }
  | { kind: "already_analyzing" }
  | { kind: "error"; code: string; message: string; status: number };

interface DismissBundleResponseBody {
  analysis_id: string;
  acted_at: string;
  outcome: "dismissed" | "already_dismissed" | "already_analyzing";
}

/**
 * Admin-gated dismiss affordance for gated bundles. Sends the
 * operator's dismissal reason; the server records dismissed_at,
 * dismissed_by (from session.email), and dismissed_reason in
 * one atomic UPDATE.
 */
export async function dismissBundle(
  uuid: string,
  reason: DismissReason,
  note?: string,
): Promise<DismissBundleOutcome> {
  const res = await fetch(`/api/bundles/${uuid}/dismiss`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason, note: note ?? null }),
  });

  if (res.status === 200) {
    const body = (await res.json()) as DismissBundleResponseBody;
    if (body.outcome === "dismissed") {
      return { kind: "dismissed", actedAt: body.acted_at };
    }
    return { kind: "already_dismissed", actedAt: body.acted_at };
  }
  if (res.status === 409) {
    // Could be ALREADY_ANALYZING (race lost to rush) — surface
    // distinctly so the DismissSection can render a specific
    // "the analysis already started" message rather than
    // generic 409.
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // body wasn't JSON; fall through
    }
    const code =
      body && typeof body === "object" && "code" in body
        ? String((body as { code: unknown }).code)
        : null;
    if (code === "ALREADY_ANALYZING") {
      return { kind: "already_analyzing" };
    }
    const message =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `Dismiss rejected with status ${res.status}`;
    return {
      kind: "error",
      code: code ?? "CONFLICT",
      message,
      status: res.status,
    };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // ignore non-JSON error bodies
  }
  const code =
    (body && typeof body === "object" && "code" in body
      ? String((body as { code: unknown }).code)
      : null) ?? "UNKNOWN";
  const message =
    (body && typeof body === "object" && "error" in body
      ? String((body as { error: unknown }).error)
      : null) ?? `Dismiss failed with status ${res.status}`;
  return { kind: "error", code, message, status: res.status };
}

export type UndismissBundleOutcome =
  | { kind: "undismissed"; actedAt: string }
  | { kind: "not_dismissed"; actedAt: string }
  | { kind: "error"; code: string; message: string; status: number };

interface UndismissBundleResponseBody {
  analysis_id: string;
  acted_at: string;
  outcome: "undismissed" | "not_dismissed";
}

/**
 * Clear a prior dismissal, returning the bundle to the awaiting-
 * action queue. Admin-gated. Mirrors `dismissBundle`'s shape; no
 * request body needed since undismiss carries no payload.
 */
export async function undismissBundle(
  uuid: string,
): Promise<UndismissBundleOutcome> {
  const res = await fetch(`/api/bundles/${uuid}/undismiss`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
  });

  if (res.status === 200) {
    const body = (await res.json()) as UndismissBundleResponseBody;
    if (body.outcome === "undismissed") {
      return { kind: "undismissed", actedAt: body.acted_at };
    }
    return { kind: "not_dismissed", actedAt: body.acted_at };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // ignore non-JSON
  }
  const code =
    (body && typeof body === "object" && "code" in body
      ? String((body as { code: unknown }).code)
      : null) ?? "UNKNOWN";
  const message =
    (body && typeof body === "object" && "error" in body
      ? String((body as { error: unknown }).error)
      : null) ?? `Undismiss failed with status ${res.status}`;
  return { kind: "error", code, message, status: res.status };
}

/**
 * Count of bundles currently in the awaiting-operator-action
 * state. Backs the dashboard top-nav `⚠️ <count>` badge. The
 * server caches for 10s via `Cache-Control: private, max-age=10`;
 * the SPA polls every 30s via TanStack Query.
 */
/** Galley #26408: one DM kept as feedback about triage (OPERATOR LIST v1,
 *  #26891). `text` is exactly what Slack sent. */
export interface TriageFeedbackItem {
  channel_id: string;
  ts: string;
  author_id: string;
  author_name: string | null;
  text: string;
  posted_at: string;
  permalink: string | null;
}

export interface TriageFeedbackPage {
  items: TriageFeedbackItem[];
  next_cursor: string | null;
}

/** The Operator page's list, newest first. Operators only: anyone else
 *  gets the server's 403, surfaced as an `ApiError`. */
export function operatorTriageFeedback(): Promise<TriageFeedbackPage> {
  return api<TriageFeedbackPage>("/api/operator/triage-feedback?limit=50");
}

export async function awaitingCount(): Promise<{ count: number }> {
  const res = await fetch(`/api/bundles/awaiting-count`, {
    method: "GET",
    credentials: "same-origin",
  });
  if (!res.ok) {
    throw new Error(`awaiting-count failed with status ${res.status}`);
  }
  const body = (await res.json()) as { count: number };
  return { count: body.count };
}

// ══════════════════════════════════════════════════════════════════════
// M1 observability types (Tier 2/3/4/5) — mirror the Rust wire shapes
// ═══════════════════════════════════════════════════════════════════════════
//
// Every type here has a corresponding server-side serde struct. A
// schema drift between the two produces a TypeScript decode that
// silently returns an untyped `any` at runtime (TS doesn't validate
// — we trust the backend). The Rust test suite pins the wire field
// names on the server side; the SPA renders them by exact key.

/**
 * One row of the Tier 2 `analysis_phases` table. Mirrors
 * `AnalysisPhaseRow` in `src/worker/analysis_phases/types.rs`.
 *
 * `status` is the lowercase string form of the Rust enum
 * (`running` | `completed` | `failed` | `skipped`) — NOT capitalized.
 */
export interface AnalysisPhaseRow {
  analysis_id: string;
  turn_idx: number;
  phase_label: string;
  cluster_id: string | null;
  cluster_title: string | null;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  tool_calls: number;
  api_calls: number;
  status: "running" | "completed" | "failed" | "skipped";
  stop_reason: string;
  error: string | null;
  recorded_at: string;
  /**
   * Only on the row the analysis' last resume continued (Galley #33275): when
   * the worker took it back up after a restart. That row's bar runs from its
   * first start, so its time includes the stop.
   */
  resumed_at?: string;
}

export interface AnalysisPhasesResponse {
  rows: AnalysisPhaseRow[];
}

/**
 * One row of the `GET /api/analysis-phases/medians` response.
 * Mirrors `PhaseMedian` in `src/worker/analysis_phases/store.rs`.
 *
 * Used by `PipelineStrip`'s "Expected remaining" ETA (P2b Item 5,
 * wired 2026-05-01). The backend endpoint has been live since
 * commit `6f86629` (2026-04-30) but no frontend consumer existed
 * until this landed.
 */
export interface PhaseMedian {
  /**
   * Full phase_label string, e.g. `"phase2b-cluster-abc123"` or
   * `"phase1"`. Matches the same column the writer binds on
   * `analysis_phases.record`.
   */
  phase_label: string;
  /**
   * Median `duration_ms` across all `status = 'completed'
   * AND duration_ms > 0` rows with this phase_label in the
   * window. Non-negative; zero is impossible (zero-duration rows
   * are filtered out server-side).
   */
  median_duration_ms: number;
  /**
   * Number of completed rows that contributed to the median.
   * The SPA should suppress the ETA when the sample is too thin
   * (e.g. `< 3`) to be meaningful.
   */
  sample_count: number;
}

/**
 * Wire response for `GET /api/analysis-phases/medians`.
 * Mirrors `PhaseMediansResponse` in `src/server/routes/api/analysis_phases.rs`.
 */
export interface PhaseMediansResponse {
  /**
   * Effective window used to compute the medians (may be clamped
   * server-side from the requested value).
   */
  window_days: number;
  /** One entry per distinct `phase_label` observed. */
  rows: PhaseMedian[];
}

/**
 * One row of the Tier 3 `tool_calls` table. Mirrors `ToolCallRow`.
 *
 * `tool_input` is the PII-redacted JSON object the LLM passed to
 * the tool. `tool_output_full_s3` is `null` when the output fit in
 * the 2 KB inline preview — non-null means the SPA should offer a
 * "Download full output →" button pointing at
 * `/api/bundles/:aid/tool-calls/:idx/full-output` (which 302s to a
 * presigned URL).
 */
export interface ToolCallRow {
  analysis_id: string;
  call_idx: number;
  phase_label: string;
  cluster_id: string | null;
  tool_name: string;
  tool_input: unknown;
  tool_output_preview: string;
  tool_output_full_s3: string | null;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  success: boolean;
  error: string | null;
  recorded_at: string;
}

export interface ToolCallsResponse {
  rows: ToolCallRow[];
  next_since: number;
}

/**
 * Tier 4 streaming tail-poll response. `content` is a UTF-8 string
 * slice of the S3 object starting at byte offset `from` (from the
 * query string), trimmed to the last valid UTF-8 boundary. The SPA
 * advances `from` to `next_from` between polls and appends
 * `content` to the on-screen buffer.
 */
export interface StreamTailResponse {
  content: string;
  next_from: number;
  total_bytes: number;
  has_more: boolean;
}

/**
 * Tier 5 prompt-checkpoint blob (digests only — no raw Block 0).
 * Mirrors `PromptCheckpoint` in `src/anthropic/types.rs`.
 */
export interface PromptCheckpoint {
  analysis_id: string;
  phase_label: string;
  recorded_at: string;
  messages: MessageDigest[];
  system_block_digests: SystemBlockDigest[];
  tools_hash: string;
  cache_breakpoints: CacheBreakpointLocation[];
}

export interface MessageDigest {
  role: string;
  blocks: MessageBlockDigest[];
}

export type MessageBlockDigest =
  | { kind: "text"; sha256: string; char_count: number }
  | { kind: "thinking"; sha256: string; char_count: number }
  | {
      kind: "tool_use";
      tool_name: string;
      tool_use_id: string;
      input_sha256: string;
    }
  | {
      kind: "tool_result";
      tool_use_id: string;
      is_error: boolean;
      content_sha256: string;
    }
  | { kind: "image"; sha256: string; byte_count: number };

export interface SystemBlockDigest {
  name: string;
  sha256: string;
  byte_len: number;
  has_cache_control: boolean;
}

export interface CacheBreakpointLocation {
  kind: string;
  message_idx: number | null;
  block_idx: number | null;
  system_block_idx: number | null;
}

export interface PromptCheckpointResponse {
  checkpoint: PromptCheckpoint;
}

/**
 * Per-phase pair-wise diff output. Mirrors `PromptDiff` in
 * `src/analyze_llm/prompt_checkpoint/types.rs`.
 */
export interface PromptDiff {
  phase_from: string;
  phase_to: string;
  summary: PromptDiffSummary;
  messages: MessageDiff[];
  system_blocks: SystemBlockChange[];
  tools_hash_changed: boolean;
  tools_hash_from: string;
  tools_hash_to: string;
  cache_breakpoints: CacheBreakpointDiff;
}

export interface PromptDiffSummary {
  messages_added: number;
  messages_removed: number;
  messages_modified: number;
  messages_unchanged: number;
  system_blocks_added: number;
  system_blocks_removed: number;
  system_blocks_modified: number;
  tools_hash_changed: boolean;
  cache_breakpoint_changes: number;
}

export type MessageDiff =
  | { kind: "added"; idx: number; message: MessageDigest }
  | { kind: "removed"; idx: number; message: MessageDigest }
  | {
      kind: "modified";
      idx: number;
      from: MessageDigest;
      to: MessageDigest;
      blocks: BlockDiff[];
    }
  | { kind: "unchanged"; idx: number; message: MessageDigest };

export type BlockDiff =
  | { kind: "added"; idx: number; block: MessageBlockDigest }
  | { kind: "removed"; idx: number; block: MessageBlockDigest }
  | {
      kind: "modified";
      idx: number;
      from: MessageBlockDigest;
      to: MessageBlockDigest;
    }
  | { kind: "unchanged"; idx: number; block: MessageBlockDigest };

export type SystemBlockChange =
  | { kind: "added"; name: string; to: SystemBlockDigest }
  | { kind: "removed"; name: string; from: SystemBlockDigest }
  | {
      kind: "modified";
      name: string;
      from: SystemBlockDigest;
      to: SystemBlockDigest;
    }
  | { kind: "unchanged"; name: string; digest: SystemBlockDigest };

export interface CacheBreakpointDiff {
  added: CacheBreakpointLocation[];
  removed: CacheBreakpointLocation[];
  unchanged: CacheBreakpointLocation[];
}

export interface PromptDiffResponse {
  from_phase: string;
  to_phase: string;
  diff: PromptDiff;
}

/**
 * Admin-only raw Block 0 text. Returned by
 * `GET /api/bundles/:aid/prompts/system-block/:sha` — non-admin
 * sessions get 403 ADMIN_REQUIRED before the reader is ever
 * touched.
 */
export interface SystemBlockRawResponse {
  sha256: string;
  raw_text: string;
  byte_len: number;
}

/**
 * One row of the admin-gated LLM calls drill-down. Mirrors
 * `LlmCallRow` in `src/worker/token_usage_store.rs`.
 */
export interface LlmCallRow {
  recorded_at: string;
  analysis_id: string;
  operation: string;
  model: string;
  backend: string;
  duration_ms: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  tool_calls: number;
  stop_reason: string;
  success: boolean;
  error: string | null;
  environment: string;
  /**
   * Galley #33275: on the call an analysis' latest resume after a restart continued, when
   * the worker took it back up (the lease's claim). Absent on every other row.
   */
  resumed_at?: string;
}

export interface LlmCallsResponse {
  rows: LlmCallRow[];
  window_start: string;
  window_end: string;
  row_count: number;
}

/**
 * Snapshot of the four process-wide observability failure counters.
 * Admin-gated — powers the dashboard banner that surfaces retry-
 * exhausted writes (the early-warning signal from
 * CONTRIBUTING.md §"No silent failures, ever").
 */
export interface ObservabilityCountersSnapshot {
  analysis_phase_retry_exhausted: number;
  tool_call_retry_exhausted: number;
  streaming_flush_retry_exhausted: number;
  checkpoint_retry_exhausted: number;
  /**
   * Phase 1 Part B — Priority 4 (2026-05-06): lifetime count of
   * `canonical_retry_state` writer failures (the best-effort
   * fail-open-but-loud writers — `clear_canonical_retry_state` +
   * `record_canonical_retry_failure`). A sustained non-zero rate
   * indicates the retry-state table is accumulating drift vs. the
   * bundle_lifecycle row's `last_error` / supersede pointers —
   * operator signal to investigate Postgres health.
   *
   * Optional (`?: number`) for backward compat — older backends
   * that pre-date Priority 4 don't emit the field, and the
   * `useObservabilityCounters` hook treats missing / `undefined`
   * as `0` so the banner renders correctly either way.
   */
  canonical_retry_write_errors?: number;
  /** Page-view record failures (read-layer analytics writes). All
   *  the optional fields below share the back-compat contract
   *  above: absent on older backends, coerced to 0 in the banner. */
  page_view_record_errors?: number;
  /** user_seen_bundles write failures (read-state tracking). */
  user_seen_write_errors?: number;
  /** LLM-gate saturation events — a permit wait is a defect signal
   *  at current pool sizing, not load. */
  llm_gate_saturated?: number;
  /** TRIAG-337 — vector-dedup transport failures (embed call or
   *  OpenSearch k-NN errored). Non-zero = the vector layer is being
   *  bypassed and duplicates run full analysis until it heals. */
  vector_transport_errors?: number;
  // TRIAG-352 — context-pressure telemetry. `llm_context_overflow` is a
  // degradation (counts toward the server's `total`); the other three are
  // pressure/rescue metrics that are wire-visible only.
  llm_context_critical?: number;
  llm_context_overflow?: number;
  llm_refusal?: number;
  llm_refusal_fallback_served?: number;
  /** TRIAG-331 — story-vector embed/store failures. Non-zero =
   *  fresh analyses are missing from the UserStories page until
   *  `triage backfill-story-vectors` heals them. */
  story_vector_failures?: number;
  /** TRIAG-342 — shake-cluster hook failures (gist embed → vector
   *  upsert → cluster assignment). Counted in the server's `total()`
   *  since it shipped, but it was missing from this type AND from the
   *  banner rows, so a failure would have lit the banner with a
   *  non-zero total and every row reading 0 — the drift the TRIAG-331
   *  note in AdminLlm.tsx warns about. Added 2026-07-29. Heals via
   *  `triage backfill-shake-clusters --days 2`. */
  shake_vector_failures?: number;
  /** TRIAG-429 — Hardware page capture failures (the intake write of a
   *  report's phone-data summary failed; the report itself continues).
   *  Non-zero = reports are missing from the Hardware page until
   *  `triage backfill-hardware` heals the gap. */
  hardware_capture_failures?: number;
  /** 2026-09-24 — backend dossier failures (unavailable for any reason
   *  except "all slots busy", a crashed/timed-out dossier task, or a failed
   *  S3 save). The analysis continues without a dossier. */
  backend_dossier_failures?: number;
}

export interface ObservabilityCountersResponse {
  counters: ObservabilityCountersSnapshot;
  any_degraded: boolean;
  total: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// M1 observability queries
// ═══════════════════════════════════════════════════════════════════════════

export const observabilityQueries = {
  analysisPhases: (analysisId: string) =>
    queryOptions({
      queryKey: ["bundles", analysisId, "analysis-phases"] as const,
      queryFn: () =>
        api<AnalysisPhasesResponse>(
          `/api/bundles/${encodeURIComponent(analysisId)}/analysis-phases`,
        ),
      staleTime: 10_000,
    }),

  /**
   * Per-phase median duration over the last N days. Backend
   * endpoint has been live since commit `6f86629` (2026-04-30)
   * but was unconsumed until the `PipelineStrip` ETA wire-up
   * landed 2026-05-01 (P2b Item 5 / NEXT_STEPS P0-5).
   *
   * 30-day default matches the backend's `DEFAULT_WINDOW_DAYS`.
   * Cached for 5 minutes — the medians drift slowly enough that
   * refetching per bundle-page render is wasteful; 5 min also
   * keeps the endpoint load trivial even if dozens of bundle
   * pages are open simultaneously.
   */
  phaseMedians: (windowDays = 30) =>
    queryOptions({
      queryKey: ["analysis-phases", "medians", windowDays] as const,
      queryFn: () =>
        api<PhaseMediansResponse>(
          `/api/analysis-phases/medians?window_days=${windowDays}`,
        ),
      staleTime: 5 * 60_000,
    }),

  toolCalls: (analysisId: string, since: number, limit = 200) =>
    queryOptions({
      queryKey: ["bundles", analysisId, "tool-calls", since, limit] as const,
      queryFn: () => {
        const params = new URLSearchParams({
          since: String(since),
          limit: String(limit),
        });
        return api<ToolCallsResponse>(
          `/api/bundles/${encodeURIComponent(analysisId)}/tool-calls?${params}`,
        );
      },
      // Tail-poll: refresh every 5 s via `refetchInterval` on the
      // consuming hook. `staleTime: 0` here so each poll actually
      // re-fetches.
      staleTime: 0,
    }),

  streamingVerbose: (analysisId: string, from: number) =>
    queryOptions({
      queryKey: ["bundles", analysisId, "streaming", "verbose", from] as const,
      queryFn: () =>
        api<StreamTailResponse>(
          `/api/bundles/${encodeURIComponent(analysisId)}/streaming/verbose?from=${from}`,
        ),
      staleTime: 0,
      retry: false,
    }),

  streamingConversation: (analysisId: string, from: number) =>
    queryOptions({
      queryKey: [
        "bundles",
        analysisId,
        "streaming",
        "conversation",
        from,
      ] as const,
      queryFn: () =>
        api<StreamTailResponse>(
          `/api/bundles/${encodeURIComponent(analysisId)}/streaming/conversation?from=${from}`,
        ),
      staleTime: 0,
      retry: false,
    }),

  promptCheckpoint: (analysisId: string, phase: string) =>
    queryOptions({
      queryKey: ["bundles", analysisId, "prompts", phase] as const,
      queryFn: () =>
        api<PromptCheckpointResponse>(
          `/api/bundles/${encodeURIComponent(analysisId)}/prompts/${encodeURIComponent(phase)}`,
        ),
      staleTime: 60_000,
    }),

  promptDiff: (analysisId: string, phase: string, prevPhase: string) =>
    queryOptions({
      queryKey: [
        "bundles",
        analysisId,
        "prompts",
        phase,
        "diff",
        prevPhase,
      ] as const,
      queryFn: () =>
        api<PromptDiffResponse>(
          `/api/bundles/${encodeURIComponent(analysisId)}/prompts/${encodeURIComponent(phase)}/diff/${encodeURIComponent(prevPhase)}`,
        ),
      staleTime: 60_000,
    }),

  systemBlockRaw: (analysisId: string, sha: string) =>
    queryOptions({
      queryKey: [
        "bundles",
        analysisId,
        "prompts",
        "system-block",
        sha,
      ] as const,
      queryFn: () =>
        api<SystemBlockRawResponse>(
          `/api/bundles/${encodeURIComponent(analysisId)}/prompts/system-block/${encodeURIComponent(sha)}`,
        ),
      staleTime: 5 * 60_000,
      retry: false,
    }),

  llmCalls: (params: {
    since?: string;
    until?: string;
    limit?: number;
    op?: string;
    analysisId?: string;
  }) =>
    queryOptions({
      queryKey: ["admin", "llm", "calls", params] as const,
      queryFn: () => {
        const qs = new URLSearchParams();
        if (params.since) qs.set("since", params.since);
        if (params.until) qs.set("until", params.until);
        if (params.limit !== undefined) qs.set("limit", String(params.limit));
        if (params.op) qs.set("op", params.op);
        if (params.analysisId) qs.set("analysis_id", params.analysisId);
        const q = qs.toString();
        return api<LlmCallsResponse>(
          q ? `/api/admin/llm/calls?${q}` : "/api/admin/llm/calls",
        );
      },
      staleTime: 15_000,
    }),

  observabilityCounters: () =>
    queryOptions({
      queryKey: ["admin", "llm", "observability-counters"] as const,
      queryFn: () =>
        api<ObservabilityCountersResponse>(
          "/api/admin/llm/observability-counters",
        ),
      staleTime: 10_000,
    }),
};

/**
 * Build the presigned-redirect URL for a tool's full output. Not a
 * query — this endpoint returns a 302 to a presigned S3 URL. The
 * SPA uses this as an `<a href>` so the browser follows the
 * redirect transparently.
 */
export function toolCallFullOutputUrl(
  analysisId: string,
  callIdx: number,
): string {
  return `/api/bundles/${encodeURIComponent(analysisId)}/tool-calls/${callIdx}/full-output`;
}

// ─── 2026-04-29 /admin/usersnap — wire types ────────────────────────────────
//
// Mirrors `src/server/routes/api/admin_usersnap.rs` byte-for-byte.
// Any rename on one side must land in lockstep on the other —
// the Rust-side serde tests (`health_response_serializes_with_expected_field_names`)
// pin the wire shape so a drift fails the Rust test suite before
// it reaches the SPA at runtime.

/**
 * Which Usersnap env-backed credentials the worker has at boot.
 *
 * Mirrors `UsersnapCredentialsStatus` in the Rust handler. The SPA
 * renders a three-state chip:
 *   - `all_present=true`  → ingest can authenticate ✓
 *   - `all_present=false` + one-or-more false → partial (misconfig) ⚠
 *   - every field false     → absent (no env vars at boot) —
 *                              ingest task won't even spawn
 */
export interface UsersnapCredentialsStatus {
  has_intake_bucket: boolean;
  has_jwt_id: boolean;
  has_jwt_secret: boolean;
  all_present: boolean;
}

/**
 * Per-window web-bundle counts from `bundle_lifecycle`.
 *
 * Mirrors `WebBundleStats`. All integer fields; no nulls. A fresh
 * deploy with zero web bundles serializes every field as `0`.
 */
export interface WebBundleStats {
  total: number;
  completed: number;
  failed: number;
  in_last_24h: number;
  in_last_7d: number;
  in_last_30d: number;
}

/**
 * Response shape for `GET /api/admin/usersnap/health`.
 *
 * Mirrors `UsersnapHealthResponse` in the Rust handler. Every
 * field the `/admin/usersnap` page needs is here — the page
 * renders in one fetch, no per-card follow-up round-trips.
 */
export interface UsersnapHealthResponse {
  /** Kill switch from `worker_config.data.usersnap_intake_enabled`. */
  ingest_enabled: boolean;

  /**
   * Operator-configured Usersnap project UUIDs. Empty when
   * nothing has been added via `/triage add-usersnap-project`
   * or the admin UI.
   */
  configured_project_ids: string[];

  /**
   * Effective poll interval (persisted value clamped to range,
   * or the module default when persisted is NULL/0).
   */
  effective_poll_interval_seconds: number;

  /**
   * Effective max-per-tick (persisted value, or default when NULL).
   * `0` is legal (operator-configured no-op).
   */
  effective_max_per_tick: number;

  /** Raw persisted override — `null` when unset. */
  persisted_poll_interval_seconds: number | null;
  persisted_max_per_tick: number | null;

  /** Credentials presence, snapshot at worker boot. */
  credentials: UsersnapCredentialsStatus;

  /** S3 intake bucket name — `null` when env var is unset. */
  intake_bucket: string | null;

  /** Aggregate bundle counts driving the KPI strip. */
  bundle_stats: WebBundleStats;

  /**
   * ISO-8601 timestamp of the most-recently-arrived web bundle,
   * or `null` when no web bundle has ever arrived.
   */
  last_web_bundle_at: string | null;

  /**
   * `analysis_id` of the most-recent web bundle — for the
   * drill-through link to `/bundles/:uuid`. Paired with
   * `last_web_bundle_at` (both are `null` when no web bundle
   * exists; both non-null otherwise).
   */
  last_web_bundle_analysis_id: string | null;

  /**
   * Server-side wall-clock at response assembly. Clients render
   * relative times ("5 min ago") against this, NOT
   * `Date.now()` on the browser, so clock-skew between operator
   * laptop and server doesn't bleed into rendered timings.
   */
  generated_at: string;
}

// ─── #user-feedback synthesis (TRIAG-281, Slice 3) ─────────────────────

/** One deduplicated feedback issue cluster (mirrors `FeedbackClusterRow`). */
export interface FeedbackClusterRow {
  id: number;
  /** LLM label; null until the namer runs — fall back to exemplar_title. */
  label: string | null;
  summary: string | null;
  exemplar_title: string;
  count_window: number;
  count_30d: number;
  count_7d: number;
  count_total: number;
  reporters: number;
  latest_at: string | null;
  kind_bug: number;
  kind_feature: number;
  kind_question: number;
  kind_ops: number;
  kind_relay: number;
  /** TRIAG-283: trailing-14d members — trend numerator (14d/30d). */
  count_14d: number;
  /** TRIAG-283: severity mix for the digest glyph. */
  sev_blocker: number;
  sev_major: number;
  checked_count: number;
  /** TRIAG-283: harvested Jira tickets with cached live status. */
  jira_links: JiraChipView[];
}

/** One Jira ticket chip on a cluster row (TRIAG-283). */
export interface JiraChipView {
  jira_key: string;
  /** Raw Jira status name ("Merged", "Ready For Test", …). */
  status: string | null;
  /** Coarse bucket for colors: new | indeterminate | done. */
  status_category: string | null;
  /** Provenance: jira_bot_announce | thread_mention. */
  source: string;
  ticket_created_at: string | null;
}

/** Summary-strip stats (TRIAG-283). */
export interface FeedbackSummaryStrip {
  new_24h_clusters: number;
  rising_clusters: number;
  linked_tickets: number;
  linked_done: number;
  untracked_clusters: number;
}

export interface FeedbackClustersResponse {
  clusters: FeedbackClusterRow[];
  total_instances_window: number;
  window: string;
  sort: string;
  strip: FeedbackSummaryStrip;
  generated_at: string;
}

/** One member message inside a feedback cluster. */
export interface FeedbackMemberRow {
  ts: string;
  posted_at: string;
  author_id: string;
  author_name: string;
  kind: string;
  issue_title: string;
  platform: string;
  severity_hint: string;
  text_preview: string;
  reply_count: number;
  reactions: string[];
  permalink: string;
  /** Galley #18479: where the member came from. `"chat_dm"` is a DM to the
   * triage chat, whose Slack link opens only for the two people in it, so
   * the page shows a "via chat" chip instead. Absent from servers before
   * the field, which means "channel". */
  source?: string;
}

export interface FeedbackMembersResponse {
  cluster_id: number;
  members: FeedbackMemberRow[];
  generated_at: string;
}

export interface FeedbackClustersParams {
  window?: string;
  sort?: string;
  kind?: string;
  platform?: string;
  area?: string;
}

/** GET /api/feedback/clusters — deduplicated issue clusters. */
export async function fetchFeedbackClusters(
  params: FeedbackClustersParams,
): Promise<FeedbackClustersResponse> {
  const qs = new URLSearchParams();
  if (params.window) qs.set("window", params.window);
  if (params.sort) qs.set("sort", params.sort);
  if (params.kind) qs.set("kind", params.kind);
  if (params.platform) qs.set("platform", params.platform);
  if (params.area) qs.set("area", params.area);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api<FeedbackClustersResponse>(`/api/feedback/clusters${suffix}`);
}

/** GET /api/feedback/clusters/{id}/members — drill-down. */
export async function fetchFeedbackClusterMembers(
  clusterId: number,
): Promise<FeedbackMembersResponse> {
  return api<FeedbackMembersResponse>(
    `/api/feedback/clusters/${clusterId}/members`,
  );
}

/** One row on the coverage tab (TRIAG-283). */
export interface FeedbackCoverageRow {
  cluster_id: number;
  cluster_label: string;
  count_90d: number;
  jira_key: string | null;
  jira_summary: string | null;
  status: string | null;
  status_category: string | null;
  source: string | null;
  ticket_created_at: string | null;
  sync_error: string | null;
}

export interface FeedbackCoverageResponse {
  rows: FeedbackCoverageRow[];
  tickets_total: number;
  tickets_done: number;
  untracked_clusters: number;
  /** Unlinked singleton clusters hidden from the table (footer note). */
  hidden_unlinked: number;
  generated_at: string;
}

/** GET /api/feedback/coverage — the Slack-report → Jira-ticket map. */
export async function fetchFeedbackCoverage(): Promise<FeedbackCoverageResponse> {
  return api<FeedbackCoverageResponse>("/api/feedback/coverage");
}

// ─── Remediations (Feature 05 slice 9) ──────────────────────────────────────
//
// Wire shapes mirror `src/server/routes/api/remediations.rs` +
// `src/worker/remediation/lifecycle.rs` one-for-one. Reads are
// session-gated (non-admins get read-only visibility of everything);
// every mutation is admin-gated server-side via `require_admin` — the
// SPA's `useIsAdmin` gating is presentation only.

/** The nine remediation states (migration 061 CHECK ladder, doc §4). */
export type RemediationState =
  | "open"
  | "analyzing"
  | "analysis_failed"
  | "analyzed"
  | "fix_drafting"
  | "fix_ready"
  | "fix_pushed"
  | "abandoned"
  | "closed";

/** Every state, in lifecycle order — drives the list page's filter chips. */
export const REMEDIATION_STATES: readonly RemediationState[] = [
  "open",
  "analyzing",
  "analysis_failed",
  "analyzed",
  "fix_drafting",
  "fix_ready",
  "fix_pushed",
  "abandoned",
  "closed",
] as const;

/** One `remediations` row. Mirrors the Rust `RemediationRow`. */
export interface RemediationRow {
  id: string;
  title: string;
  state: RemediationState;
  created_by: string;
  created_at: string;
  updated_at: string;
  technical_solution: string | null;
  plain_summary: string | null;
  analysis_version: number;
  jira_key: string | null;
  fix_branch: string | null;
  fix_base_sha: string | null;
  fix_pushed_at: string | null;
  follow_up_of: string | null;
  expires_at: string | null;
}

/**
 * Typed evidence reference — the per-kind ref shapes of the Rust
 * `AttachmentRef` enum (serde `tag = "kind"`, flattened onto the
 * attachment row).
 */
export type RemediationAttachmentRef =
  | { kind: "bundle"; analysis_id: string }
  | { kind: "analysis"; analysis_id: string }
  | { kind: "trend_family"; hash_sha256: string; dedup_scope_key: string }
  | { kind: "story_theme"; theme_id: string }
  | { kind: "slack_feedback"; cluster_id: number };

/** One `remediation_attachments` row (ref fields flattened). */
export type RemediationAttachmentRow = RemediationAttachmentRef & {
  id: number;
  remediation_id: string;
  attached_by: string;
  attached_at: string;
};

/** One append-only `remediation_events` audit row. */
export interface RemediationEventRow {
  id: number;
  remediation_id: string;
  event: string;
  from_state: string | null;
  to_state: string | null;
  curia_session_id: string | null;
  phase: string | null;
  model: string | null;
  turns: number | null;
  fallback_turns: number | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  git_op: string | null;
  git_ref: string | null;
  actor: string;
  detail: Record<string, unknown> | null;
  created_at: string;
}

/** One append-only `remediation_fixes` attempt row. */
export interface RemediationFixRow {
  id: number;
  remediation_id: string;
  analysis_version: number;
  attempt_no: number;
  unified_diff: string | null;
  explanation: string | null;
  base_sha: string;
  /** `clear_fix` | `declined` | null (failed attempt). */
  verdict: string | null;
  verdict_reason: string | null;
  validation_record: Record<string, unknown> | null;
  /** `pending` | `approved` | `rejected`. */
  review_state: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

/** One `remediation_jira_links` row (033 soft-unlink pattern). */
export interface RemediationJiraLinkRow {
  id: number;
  remediation_id: string;
  jira_key: string;
  /** 'found' | 'created' | 'manual'. */
  origin: string;
  linked_by: string;
  linked_at: string;
  deleted_at: string | null;
  deleted_by_email: string | null;
}

/** `GET /api/remediations` response. */
export interface RemediationsListResponse {
  remediations: RemediationRow[];
}

/** `GET /api/remediations/{id}` response. */
export interface RemediationDetailResponse {
  remediation: RemediationRow;
  attachments: RemediationAttachmentRow[];
  events: RemediationEventRow[];
  fixes: RemediationFixRow[];
  /** Full link history INCLUDING soft-unlinked rows (audit view). */
  jira_links: RemediationJiraLinkRow[];
}

/**
 * One structured progress event relayed from the Curia report
 * endpoint (doc §6.6). Loose shape — the panel renders whatever
 * fields are present and falls back to `message`.
 */
export interface RemediationProgressEvent {
  ts?: string;
  step?: string;
  tool?: string;
  message?: string;
  [k: string]: unknown;
}

/**
 * `GET /api/remediations/{id}/progress` response — the thin triage
 * proxy over Curia's report endpoint for the ACTIVE session (doc
 * §6.6). `state` mirrors Curia's REAL session status strings; there
 * is NO `awaiting_confirm` status — confirms surface as EVENTS while
 * the status stays `running`, so `pending_confirm` (intent verbatim +
 * the `request_id` the confirm answer must echo, doc §12.3) can be
 * non-null while `state` is `"running"`.
 */
export interface RemediationProgressResponse {
  session_id: string | null;
  phase: string | null;
  state:
    | "running"
    | "completed"
    | "cancelled"
    | "stopped_early"
    | "failed"
    | null;
  events: RemediationProgressEvent[];
  pending_confirm: { intent: string; request_id: string | null } | null;
}

/** One find-bug candidate (deterministic JQL + ranking session). */
export interface RemediationFindBugCandidate {
  key: string;
  summary: string | null;
  status: string | null;
  rank: number | null;
  rationale: string | null;
}

/** `POST /api/remediations/{id}/jira/find-bug` response. */
export interface RemediationFindBugResponse {
  jql: string;
  candidates: RemediationFindBugCandidate[];
}

/** `POST /api/remediations/{id}/jira/create` response. */
export interface RemediationCreateBugResponse {
  link: RemediationJiraLinkRow;
  jira_key: string;
}

/** `POST /api/remediations/{id}/jira/comment` response. */
export interface RemediationJiraCommentResponse {
  jira_key: string;
  comment_id: string;
  comment_url: string;
  posted: boolean;
}

/** `POST /api/remediations/{id}/push` response. */
export interface RemediationPushResponse {
  remediation: RemediationRow;
  fix_branch: string;
  fix_base_sha: string;
}

/**
 * TanStack Query factories for the remediation surface. Separate
 * export (like `observabilityQueries`) so the remediation pages can
 * import one focused namespace.
 */
export const remediationQueries = {
  /**
   * Newest-first list, optional `?state=` filter. 15s staleTime —
   * remediations move on operator actions + job transitions, so a
   * lightly-fresh list is enough; the detail page owns live polling.
   */
  list: (params: { state?: RemediationState | "" } = {}) =>
    // retry: 1 (not the TanStack default 3): a failing API otherwise
    // hides the honest error card behind ~15s of "Loading…" skeleton
    // (slice-9b walkthrough finding #1).
    queryOptions({
      queryKey: ["remediations", params.state ?? ""] as const,
      queryFn: () => {
        const qs = params.state ? `?state=${params.state}` : "";
        return api<RemediationsListResponse>(`/api/remediations${qs}`);
      },
      staleTime: 15_000,
      retry: 1,
    }),

  /** Full detail: row + attachments + events + fixes + jira links. */
  detail: (id: string) =>
    queryOptions({
      queryKey: ["remediation", id] as const,
      queryFn: () => api<RemediationDetailResponse>(`/api/remediations/${id}`),
      staleTime: 5_000,
      // retry: 1 for the same reason as `list` — honest errors fast.
      retry: 1,
    }),

  /**
   * Live Curia progress for the active session (doc §6.6). The
   * consuming panel supplies `refetchInterval` while a session runs
   * (`analyzing` / `fix_drafting`) and disables the query otherwise —
   * `staleTime: 0` so every poll actually re-fetches.
   */
  progress: (id: string) =>
    queryOptions({
      queryKey: ["remediation", id, "progress"] as const,
      queryFn: () =>
        api<RemediationProgressResponse>(`/api/remediations/${id}/progress`),
      staleTime: 0,
      retry: false,
    }),
};

/** `POST /api/remediations` — create (admin-only server-side). */
export async function createRemediation(body: {
  title: string;
  follow_up_of?: string;
  jira_key?: string;
}): Promise<RemediationRow> {
  return api<RemediationRow>("/api/remediations", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** `POST /api/remediations/{id}/attachments` — attach evidence (open-only). */
export async function attachRemediationEvidence(
  id: string,
  ref: RemediationAttachmentRef,
): Promise<RemediationAttachmentRow> {
  return api<RemediationAttachmentRow>(`/api/remediations/${id}/attachments`, {
    method: "POST",
    body: JSON.stringify(ref),
  });
}

/** `DELETE /api/remediations/{id}/attachments/{attachmentId}`. */
export async function detachRemediationEvidence(
  id: string,
  attachmentId: number,
): Promise<RemediationAttachmentRow> {
  return api<RemediationAttachmentRow>(
    `/api/remediations/${id}/attachments/${attachmentId}`,
    { method: "DELETE" },
  );
}

/** The transition-shaped POST actions that return the updated row. */
export type RemediationTransitionAction =
  | "start-analysis"
  | "start-fix"
  | "abandon"
  | "close"
  | "reopen";

/** `POST /api/remediations/{id}/{action}` — typed transitions. */
export async function transitionRemediation(
  id: string,
  action: RemediationTransitionAction,
): Promise<RemediationRow> {
  return api<RemediationRow>(`/api/remediations/${id}/${action}`, {
    method: "POST",
  });
}

/** `POST /api/remediations/{id}/follow-up` — post-push continuation. */
export async function createRemediationFollowUp(
  id: string,
  body: { title?: string; carry_jira_key?: boolean } = {},
): Promise<RemediationRow> {
  return api<RemediationRow>(`/api/remediations/${id}/follow-up`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** `POST /api/remediations/{id}/jira/find-bug` — phase-2 find-bug. */
export async function findRemediationBug(
  id: string,
): Promise<RemediationFindBugResponse> {
  return api<RemediationFindBugResponse>(
    `/api/remediations/${id}/jira/find-bug`,
    { method: "POST" },
  );
}

/** `POST /api/remediations/{id}/jira/link` — link THE bug. */
export async function linkRemediationBug(
  id: string,
  body: { jira_key: string; origin?: "found" | "manual" },
): Promise<RemediationJiraLinkRow> {
  return api<RemediationJiraLinkRow>(`/api/remediations/${id}/jira/link`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** `POST /api/remediations/{id}/jira/unlink` — soft-unlink (idempotent). */
export async function unlinkRemediationBug(
  id: string,
): Promise<RemediationJiraLinkRow> {
  return api<RemediationJiraLinkRow>(`/api/remediations/${id}/jira/unlink`, {
    method: "POST",
  });
}

/** `POST /api/remediations/{id}/jira/create` — file + link a new ticket. */
export async function createRemediationBug(
  id: string,
  body: { summary: string; description?: string },
): Promise<RemediationCreateBugResponse> {
  return api<RemediationCreateBugResponse>(
    `/api/remediations/${id}/jira/create`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** `POST /api/remediations/{id}/jira/comment` — consolidated comment. */
export async function commentRemediationBug(
  id: string,
): Promise<RemediationJiraCommentResponse> {
  return api<RemediationJiraCommentResponse>(
    `/api/remediations/${id}/jira/comment`,
    { method: "POST" },
  );
}

/** `POST /api/remediations/{id}/fixes/{fixId}/review` — write-once review. */
export async function reviewRemediationFix(
  id: string,
  fixId: number,
  decision: "approved" | "rejected",
): Promise<RemediationFixRow> {
  return api<RemediationFixRow>(
    `/api/remediations/${id}/fixes/${fixId}/review`,
    { method: "POST", body: JSON.stringify({ decision }) },
  );
}

/** `POST /api/remediations/{id}/push` — the admin-only, NO-LLM push. */
export async function pushRemediationFix(
  id: string,
): Promise<RemediationPushResponse> {
  return api<RemediationPushResponse>(`/api/remediations/${id}/push`, {
    method: "POST",
  });
}

/**
 * `POST /api/remediations/{id}/confirm` — forward the operator's
 * answer to a session's pending-confirm request (doc §12.3, relaying
 * Curia's confirm endpoint). `request_id` names the confirm event
 * being answered (from `pending_confirm.request_id`); the real Curia
 * body has no note field. Admin-only server-side.
 */
export async function confirmRemediationSession(
  id: string,
  body: { request_id: string; approve: boolean },
): Promise<{ applied: boolean }> {
  return api<{ applied: boolean }>(`/api/remediations/${id}/confirm`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Mirror of `FeedbackHealthResponse` (admin observability). */
export interface FeedbackHealth {
  ingest_enabled: boolean;
  channel_id: string | null;
  effective_poll_interval_seconds: number;
  effective_max_per_tick: number;
  persisted_poll_interval_seconds: number | null;
  persisted_max_per_tick: number | null;
  poll_state: {
    watermark_ts: string | null;
    backfill_done: boolean;
    backfill_cursor_ts: string | null;
    last_tick_at: string | null;
    last_deep_rescan_at: string | null;
  } | null;
  message_stats: {
    total: number;
    top_level_instances: number;
    replies: number;
    instances_30d: number;
    instances_90d: number;
    flagged_missing: number;
    unenriched_eligible: number;
    enrich_failed: number;
    last_message_at: string | null;
  };
  generated_at: string;
}

/** GET /api/admin/feedback/health — admin-gated ingest health. */
export async function fetchFeedbackHealth(): Promise<FeedbackHealth> {
  return api<FeedbackHealth>("/api/admin/feedback/health");
}

// ─── Trend alerts (TRIAG-343) ────────────────────────────────────────────

/** One trend-alert list row — the immutable as-fired capture. */
export interface TrendAlertRow {
  id: number;
  group_key: string;
  lane: "storm" | "newborn" | "slowburn" | "human" | string;
  kind: "post" | "thread" | "digest_item" | string;
  fired_at: string;
  window_count: number;
  window_people: number;
  baseline_rate: number;
  severity: number;
  dry_run: boolean;
  /** TRUE = a Slack message actually went out. FALSE + !dry_run =
   *  delivery failed / channel unset (scenario-1 badge state). */
  delivered: boolean;
  one_line: string | null;
  feature_area: string | null;
}

/** List envelope: rows + live system flags for the empty state. */
export interface TrendAlertList {
  alerts: TrendAlertRow[];
  next_before_id: number | null;
  trend_alerts_enabled: boolean;
  trend_dry_run: boolean;
}

/** One hourly bucket of the detail graph (people = the honest series). */
export interface TrendAlertHourBucket {
  hour: string;
  reports: number;
  people: number;
}

export interface TrendAlertDetail extends TrendAlertRow {
  /** Tri-zone stamp (PST/PDT · EST/EDT · UTC) — server-computed from
   *  the same chrono-tz source as the Slack messages. */
  fired_at_stamp: string;
  slack_channel: string | null;
  /** The composed message exactly as posted / would have posted. */
  message: string;
  app_version: string | null;
  series: TrendAlertHourBucket[];
}

export interface TrendAlertListParams {
  before_id?: number;
  lane?: string;
  kind?: string;
  dry_run?: boolean;
  group_key?: string;
  /** Page size; the server clamps to [1, 200] (default 50). */
  limit?: number;
}

/** One family-episode (the grouped /alerts default view): every alert
 *  event of one family within the suppression layer's episode window,
 *  collapsed to a single row. */
export interface TrendAlertEpisode {
  group_key: string;
  lanes: string[];
  first_fired_at: string;
  last_fired_at: string;
  latest_id: number;
  peak_people: number;
  peak_count: number;
  events: number;
  posts: number;
  threads: number;
  digest_items: number;
  delivered_events: number;
  dry_run_events: number;
  one_line: string | null;
  feature_area: string | null;
}

export interface TrendAlertEpisodeList {
  episodes: TrendAlertEpisode[];
  window_days: number;
  trend_alerts_enabled: boolean;
  trend_dry_run: boolean;
}

export async function fetchTrendAlertEpisodes(params: {
  days?: number;
  dry_run?: boolean;
}): Promise<TrendAlertEpisodeList> {
  const q = new URLSearchParams();
  if (params.days !== undefined) q.set("days", String(params.days));
  if (params.dry_run !== undefined) q.set("dry_run", String(params.dry_run));
  const qs = q.toString();
  return api<TrendAlertEpisodeList>(
    `/api/trend-alerts/grouped${qs ? `?${qs}` : ""}`,
  );
}

export async function fetchTrendAlerts(
  params: TrendAlertListParams,
): Promise<TrendAlertList> {
  const q = new URLSearchParams();
  if (params.before_id !== undefined) q.set("before_id", String(params.before_id));
  if (params.lane) q.set("lane", params.lane);
  if (params.kind) q.set("kind", params.kind);
  if (params.dry_run !== undefined) q.set("dry_run", String(params.dry_run));
  if (params.group_key) q.set("group_key", params.group_key);
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  const qs = q.toString();
  return api<TrendAlertList>(`/api/trend-alerts${qs ? `?${qs}` : ""}`);
}

export async function fetchTrendAlert(id: number): Promise<TrendAlertDetail> {
  return api<TrendAlertDetail>(`/api/trend-alerts/${id}`);
}
